/**
 * Every English string the application shows, in one file.
 *
 * Spanish is second and comes as `es.ts` beside this one, so a translator gets
 * one file and never reads TSX. Nothing user-visible is written inline in a
 * component — a string that lives in a component is a string that ships
 * untranslated, and the site that needs Spanish is the site where a foreman is
 * standing in a mechanical room.
 *
 * Two kinds of text are deliberately absent. Record content people type
 * (`Damper linkage clash at L3`) is never keyed, and neither are identifiers
 * (`DEF-00012`, `AHU-3`). Server-generated text arrives as a message key with
 * arguments and is resolved from the API's own catalogue, not from here.
 */
export const en = {
  'app.name': 'SiteFocus: CM',
  'app.skip_to_content': 'Skip to main content',
  'app.loading': 'Loading…',
  'app.retry': 'Try again',
  'app.cancel': 'Cancel',
  'app.close': 'Close',
  'app.save': 'Save',
  'app.none': 'None',
  // "Any" on a filter and "None" on an optional field are different
  // words: one widens the list, the other empties a value.
  'app.any': 'Any',
  'app.choose': 'Choose…',
  'app.optional': 'optional',
  'app.search': 'Search',
  'app.filters': 'Filters',
  'app.clear_filters': 'Clear filters',
  'app.no_results': 'Nothing here yet.',
  // A module the address names and this job does not run. Kept apart from
  // `app.no_results` on purpose: a module that is not here and a module that is
  // here with nothing in it are different answers, and this screen used to give
  // the second one to both.
  'module.absent.title': 'Not one of this job\u2019s modules',
  'module.absent.head': 'This job does not have that module.',
  'module.absent.body':
    'The address named a module this job does not run \u2014 a link copied from another job, or a typed address. That is not the same as a module with nothing in it. What this job does have is in the menu on the left.',
  'app.error_title': 'This could not be completed',
  'app.error.technical': 'Technical details',
  'app.error.technical_status': 'Response {status}',
  // A form the instance refused because fields were empty or not accepted.
  // Each field is named on its own line below this sentence.
  'app.error.fields': 'Some fields need attention.',
  'app.error.field_missing': '{field}: this is required.',
  'app.error.field_invalid': '{field}: this value is not accepted.',
  'app.unknown_address': 'That address does not exist',
  'app.unknown_address_body':
    'This project is open and nothing was refused. The address in the bar names a screen this build does not have — a typo, a link saved before a screen moved, or a path from another build.',
  'app.unknown_address_back': 'Open the overview for this project',
  // A screen whose code a deploy has replaced since this tab loaded (D1-05).
  'app.chunk_failed': 'This page could not load. Reload to get the latest version.',
  'app.reload': 'Reload',
  // The other door onto the same fault: an address whose *project* this
  // account cannot open, rather than an address whose screen does not exist.
  // One wording covers a project that does not exist and a project belonging
  // to somebody else, deliberately and permanently — see `UnavailableProject`.
  // The body used to explain that reasoning to the reader; it now says only
  // what is true in both cases and what to do about it (#288, E1-17).
  'app.unavailable_project': 'That project is not available',
  'app.unavailable_project_body':
    'This project is unavailable or you don’t have access. Ask your project administrator for access.',
  'app.unavailable_project_back': 'Go to My Projects',
  'app.offline_note': 'This browser is offline. Reconnect to load this screen.',
  'app.node': 'Answered by {origin}',
  'app.node_on_site': 'on site',
  'app.node_cloud': 'cloud',
  // What this site is set up to need before it calls a write saved
  // (Q-225). A browser has no outbox, so it says what the site is for
  // rather than where one record has got to.
  'app.node_copies_one': 'keeps {count} copy',
  'app.node_copies': 'keeps {count} copies',
  'app.stale_note':
    'These lists could not be refreshed just now, so they may be a little out of date. Nothing you have entered was lost.',
  // Said on a detail screen after an action that SUCCEEDED, where the sentence
  // above it reports that success. It has to be unmistakably about the reading
  // and not about the write, because the two notices sit one under the other
  // and a person who reads the second as "the issue failed" will press the
  // button again.
  'app.stale_record':
    'This record could not be refreshed just now, so what is shown may be a little out of date. Anything you have already done or entered is safe.',
  // Said on a panel whose list could not be refreshed after a write the person
  // just watched succeed. Like `app.stale_record` it has to be about the
  // reading rather than the write: the file was stored, the cycle was opened,
  // and the list below is simply older than that.
  'app.stale_list':
    'This list could not be refreshed just now, so it may not show everything yet. Anything you have already done is safe.',
  // And where the list it could not refresh is empty, because "nothing here"
  // is then a claim the screen has no grounds for.
  'app.stale_list_unknown':
    'This list could not be refreshed just now, so what is on the record cannot be shown here. Anything you have already done is safe.',
  'app.version': 'Version {version}',

  // The instance's own catalogue, and what the shell says when it did not
  // arrive. Written for whoever is standing in front of the screen rather than
  // for an administrator: that person cannot repair it, and the useful thing
  // they can do is report the right fault instead of "the screen is broken".
  'app.wording_unavailable':
    "This site's own wording could not be loaded, so some labels show as codes rather than sentences. The records themselves are unaffected; please tell an administrator.",

  // Searching the drawing set and the specification book. The scope labels
  // themselves are not here: they come off the response as message keys, so a
  // scope a module adds is named by that module rather than by this file.
  'documents.title': 'Document search',
  'documents.subtitle': 'The drawing set and the specification book, by number, title or what is written inside.',
  'documents.source': 'Look in',
  'documents.source.drawings': 'Drawings',
  'documents.source.specifications': 'Specifications',
  'documents.terms': 'Look for',
  'documents.placeholder': 'A number, a title, or a phrase',
  'documents.current_only': 'Current revisions only',
  'documents.ask': 'Type what you are looking for and press Search.',
  'documents.page': 'page {page}',
  'documents.superseded': 'Superseded',
  // Every scope that was searched is shown, so "nothing" here is an answer
  // rather than an absence: it says the search looked and did not find.
  'documents.none': 'nothing',
  'documents.none_here': 'Nothing matched in this one.',
  'documents.showing': '{count} of {total}',
  // The count stops at a cap, so past it the number is a floor rather than a
  // total, and the screen must not print a floor as though it were exact.
  'documents.at_least': '{total}+',

  // The company picker reads the project's own directory rather than every
  // firm the owner has ever dealt with, so it has two states worth words: a
  // project nobody has filled the directory in for, and a person on the job
  // whose firm was never added to it.
  'companies.none_on_project':
    'No companies are on this project yet. An administrator adds them in the project directory before work can be assigned.',
  'companies.person_off_the_book':
    'Some people on this project work for a firm that is not in the project directory, so they cannot be chosen here. An administrator adds the firm to the project to make them available.',
  'companies.no_people_at_company': 'Nobody from this company is on this project.',

  'signin.title': 'Sign in',
  'signin.trusted_header.title': 'Local development sign-in',
  'signin.trusted_header.explain':
    'This instance is running in trusted-header mode, which is for local development only. Enter the person you want to work as; the browser sends it as a header and nothing is verified.',
  'signin.trusted_header.label': 'Person identifier',
  'signin.trusted_header.submit': 'Continue',
  'signin.federated.title': 'Sign in with your SiteFocus account',
  'signin.federated.explain':
    'One account for the SiteFocus applications you use. It says who you are; what you may open here is decided here.',
  'signin.federated.submit': 'Continue with SiteFocus',
  'signin.federated.failed':
    'That sign-in did not finish. Try again, or sign in with a password below.',
  'signin.directory.title': 'Sign in with the SiteFocus Directory',
  'signin.directory.explain':
    'A deployment verifies a directory-issued token locally, so signing in keeps working with the uplink down. That flow is not wired into this browser yet.',
  'signin.directory.submit': 'Continue with the Directory',
  'signin.failed': 'That person could not be signed in on this instance.',
  'signin.password.title': 'Sign in',
  'signin.password.email': 'Email address',
  'signin.password.password': 'Password',
  'signin.password.submit': 'Sign in',
  'signin.password.forgot': 'Cannot sign in?',
  'signin.password.mail_link': 'Email me a link',
  'signin.password.reset_link': 'I have a reset code from an administrator',
  'signin.second_factor.asked':
    'Enter the six-digit code from your authenticator app to finish signing in.',
  'signin.second_factor.label': 'Authenticator code',
  // One message for every way a password sign-in can fail, because the API
  // answers with one: saying which of the address and the password was wrong is
  // a free round of elimination for anybody guessing at either.
  'signin.password.failed': 'That email address and password were not accepted.',
  'signin.expired': 'That sign-in is no longer valid. Sign in again.',
  'signin.unreachable': 'This site did not answer. Try again in a moment.',
  'signin.waiting':
    'Too many attempts. Try again in {seconds} seconds; the wait passes on its own.',
  'reset.title': 'Set a new password',
  'reset.heading': 'Your reset code',
  'reset.explain':
    'Ask whoever administers your directory for a reset code, then type it here with the password you want. A code lasts a few hours and works once.',
  'reset.code': 'Reset code',
  'reset.new_password': 'New password',
  'reset.again': 'New password again',
  'reset.submit': 'Set the password',
  'reset.back': 'Back to sign in',
  'reset.done': 'That password is set.',
  'reset.now_sign_in': 'Sign in',
  'reset.code_refused': 'That code was not accepted. It may have expired or already been used.',
  'reset.second_factor': 'Authenticator code',
  'reset.second_required':
    'This link also needs the six-digit code from your authenticator app.',
  'reset.second_refused': 'That authenticator code was not accepted. Try the next one.',
  'reset.second_unavailable':
    'This site cannot check an authenticator code yet. Ask an administrator for a reset code.',
  'reset.enrol_heading': 'Set up your authenticator app',
  'reset.enrol_explain':
    'Scan this with an authenticator app, then enter the six digits it shows.',
  'reset.enrol_by_hand': 'Or type this into the app by hand:',
  'reset.ask_for_a_link': 'Email me a link instead',
  'forgot.title': 'Email me a link',
  'forgot.heading': 'The address you sign in with',
  'forgot.explain':
    'We will email a link that lets you set a new password. The link lasts 30 minutes.',
  'forgot.submit': 'Send the link',
  'forgot.sent':
    'If that address has an account here, a link is on its way. Check your email.',
  'forgot.no_mail':
    'No email? Ask whoever administers the directory for a reset code.',
  'forgot.have_a_code': 'I already have a reset code',
  'reset.password_shape': 'That password is too short. Twelve characters or more.',
  'reset.mismatch': 'The two passwords are not the same.',
  'signin.no_projects':
    'You are signed in, but you are not a member of any project on this instance. Somebody with administrator access has to add you to one.',
  'signin.sign_out': 'Sign out',

  // The trailer tablet. Two audiences and they are kept apart on purpose: the
  // `device.*` strings are read by somebody standing at a tablet who has not
  // proved who they are, and the `device.admin.*` ones by an administrator at a
  // desk. Nothing in the first group may name anything the tablet has not been
  // told, which is why an unenrolled tablet's message says only what it is
  // called and who to ask.
  'device.who': 'Who is this?',
  'device.nobody':
    'Nobody is on this project yet, so there is no one to sign in. An administrator adds people to the project first.',
  'device.someone_else': 'Somebody else',
  'device.backspace': 'Undo',
  'device.entered': '{count} of 6 digits entered',
  'device.refused': 'That did not work. Check the name and try the code again.',
  'device.waiting':
    'The keypad is waiting {seconds} more seconds before it takes another code. Nothing is locked; it will take one again by itself.',
  'device.identifier': 'Device ID',
  'device.not_enrolled': 'This tablet is not set up for a project yet.',
  'device.not_enrolled_explain':
    'Give the identifier below to somebody with administrator access. Once they add this tablet to a project, reload this page and the crew will be listed.',
  'device.no_storage':
    'This browser will not let the page remember anything, so this tablet cannot be set up. Turn off private browsing, or allow site data, and open this page again.',
  'device.unreachable':
    'This tablet could not reach the site box. Check the network and try again.',
  // Said over a crew list the tablet already has, so it reports the refresh
  // and never suggests starting again: the list on the screen is usable and
  // the person reading this is part-way through a code.
  'device.list_stale':
    'The list could not be refreshed just now. This is the last one the tablet received; carry on.',

  'device.admin.title': 'Tablets',
  'device.admin.explain': 'Manage project tablets and user sign-in PINs.',
  'device.admin.enrol_title': 'Add a tablet',
  'device.admin.enrol_explain':
    'Open SiteFocus on the tablet and copy the Device ID from its sign-in screen. Deactivate the tablet in its current project before adding it here.',
  'device.admin.enrol_submit': 'Add tablet',
  'device.admin.enrolled': '{label} is now set up for this project.',
  'device.admin.label': 'Device name',
  'device.admin.list_title': 'Project tablets',
  'device.admin.none': 'No tablets are set up for this project yet.',
  'device.admin.enrolled_at': 'Added',
  'device.admin.show_withdrawn': 'Show inactive tablets',
  'device.admin.withdraw': 'Take out of service',
  'device.admin.withdrawn': 'That tablet is out of service, and any session open on it has ended.',
  'device.admin.withdrawn_at': 'Out of service since {when}',
  'device.admin.pin_title': 'Set a PIN',
  'device.admin.pin_explain':
    'Enter a six-digit PIN. The user selects their name on the tablet and enters this PIN to sign in. Setting a new PIN replaces the previous one and resets the sign-in retry delay.',
  'device.admin.pin_person': 'User',
  'device.admin.pin_label': 'New PIN',
  'device.admin.pin_submit': 'Set PIN',
  'device.admin.pin_set': 'That PIN is set. It is not shown again anywhere.',
  // Shown instead of the forms to anybody the instance would refuse (D1-16).
  'device.admin.manage_read_only':
    'Only an administrator of the directory, or somebody given the permission to manage tablets, can add a tablet or take one out of service.',
  'device.admin.pin_own_only':
    "You can set your own PIN here. Setting anybody else's needs an administrator of the directory, or the permission to manage people.",

  // The chooser at `/`. "My Projects" is the name on the screen and the name on
  // the way back to it in the app bar, so the same string is used for both: two
  // wordings for one destination is how somebody stops believing they are the
  // same place.
  'projects.title': 'My projects',
  'projects.explain':
    'The jobs you are a member of. A job you expect to see and cannot is a permission to be granted, not a setting on this screen.',
  'projects.you_can_open': 'You can open:',
  'projects.nothing_open':
    'You are a member of this job but hold no access on it yet.',
  // The applications beside CM. These are what the job has, never what this
  // person may do in them: QA/QC and lock-out/tag-out hold their own
  // permissions and Daily Report is separately hosted, so the caution is not
  // decoration. A link without it reads as an integration, which is the one
  // version of this that must not ship.
  'projects.beside.title': 'This job also has:',
  'projects.beside.qaqc': 'QA/QC',
  'projects.beside.loto': 'Lock-out/tag-out',
  'projects.beside.dailyreport': 'Daily Report',
  'projects.beside.no_address': '— this site has not been told where it is',
  'projects.beside.caution':
    'These are separate applications on their own addresses. Opening one does not sign you in to it and copies nothing between it and this job; it will ask who you are and refuse anybody it does not know.',
  // The three states of the second read. None of them says a job has nothing:
  // a read that did not arrive is not a job with no applications on it, and
  // saying so would be the confident wrong answer this screen exists to avoid.
  'projects.beside.reading': 'Still reading what else these jobs have.',
  'projects.beside.unavailable':
    'What else these jobs have could not be read, so nothing beside CM is shown. This says nothing about whether a job has QA/QC, lock-out/tag-out or Daily Report on it.',
  'projects.beside.stale':
    'What else these jobs have could not be refreshed. What is shown below was read earlier and may be out of date.',
  // The access administration screen. Its own namespace rather than under
  // `device.admin`, because the two are different jobs done by different
  // people: one enrols tablets on a job, the other decides what anybody on the
  // site can do.
  'access.title': 'Access',
  'access.explain':
    'Who can do what on this site, how to change it, and the record of every change that has been made.',
  'access.back': 'Back to the job',
  'access.scope': 'Job',
  'access.scope_explain':
    'Access is held per job. What somebody can do on one job says nothing about the next one.',
  'access.people_title': 'Who is on this job',
  'access.people_none': 'Nobody is on this job yet.',
  'access.grant_person_none': 'Everybody in the directory is already on this job.',
  'access.col_person': 'Person',
  'access.col_company': 'Company',
  'access.col_title': 'Job title',
  'access.col_state': 'Status',
  'access.active': 'On the job',
  'access.ended': 'Ended',
  'access.look': 'Look at their access',
  'access.holds_title': 'What {name} can do',
  'access.holds_explain': 'On {job}, as the server itself works it out.',
  'access.holds_nothing': 'Nothing on this job.',
  'access.on_jobs': 'On these jobs: {jobs}',
  'access.not_a_member': 'This person is not on this job, so they can reach nothing on it.',
  'access.show_all': 'Include the tools they cannot reach',
  'access.col_tool': 'Tool',
  'access.col_level': 'Level',
  'access.col_granulars': 'Also allowed',
  'access.col_from': 'Given by',
  'access.col_template': 'Template',
  'access.search': 'Find a person',
  'access.search_count': '{shown} of {all}',
  // The applications this platform is made of. Four lines that are deliberately
  // The three products this platform administers, named once. These are
  // identical to the ones the access administration screen adds on its own
  // branch: two screens naming the same three products two different ways is
  // how a vocabulary starts to drift, and an identical pair collapses to one
  // copy when the two land.
  'access.app.cm': 'Construction management',
  'access.app.qaqc': 'Quality',
  'access.app.loto': 'Lock-out/tag-out',
  'access.app.unknown': 'Application not recorded',
  'access.level.none': 'None',
  'access.level.read_only': 'Read Only',
  'access.level.standard': 'Standard',
  'access.level.admin': 'Admin',
  'access.nothing': 'nothing',
  'access.grant_title': 'Put somebody on this job',
  'access.grant_person': 'Who',
  'access.grant_company': 'For which company',
  'access.grant_job_title': 'Job title',
  'access.grant_template': 'What they may do',
  'access.grant_no_template': 'Nothing yet',
  'access.grant_submit': 'Put them on the job',
  'access.granted': '{name} is on this job.',
  'access.send_welcome': 'Send them the welcome message',
  'access.send_welcome_hint':
    'A mail that tells them who added them, opens the project, and explains how to get in. Sent only when they are new to this job.',
  'access.welcome.sent': 'The welcome message has been sent.',
  'access.welcome.no_route': 'No welcome message was sent: this site is not set up to send it.',
  'access.welcome.failed':
    'We could not confirm that the welcome message was sent, so tell them yourself.',
  'access.welcome.unconfirmed':
    'We could not confirm that the welcome message was sent, so tell them yourself.',
  'access.restricted_note': 'This list is what you have been permitted to hand out.',
  'access.change_title': 'Change what they may do',
  'access.change_template': 'New template',
  'access.preview_note':
    'This is what {name} allows. It is read here and changed on the templates screen, so everybody who holds it moves together.',
  'access.preview_edit': 'Open this template',
  'access.change_submit': 'Change it',
  'access.changed': "{name}'s access on this job has changed.",
  'access.end_title': 'End their access to this job',
  'access.end_explain':
    'The record of their time on the job stays. They keep whatever they hold on other jobs.',
  'access.end_submit': 'End it',
  'access.ended_done': '{name} is off this job.',
  'access.reason': 'Why',
  'access.reason_hint': 'This goes into the record below, where somebody will read it later.',
  'access.trail_title': 'What has been given and taken away',
  'access.trail_explain':
    'Who decided it, whose access it was, on which job, what they could do before and after, and any reason written down at the time.',
  'access.only_access': 'Only changes to somebody\u2019s access',
  'access.trail_none': 'Nothing has been recorded on this job yet.',
  'access.col_when': 'When',
  'access.col_actor': 'Who decided',
  'access.col_subject': 'Whose access',
  'access.col_scope': 'Where',
  'access.col_change': 'What changed',
  'access.col_reason': 'Why',
  'access.whole_site': 'the whole site',

  // The permission grid's own words. The row names come from the instance's
  // catalogue (`directory.permission_row.*`). The templates screen's words are
  // its own, further down; the grid's first column holds tools, not templates.
  'templates.grid_col_name': 'Name',
  'templates.granular': 'Granular Permissions:',
  'templates.coming_soon': '(Coming Soon)',

  // The queue of people who cannot reach something and have said so.
  'requests.title': 'Access requests',
  'requests.explain':
    'People who could not reach something and said so. Answering one is done where access is given; marking it here only says nobody is still waiting.',
  'requests.back': 'Back',
  'requests.status': 'Show',
  'requests.status.open': 'Still waiting',
  'requests.status.handled': 'Dealt with',
  'requests.status.withdrawn': 'Withdrawn',
  'requests.none': 'Nothing here.',
  'requests.col_when': 'When',
  'requests.col_who': 'Who asked',
  'requests.col_wanted': 'What they wrote',
  'requests.col_app': 'What they named',
  'requests.col_told': 'Announced',
  'requests.app_none': 'They did not say',

  // Shared with the queue screen, which shows the same request to whoever
  // answers it. Only the words that change person live under `ask.` -- a status
  // and a delivery read the same whoever is looking at them, and duplicating
  // them under two prefixes is how the two screens would come to disagree about
  // what "Dealt with" means.
  'requests.delivery.pending': 'Not yet',
  'requests.delivery.sent': 'Yes',
  'requests.delivery.no_route': 'Nobody was mailed',
  'requests.delivery.failed': 'The mail failed',
  'requests.unannounced':
    'Some of these were never announced by mail, so this page is the only place they exist. Nobody has been told about them except by being here.',
  'requests.note': 'Note',
  'requests.close_grants_nothing':
    'This grants nothing. Give the access where access is given; this only says nobody is still waiting.',
  'requests.close': 'Mark dealt with',
  'requests.withdraw': 'They withdrew it',
  'requests.closed': "{name}'s request is off the queue.",

  // Asking for access: the screen for somebody who holds nothing.
  'signin.ask_for_access': 'Ask for access',
  'ask.title': 'Ask for access',
  'ask.explain':
    'If a job you were told about is not on your list, or it is and the application you need is not, say so here.',
  'ask.grants_nothing':
    'This gives you nothing by itself. It reaches whoever answers for this site, and they decide.',
  'ask.wanted': 'What can you not reach?',
  'ask.no_identifier':
    'Describe it in your own words -- the job, the company, the work. There is nowhere to type a project number and you do not need one.',
  'ask.app': 'Which application',
  'ask.app_unsure': 'I am not sure',
  'ask.send': 'Send it',
  'ask.sent': 'Sent. It is waiting for whoever answers for this site.',
  'ask.open_title': 'What you asked for',
  'ask.app_none': 'You did not say which',
  'ask.told': 'Anybody told:',
  'ask.nobody_told':
    'Nobody has been mailed about this. It is still recorded and still waiting, and whoever answers for this site sees it when they open their queue.',
  'ask.one_at_a_time':
    'One at a time. While this one is open you cannot start another, so that nobody has four of yours to read.',
  'ask.withdraw': 'Take it back',
  'ask.earlier': 'Earlier',
  'ask.col_wanted': 'What you wrote',
  'ask.col_outcome': 'What happened',
  // Following an invitation link: what it offers, and accepting it.
  'invitation.title': 'Your invitation',
  'invitation.no_link':
    'This address carries no invitation. Open the link exactly as it was sent to you.',
  'invitation.sign_in_first':
    'Sign in to see this invitation. The link says what you are being offered; signing in says who you are, and the two have to agree.',
  'invitation.sign_in': 'Sign in',
  'invitation.for': 'Written for',
  'invitation.job': 'Job',
  'invitation.company': 'Company',
  'invitation.none_named': 'None named',
  'invitation.good_until': 'Good until',
  'invitation.expired': 'This invitation has expired. Ask whoever sent it for a new one.',
  'invitation.signed_in_as':
    'You are signed in as {email}. Only the person the invitation was written to can accept it.',
  'invitation.accept': 'Accept the invitation',
  'invitation.accepted': 'Accepted. What the invitation named is yours now.',
  'invitation.open_job': 'Open the job',
  'invitation.uncertain':
    'Your acceptance was sent and no answer came back, so it may already have gone through. Checking again sends the same acceptance under the same key, so one that did go through is answered with what it did rather than sent as a new one.',
  'invitation.check_again': 'Check again',
  'invitation.refused_after_uncertain':
    'That does not show your earlier acceptance failed, and it may have gone through. Checking again sends that same acceptance once more under the same key, so one that did go through is answered with what it did rather than sent as a new one.',
  // Opening a job and changing one that is open, on the administration screen.
  // `projectsetup.` rather than `projects.`: the chooser somebody lands on
  // after signing in is on its own branch and calls itself My Projects, and two
  // screens competing for `projects.title` would have given whichever landed
  // second the other one's heading. `requests.back` is shared with the access
  // screens on their own branches and is byte-identical to the copy there, so
  // the two collapse when they land.
  'projectsetup.title': 'Projects',
  'ownersites.title': 'Owner and site names',
  'ownersites.explain':
    'Correct the name of the owner and the name of each site, as everybody on this site sees them. Nothing about who may open what changes.',
  'ownersites.owner': 'Owner',
  'ownersites.site': 'Site',
  'ownersites.new_name': 'New name',
  'ownersites.rename_owner': 'Rename the owner',
  'ownersites.rename_site': 'Rename this site',
  'ownersites.owner_renamed': 'The owner is now called {name}.',
  'ownersites.site_renamed': 'The site is now called {label}.',
  'sitecomputers.title': 'Site computers',
  'sitecomputers.explain':
    'The computers on site that hold a copy of this site so it keeps working when the internet drops. Each one links itself with a one-time code, and each can be unlinked on its own.',
  'sitecomputers.address': 'Site',
  'sitecomputers.link_title': 'Link a site computer',
  'sitecomputers.link_explain':
    'Get a code here and type it at the site computer when it asks. The computer connects to the cloud by itself; nothing here reaches into it.',
  'sitecomputers.link_submit': 'Get a link code',
  'sitecomputers.linking_off':
    'Linking site computers is turned off on this site, so no link code is given out. Computers already linked can still be unlinked.',
  'sitecomputers.code_once': 'Type this at the site computer. It is shown once:',
  'sitecomputers.code_expires': 'It stops working at {when}, or once it is used.',
  'sitecomputers.list_title': 'Linked computers',
  'sitecomputers.show_unlinked': 'Show unlinked computers',
  'sitecomputers.none': 'No site computer is linked to this site.',
  'sitecomputers.name': 'Name',
  'sitecomputers.linked_at': 'Linked',
  'sitecomputers.last_heard': 'Last heard from',
  'sitecomputers.unlink': 'Unlink',
  'sitecomputers.unlinked': '{name} is unlinked. Its next call to the cloud is refused.',
  'sitecomputers.unlinked_at': 'Unlinked {when}',
  'directory.error.site_computer_not_found': 'That site computer is not linked to this site.',
  'projectsetup.explain':
    'The jobs at this site: what they are called, how they are numbered, and which are still in use.',
  'projectsetup.no_entitlements':
    'This page does not give anybody access to anything. Who may open a job, and what they may do on it, is set where access is set.',
  'projectsetup.open_title': 'Open a job',
  'projectsetup.address': 'Address',
  'projectsetup.number': 'Job number',
  'projectsetup.number_hint':
    'Your number for the job. It is the front of every RFI, submittal and deficiency number this job issues, so changing it later changes the numbers issued afterwards and never the ones already on a tag.',
  'projectsetup.name': 'Name',
  'projectsetup.currency': 'Currency',
  'projectsetup.mode': 'Delivery',
  'projectsetup.mode_hint':
    'This decides which modules the job has at all, and it only ever moves up: simple, then design-bid-build, then EPC.',
  'projectsetup.mode.simple': 'Simple',
  'projectsetup.mode.dbb': 'Design-bid-build',
  'projectsetup.mode.epc': 'EPC',
  'projectsetup.open': 'Open it',
  'projectsetup.opened': 'Job {number} is open.',
  'projectsetup.changed': 'Job {number} is changed.',
  'projectsetup.list_title': 'The jobs here',
  'projectsetup.show_inactive': 'Include jobs taken out of use',
  'projectsetup.none': 'No jobs here yet.',
  'projectsetup.no_address':
    'This site has no address yet. Add one first, and the job is opened at it.',
  'projectsetup.address_title': 'Add a site address',
  'projectsetup.address_label': 'Site name',
  'projectsetup.address_key': 'Short key',
  'projectsetup.address_key_hint':
    'Used in exports and host names: lowercase letters, digits and hyphens. It cannot be changed later.',
  'projectsetup.address_line1': 'Street',
  'projectsetup.address_city': 'City',
  'projectsetup.address_state': 'State',
  'projectsetup.address_postal': 'Postal code',
  'projectsetup.address_country': 'Country (two letters)',
  'projectsetup.address_zone': 'Time zone',
  'projectsetup.address_add': 'Add the address',
  'projectsetup.address_added': 'The address {label} is added. Now open the job at it.',
  'projectsetup.address_added_draft_kept':
    'The address {label} is added, as it was first sent. Your edits were not sent: they are still in the form, and adding them is a new address.',
  'projectsetup.address_unsettled':
    'No answer came back for the address {label} ({key}, {city}), so it may already be added. Sending it again sends it exactly as it was, and the server says what became of it.',
  'projectsetup.address_draft_waits':
    'What you have typed since is kept in the form and is not sent until that is settled.',
  'projectsetup.address_resend': 'Send it again unchanged',
  'projectsetup.address_set_aside': 'Set it aside',
  'projectsetup.address_another': 'Add another address',
  'projectsetup.col_number': 'Number',
  'projectsetup.col_name': 'Name',
  'projectsetup.col_mode': 'Delivery',
  'projectsetup.col_numbering': 'Next number reads',
  'projectsetup.col_ai': 'AI',
  'projectsetup.out_of_use': 'Taken out of use',
  'projectsetup.rename': 'Rename',
  'projectsetup.take_out': 'Take out of use',
  'projectsetup.put_back': 'Put back in use',
  // The project's own AI switch. Off is the state a job opens in, and a job
  // with it off gets a provider that refuses and says which switch did it,
  // rather than a quietly weaker one, so the word here is on or off and never
  // "available".
  'projectsetup.ai_on': 'On',
  'projectsetup.ai_off': 'Off',
  'projectsetup.ai_switch_on': 'Switch AI on',
  'projectsetup.ai_switch_off': 'Switch AI off',
  'projectsetup.ai_explain':
    'With it off, every feature that asks a model refuses and names this switch: the drawing comparisons and their narratives, the automatic sheet checks, and the specification extractions. Nothing already generated is removed by switching it off.',
  // A switch write nobody has an answer for. It names the value that was
  // asked for rather than the row's, because the row is the thing nobody
  // knows the state of, and it says what sending it again does, so nobody
  // has to guess whether pressing it twice sets it twice.
  'projectsetup.ai_unsettled':
    'No answer came back for the AI switch on {number}, so it may already be {state}. Sending it again sends exactly that, under the same key, and the server says what became of it.',
  // The value that was asked for, written as the sentence above needs it
  // rather than as the column writes it: the column labels a state that is
  // known and this one says what may have become of it.
  'projectsetup.ai_asked_on': 'switched on',
  'projectsetup.ai_asked_off': 'switched off',
  'projectsetup.ai_send_again': 'Send it again',

  // What a job is called in the other applications a site works with. The
  // explanation is the first thing on the panel rather than a note under it:
  // a box that takes the other side's number, beside a job, reads as the thing
  // that connects the two, and nobody who reads it that way will read further.
  'projectsetup.external.open': 'Other applications',

  // Which of the applications beside this one a job is offered a way through
  // to. The sentence under the heading and the notice under the switches are
  // both load-bearing: a control on a CM screen labelled QA/QC is exactly what
  // somebody reads as handing out QA/QC access, and it is not that.
  'projectsetup.apps.title': 'The other applications on this job',
  'projectsetup.apps.explain':
    'Whether a way through to each of these appears on this job. Turning one on does not give anybody access to it: that application decides who may do anything there, on its own terms, and refuses anybody it has not authorised.',
  'projectsetup.apps.not_a_grant':
    'These switches grant nothing. Nobody gains QA/QC, lock-out or Daily Report access by being on this job, and nothing here is sent to those applications.',
  'projectsetup.apps.qaqc': 'QA/QC',
  'projectsetup.apps.loto': 'Lock-out/tag-out',
  'projectsetup.apps.dailyreport': 'Daily Report',
  'projectsetup.external.title': 'What this job is called elsewhere',
  'projectsetup.external.explain':
    'A note of which job this is in another application, so somebody looking at both knows they are the same one. It connects nothing, gives nobody access to anything there, and nothing here reads it to fetch anything. Making the connection itself is done in the other application.',
  'projectsetup.external.none': 'Nothing recorded for this job.',
  'projectsetup.external.system': 'Application',
  'projectsetup.external.system_hint':
    'A short name for the other application, in lower case: dailyreport, or whatever this site calls it. The same spelling every time, because it is what the lists group by.',
  'projectsetup.external.reference': 'Its number for this job',
  'projectsetup.external.reference_hint':
    'Exactly as it is written over there, including capitals, punctuation and any leading zeroes. A tidied-up reference stops matching the thing it names.',
  'projectsetup.external.note': 'How you know',
  'projectsetup.external.note_hint':
    'Optional, and the part worth writing. "The PM confirmed it on the handover call" and "the numbers matched" are very different claims about the same job.',
  'projectsetup.external.record': 'Record it',
  'projectsetup.external.recorded': 'Recorded.',
  'projectsetup.external.take_back': 'Take it back',
  'projectsetup.external.unsettled_record':
    'No answer came back for recording {reference} in {system}, so it may already be recorded. Sending it again sends exactly that, under the same key, and the server says what became of it.',
  'projectsetup.external.unsettled_withdraw':
    'No answer came back for taking back {reference}, so it may already be taken back. Sending it again sends exactly that, under the same key, and the server says what became of it.',
  'projectsetup.external.send_again': 'Send it again',
  'projectsetup.external.withdraw': 'Take it back',
  'projectsetup.external.why_wrong': 'Why it is wrong',

  // The server's own refusals, in the server's own words. `tOr` falls back to
  // the bare key, and a person reading `directory.error.` anything has been
  // shown a defect rather than an answer.
  'directory.error.access_request_already_open':
    'You already have a request open. It has not been answered yet.',
  'directory.error.unknown_application':
    'That is not one of the applications somebody can ask about here.',
  'directory.error.delivery_mode_moves_up':
    "A project's delivery mode only moves up, and this one is already further along than that. The mode it is on now is on the list above.",
  'directory.error.address_not_found': 'That address is not on this site.',
  'directory.error.external_system_shape':
    'The application name is lower-case letters, digits and hyphens: dailyreport, not Daily Report.',
  // No arguments in this one on purpose, and not because they could not be
  // rendered: a refusal's values do reach `ErrorNotice` and are put into the
  // sentence. This refusal simply does not send one, so a sentence with
  // {reference} in it would be shown with the braces still there. The
  // reference they would have been told is on the list above the form anyway.
  'directory.error.external_ref_already_standing':
    'This job already has a reference for that application. Take that one back before recording another.',
  'directory.error.external_ref_not_found': 'That reference is not here.',

  // The two document modules' refusals, for the same reason: loading a set or
  // a book is the one place in this application where an ordinary person meets
  // a refusal on a routine day -- a duplicated page, a label already used, a
  // scan with no text in it -- and a screen that answered them with
  // `drawings.error.cannot_publish` would be telling them nothing.
  //
  // `{detail}` is the module's own sentence, which names the duplicated number
  // or the clashing label. It reaches the screen: a refusal's arguments are
  // carried on `ApiCallError` and handed to `tOr`.
  'drawings.error.empty_upload': 'This file is empty.',
  'drawings.error.set_already_published':
    'This set is already published and can\'t be edited.',
  'drawings.error.bad_region':
    'Draw a box with some width and height.',
  'drawings.error.cannot_publish': 'Couldn\'t publish the set: {detail}.',
  'drawings.error.no_set': 'This set isn\'t in this project.',
  'drawings.error.no_entry': 'This page isn\'t in this set.',
  'drawings.error.number_taken':
    'Sheet {number} already exists in this project. Use a different number.',
  'drawings.error.rename_key_reused':
    'That save was already used for a different change, so nothing was saved. Make the change again.',
  'drawings.error.bad_sheet_number': 'Enter a sheet number.',
  'drawings.error.bad_sheet_title': 'Enter a sheet title.',
  'drawings.error.bad_discipline': 'Choose a discipline.',
  'drawings.error.bad_set_name': 'Enter a set name.',
  'drawings.error.scale_key_reused':
    'That save was already used for a different scale, so nothing was saved. Set the scale again.',
  'specifications.error.empty_upload': 'That file is empty. Nothing was loaded.',
  'specifications.error.not_a_pdf':
    'That file could not be opened as a PDF, so nothing was loaded. Choose a PDF that is not damaged or password-protected.',
  'specifications.error.book_already_published':
    'This book has been issued, so it can no longer be corrected.',
  'specifications.error.bad_page_range':
    'A section cannot end before it starts. Check which page it runs to.',
  'specifications.error.cannot_publish': 'The book was not issued, because {detail}.',
  'specifications.error.no_book': 'That book is not on this project.',
  'specifications.error.no_candidate': 'That section is not in this book.',
  'markup.error.pin_query_needs_an_end':
    'Ask for the pins on a document or the pins of a record, and name that one whole.',
  'markup.error.not_that_documents_file':
    'That file is not the one this record is a page of.',
  'markup.error.action_key_reused':
    'That change has already been made to a different mark.',
  // A person named on a record, wherever the screen names one. Never the id:
  // a reader cannot do anything with a UUID, and a person who has left is
  // said to have left only when the server says so.
  'people.name.unavailable': 'Author unavailable',
  'people.name.loading': '…',
  'people.name.left_project': '{name} (no longer on this project)',
  'people.admin.title': 'People',
  'people.admin.explain':
    'Everybody this site knows, and which of them a sign-in provider can sign in as. Every control on this screen is administration \u2014 on your own row as much as on anybody else\u2019s \u2014 because the identifier below decides who signs in as a person.',
  'people.admin.list_title': 'People on this site',
  'people.admin.never_shown':
    'An identifier is recorded here and never shown again. This page can say whether somebody has one; it cannot say what it is, and nothing here can ask. To correct a wrong one, clear it and record the right one from the provider\u2019s own console.',
  'people.admin.show_inactive': 'Include people who have been deactivated',
  'people.admin.none': 'Nobody is on this site yet.',
  'people.admin.person': 'Name',
  'people.admin.email': 'Email',
  'people.admin.identifier': 'Provider identifier',
  'people.admin.linked': 'Recorded',
  'people.admin.not_linked': 'Not recorded',
  'people.admin.record': 'Record one',
  'people.admin.replace': 'Replace it',
  'people.admin.clear': 'Clear it',
  'people.admin.record_title': 'Record the identifier for {who}',
  'people.admin.record_purpose':
    'This links this person to their account with an external sign-in provider, such as your company\u2019s single sign-on service. The provider identifier is the unique account ID supplied by that service, not a password or verification code.',
  'people.admin.record_when':
    'If this person uses a SiteFocus password to sign in and you are not setting up provider sign-in, leave this blank and choose Cancel. If you are unsure which ID to use, ask the administrator who manages your sign-in provider before saving.',
  'people.admin.record_explain':
    'Copy it from the provider\u2019s own console. It is stored as given and never shown here again. An identifier already recorded against somebody else is refused rather than moved, because the alternative is two people signing in as each other.',
  'people.admin.record_submit': 'Record it',
  'people.admin.ends_sessions':
    'Recording, replacing or clearing an identifier signs that person out of the provider sign-ins this box knows about, and cancels a sign-in link already sent to them \u2014 on your own row as much as on anybody else\u2019s. Another box signs them out as this change reaches it, so a trailer that is off the network goes on serving the session it already gave until it is back in touch. A password sign-in is not affected, and saving the same identifier again changes nothing and signs nobody out.',
  'people.admin.clear_ask':
    'Clear it? {who} is signed out here at once, and on every other box as this reaches it.',
  'people.admin.clear_confirm': 'Clear it and sign them out',
  'people.admin.recorded':
    '{who} is linked to the provider. The identifier is not shown again anywhere.',
  'people.admin.cleared': '{who} is no longer linked to the provider.',
  'people.admin.password': 'Password',
  'people.admin.password_set': 'Set',
  'people.admin.password_none': 'None yet',
  'people.admin.password_unread': 'Could not be read',
  'people.admin.code_outstanding': 'A code is outstanding until {until}.',
  'people.admin.issue': 'Issue a one-time code',
  'people.admin.issue_ask':
    'Issue a code for {who}? Whoever holds it can set their password, and it replaces any code already outstanding.',
  'people.admin.issue_confirm': 'Issue the code',
  'people.admin.code_title': 'One-time code for {who}',
  'people.admin.code_once':
    'Shown once, here and nowhere else. It is not kept anywhere it can be read back, so hand it over yourself, in person or by phone, and do not paste it into a message.',
  'people.admin.code_use':
    '{who} follows \u201c{link}\u201d on the sign-in page, enters it, and chooses their own password. It is good until {until}. Issuing another replaces this one.',
  'people.admin.code_not_returned':
    'No code came back with this answer, so none can be shown. Issue another; it replaces any that is outstanding.',
  'people.admin.code_dismiss': 'Handed over \u2014 hide it',

  'nav.title': 'Project tools',
  'nav.home': 'Overview',
  'nav.no_access': 'No access',
  'nav.preview': 'Preview',
  // The address a project sits at, shown beside the project so a shared site
  // reads as the parent it is rather than as the name of the job.
  'nav.at_address': 'at {address}',
  'nav.project_switcher': 'Project',
  'nav.switch_project': 'Switch project',
  'nav.delivery_mode.simple': 'Simple',
  'nav.delivery_mode.dbb': 'Design-Bid-Build',
  'nav.delivery_mode.epc': 'EPC',
  'tools.caption': 'Project tools',
  'tools.menu': 'All project tools',
  'tools.group.core': 'Core',
  'tools.group.project': 'Project management',
  'tools.group.field': 'Field and quality',
  'tools.group.cost': 'Cost and change',
  'tools.group.other': 'Other tools',
  'app.account': 'Signed in as {name}',
  'admin.caption': 'Company admin',
  // The one place the directory, People, Access and the request queue sit
  // together. The title is the directory module's own name, so the menu entry
  // and the area it opens read the same; the scopes say whose each section is.
  'area.title': 'Directory and permissions',
  'area.scope.job': 'This job',
  'area.scope.site': 'Whole site',
  'area.scope.every_job': 'Each job on this site',
  'admin.rail': 'Administration',
  'admin.rail.access': 'Roles and permissions',
  'admin.rail.requests': 'Requests for access',
  'admin.rail.projects': 'Projects and sites',
  'admin.rail.site_computers': 'Site computers',
  'admin.rail.templates': 'Permission templates',
  'admin.rail.integrations': 'Integrations',
  'admin.rail.points': 'Points',
  'points.title': 'What each action is worth',
  'points.explain':
    'The points each action earns on every project of this site. A change applies from the first week that has not yet begun on any project: usually next week, or the week after when it is made late on a Sunday (UTC). It starts on Monday on each project\'s own calendar, never in the middle of a week, and the table shows that Monday. Work already done keeps what it earned.',
  'points.col_shipped': 'As shipped',
  'points.col_now': 'This week (UTC)',
  'points.calendar':
    'This week is counted in UTC here. Each project changes price at Monday 00:00 on its own calendar, so over a weekend a project can be a few hours ahead of or behind this column.',
  'points.not_allowed': 'Only a super admin or the global admin can set what each action is worth.',
  'points.saving': 'Saving…',
  'points.unsettled':
    'The change of {action} to {points} points was sent and no answer came back. Send it again to find out whether it was saved.',
  'points.retry': 'Send it again',
  'points.col_coming': 'Coming',
  'points.col_new': 'New points',
  'points.coming': '{points} from {from}',
  'points.new_for': 'New points for {action}',
  'points.save': 'Save',
  'points.saved': '{action} will earn {points} points from {from}.',
  'admin.rail.signin_policy': 'Sign-in',
  'admin.rail.administration': 'Administration',
  'admin.rail.wall_screens': 'Wall screens',
  'administration.title': 'Administration',
  'administration.explain':
    'The people who run this site. The Global Admin holds every right on every job. A Super Admin sets jobs up: sites, projects, people, templates and invitations.',
  'administration.read_only': 'Only the Global Admin can change who is a Super Admin.',
  'administration.not_yours': 'This page is for the Global Admin and the Super Admins.',
  'administration.to_projects': 'My Projects',
  'administration.globals': 'Global Admin',
  'administration.seat_from_setting':
    'The Global Admin seat is set by the deployment setting SITEFOCUS_GLOBAL_ADMIN_EMAIL and is not changed on this page.',
  'administration.organization': 'Super Admins',
  'administration.none': 'Nobody yet.',
  'administration.col_name': 'Name',
  'administration.col_email': 'Email',
  'administration.you': 'You',
  'administration.switched_off': 'Switched off',
  'administration.remove': 'Remove',
  'administration.remove_who': 'Remove {name}',
  'administration.removed': '{name} no longer holds that role.',
  'administration.add_title': 'Add a Super Admin',
  'administration.name': 'Name',
  'administration.email': 'Email',
  'administration.email_hint':
    'If somebody on the site already has this address, they are given the role and their details are left as they are.',
  'administration.role.global_admin': 'Global Admin',
  'administration.role.super_admin': 'Super Admin',
  'administration.add': 'Add',
  'administration.added': '{name} is added as {role}.',
  'administration.projects': 'Projects',
  'administration.add_project': 'Add a project',
  'administration.elsewhere': 'More administration',
  'signin_policy.title': 'Sign-in',
  'signin_policy.explain':
    'How somebody added to a project gets in. Each choice starts where the site has always been, and a change applies from the next welcome sent.',
  'signin_policy.welcome_button': 'The welcome email\u2019s main button',
  'signin_policy.welcome_button.hint':
    'For somebody who has not set a password yet. Somebody who already has one always gets the button that opens the project.',
  'signin_policy.welcome_button.project':
    'Open the project, with the link that sets the password below it',
  'signin_policy.welcome_button.password':
    'Set the password, with the project\u2019s address below it',
  'signin_policy.default': '(the default)',
  'signin_policy.saved': '{name} is saved.',
  'signin_policy.changed_by_you': 'Last changed by you, {when}.',
  'signin_policy.changed_by_someone': 'Last changed by another administrator, {when}.',
  'signin_policy.never_changed': 'Never changed.',
  'integrations.title': 'Integrations',
  'integrations.explain':
    'The keys this site uses to send mail and to run the AI-assisted features. A key saved here is used instead of the one in the server settings, from the next mail or AI job. Once saved, a key is never shown again.',
  'integrations.col_key': 'Key',
  'integrations.col_state': 'Saved here',
  'integrations.col_changed': 'Last changed',
  'integrations.col_new': 'New value',
  'integrations.set': 'Set',
  'integrations.not_set': 'Not set',
  'integrations.fingerprint': 'Fingerprint {fingerprint}',
  'integrations.by_you': 'by you',
  'integrations.by_someone': 'by another administrator',
  'integrations.new_value': 'Paste a new value',
  'integrations.choose_value': 'Choose a new value',
  'integrations.choose_none': 'Choose…',
  'integrations.chosen': 'Chosen: {value}',
  'integrations.save': 'Save',
  'integrations.clear': 'Clear',
  'integrations.saved': '{name} is saved.',
  'integrations.cleared': '{name} is cleared. The server setting, if there is one, is used again.',
  'templates.title': 'Permission templates',
  'templates.explain':
    'What each template hands a person when they are put on a job: one level per tool. Changing a template changes it for everybody who holds it.',
  'templates.list_title': 'Templates on this site',
  'templates.none': 'There are no templates yet.',
  'templates.col_name': 'Template',
  'templates.col_kind': 'Applies to',
  'templates.col_assigned': 'Held by',
  'templates.col_admin_on': 'Tools at Admin',
  'templates.col_tool': 'Tool',
  'templates.kind.company': 'The company',
  'templates.kind.project_global': 'Any job',
  'templates.kind.project_specific': 'One job',
  'templates.group.project_global': 'For any job',
  'templates.group.project_specific': 'Written for one job',
  'templates.group.company': 'For a company',
  'grid.held': 'Held',
  'templates.people_one': '{count} person',
  'templates.people': '{count} people',
  'templates.shipped': 'Shipped',
  'templates.new_title': 'Start a new template',
  'templates.name': 'Name',
  'templates.new_hint':
    'It starts with every tool at None. To start from an existing template instead, open it and make a copy.',
  'templates.new_submit': 'Create the template',
  'templates.back': 'All templates',
  'templates.missing': 'That template is not on this site.',
  'templates.grid_caption': 'What {name} allows, tool by tool',
  'templates.level_for': '{tool}: {level}',
  'templates.assigned_note':
    'People already hold this template. Saving changes what they can do as soon as the change reaches their box.',
  'templates.save': 'Save the template',
  'templates.undo': 'Put it back',
  'templates.saved': '{name} saved.',
  'templates.copy_title': 'Make a copy',
  'templates.copy_name': 'Name of the copy',
  'templates.copy_hint': 'The copy starts with exactly this grid and is held by nobody until somebody is given it.',
  'templates.copy_submit': 'Copy it',

  'dir.title': 'Project directory',
  'dir.explain': 'Everybody on this job, under their company, with the template that sets what they can do.',
  'dir.tab.people': 'People',
  'dir.tab.companies': 'Companies',
  'dir.tab.groups': 'Groups',
  'dir.tab.roles': 'Roles',
  'dir.tab.teams': 'Teams',
  'teams.project_route': 'Project escalation route',
  'teams.project_route_hint':
    'Used for work on a Team with no route of its own and no parent with one. Each tier hands the task to one person; the wait before each tier is set on the review step, not here.',
  'teams.change_route': 'Change the project route',
  'teams.route_none': 'No route published yet.',
  'teams.route_version': 'Version {version}',
  'teams.tier': 'Tier {number}: {name}',
  'teams.tier_cc': 'copied to {names}',
  'teams.route_own': 'Escalates along its own route.',
  'teams.route_parent': 'Escalates along {name}’s route.',
  'teams.route_project': 'Escalates along the project route.',
  'teams.route_gap': 'No route reaches this Team, so work on it will not escalate. Publish a route for it, a parent Team or the project.',
  'teams.new': 'New Team',
  'teams.count_one': '{count} active Team',
  'teams.count': '{count} active Teams',
  'teams.none': 'No Teams on this job yet.',
  'teams.withdrawn': '(withdrawn)',
  'teams.leader': 'Leader: {name}',
  'teams.pm': 'Team PM: {name}',
  'teams.no_members': 'No other members',
  'teams.edit': 'Edit',
  'teams.edit_named': 'Edit {name}',
  'teams.set_route': 'Set its route',
  'teams.set_route_named': 'Set the route for {name}',
  'teams.company': 'Company',
  'teams.name': 'Team name',
  'teams.parent': 'Under',
  'teams.parent_none': 'No parent Team',
  'teams.leader_field': 'Team leader',
  'teams.pm_field': 'Team PM',
  'teams.pm_none': 'No Team PM',
  'teams.members': 'Members',
  'teams.members_hint': 'The leader and the Team PM are members whether or not they are ticked.',
  'teams.active': 'Active (untick to withdraw the Team)',
  'teams.save': 'Save the Team',
  'teams.create': 'Create the Team',
  'teams.cancel': 'Cancel',
  'teams.route_form_hint':
    'Publishing makes a new version. Work already escalating keeps the version it started with.',
  'teams.tier_legend': 'Tier {number}',
  'teams.primary': 'Hands the task to',
  'teams.cc': 'Copies (optional)',
  'teams.add_tier': 'Add a tier',
  'teams.remove_tier': 'Remove the last tier',
  'teams.publish': 'Publish the route',
  'teams.sending': 'Saving…',
  'teams.unsettled':
    'No answer came back for that save, so it may already be on the job. Sending it again sends exactly that, under the same key, and the server says what became of it.',
  'teams.send_again': 'Send that save again',
  'teams.withheld':
    'This job\'s Teams and routes are no longer shown to you. They come back when the instance answers for you again.',
  'continuity.open': 'Backup and time off for {name}',
  'continuity.summary': 'Backup and time off',
  'continuity.backup': 'Backup',
  'continuity.backup_none': 'No backup',
  'continuity.backup_hint':
    'A backup can take the actions this person can, from the moment they are appointed, whether or not the person is away. A backup does not see this person’s private records, restricted documents or files unless the backup holds that access in their own right.',
  'continuity.save_backup': 'Save the backup',
  'continuity.time_off': 'Time off',
  'continuity.status.available': 'Available',
  'continuity.status.pto': 'PTO',
  'continuity.status.away': 'Away',
  'continuity.now_pto': 'On PTO from {from} until {until}.',
  'continuity.now_away': 'Away from {from} until {until}.',
  'continuity.open_ended': 'further notice',
  'continuity.starts': 'From',
  'continuity.ends': 'Until (optional)',
  'continuity.note': 'Note (optional)',
  'continuity.pto_hint':
    'In this release, PTO hands no reviews over to the backup. The backup can act for this person from the moment they are appointed, whether or not the person is away.',
  'continuity.save_time_off': 'Save the time off',
  'continuity.sending': 'Saving…',
  'continuity.unsettled':
    'No answer came back for that save, so it may already be on record. Sending it again sends exactly that, under the same key.',
  'continuity.send_again': 'Send that save again',
  'continuity.withheld':
    'This person\'s backup and time off are no longer shown to you. They come back when the instance answers for you again.',
  'teams.reconciling': 'Bringing the Teams up to date with that save before editing opens again…',
  'dir.roles_count_one': '{count} role',
  'dir.roles_count': '{count} roles',
  'dir.roles_none': 'No roles are defined on this site yet.',
  'dir.col_seat': 'Role',
  'dir.col_holders': 'Held by',
  'dir.col_add_holder': 'Add somebody',
  'dir.role_empty': 'Nobody yet',
  'dir.role_add': 'Add',
  'dir.role_pick': 'Person to add as {role}',
  'dir.role_remove': 'Take {name} off {role}',
  'dir.roles_change': '{name}: {roles}',
  'dir.roles_change_none': 'no roles',
  'dir.roles_sending': 'Saving {change}…',
  'dir.roles_unsettled':
    'No answer came back for {change}, so it may already be saved. Sending it again sends exactly that, under the same key, and the server says what became of it.',
  'dir.roles_unsettled_hint':
    'The seats stay shut until this is settled: a change built from the list on screen now could undo it.',
  'dir.roles_send_again': 'Send that change again',
  'dir.roles_reconciling': 'Bringing the list up to date with that change before the seats open again…',
  'dir.roles_note': 'A role is a seat that reviews are routed to. It grants nothing on its own: what somebody can open still comes from their permission template.',
  'court.role.mep_coordinator': 'MEP coordinator',
  'court.role.engineer_of_record': 'Engineer of record',
  'dir.actions': 'Directory actions',
  'dir.add_person': 'Add a person',
  'dir.add_company': 'Add a company',
  'dir.link_access': 'Roles and permissions',
  'dir.link_templates': 'Permission templates',
  'dir.link_history': 'Change history',
  'dir.show_inactive': 'Show people who have left the job',
  'dir.filter_company': 'Show one company',
  'dir.all_companies': 'All companies',
  'dir.sort_by': 'Sort by',
  'dir.sort_name': 'Name',
  'dir.sort_email': 'Email',
  'dir.people_count_one': '{count} person',
  'dir.people_count': '{count} people',
  'dir.people_none': 'Nobody matches.',
  'dir.col_name': 'Name',
  'dir.col_job_title': 'Job title',
  'dir.col_contact': 'Contact',
  'dir.col_template': 'Permission template',
  'dir.col_actions': 'Welcome and password',
  'dir.resend_welcome': 'Send login email',
  'dir.reset_email': 'Send password reset email',
  'dir.reset_email_asked': 'Request received. If this account is eligible, a reset email will be sent.',
  'dir.reset_check_failed':
    'Whether {name} has a password could not be checked, so no reset was requested. Press the button again to try.',
  'dir.send_uncertain':
    'The request was sent and no answer came back, so the email may already have been sent. Pressing the button again makes another request.',
  'dir.reset_needs_code':
    '{name} has never set a password, so no reset link was sent. Send them a login email instead: it carries the link that lets them set one. Or issue them a one-time code from the people screen.',
  'dir.reset_needs_code_link': 'Open the people screen',
  'dir.no_company': 'No company',
  'dir.inactive': 'left the job',
  'dir.no_template': 'No template',
  'dir.template_unnamed': 'A template',
  'dir.administrator': 'Administrator',
  'dir.companies_count_one': '{count} company',
  'dir.companies_count': '{count} companies',
  'dir.companies_none': 'No company is on this job yet.',
  'dir.col_company': 'Company',
  'dir.col_role': 'Role on the job',
  'dir.col_trade': 'Trade',
  'dir.col_people': 'People',
  'dir.role.owner': 'Owner',
  'dir.role.owner_rep': "Owner's representative",
  'dir.role.gc': 'General contractor',
  'dir.role.designer': 'Designer',
  'dir.role.consultant': 'Consultant',
  'dir.role.trade': 'Trade contractor',
  'dir.role.vendor': 'Supplier',
  'dir.groups_count_one': '{count} group',
  'dir.groups_count': '{count} groups',
  'dir.groups_none': 'There are no groups yet.',
  'dir.col_group': 'Group',
  'dir.col_scope': 'Used on',
  'dir.col_members': 'Members',
  'dir.scope_job': 'This job',
  'dir.scope_site': 'Every job',
  'dir.f_name': 'Full name',
  'dir.f_email': 'Email',
  'dir.f_phone': 'Phone',
  'dir.f_company': 'Company',
  'dir.f_job_title': 'Job title',
  'dir.f_template': 'Permission template',
  'dir.f_company_name': 'Company name',
  'dir.template_admin': '{name} (administrator)',
  'dir.template_hint': 'A template marked administrator lets this person add and change people on the job.',
  'dir.person_made_not_placed':
    'The person was created but not yet added to this job. Fix what is shown below and add them again; they will not be created twice.',
  'dir.add_person_submit': 'Add to the job',
  'dir.person_added': '{name} added to the job.',
  'dir.add_company_submit': 'Add the company',
  'dir.company_added': '{name} added to the job.',

  'module.CM-PUN': 'Punch list',
  'module.CM-MTG': 'Meetings',
  'module.CM-COR': 'Correspondence',
  'module.CM-CHG': 'Change management',
  'module.CM-TEN': 'Directory and permissions',
  'module.CM-RFI': 'RFIs',
  'module.CM-SUB': 'Submittals',
  'module.CM-DLY': 'Daily log',
  'module.CM-FRP': 'Field report',
  'module.CM-PHO': 'Photos and media',
  'module.CM-DWG': 'Drawings',
  'module.CM-SPC': 'Specifications',
  'module.CM-MDL': 'Models',
  'module.CM-DOC': 'Document repository',
  'module.CM-MKP': 'Markup and review canvas',
  'module.CM-SCH': 'Schedule',
  'module.CM-FBK': 'Feedback',
  'module.CM-XMP': 'Example',
  'module.CM-WFA': 'Workforce',
  'module.CM-INS': 'QA/QC and lockout',
  'module.CM-RPT': 'Custom fields',
  'module.CM-SCP': 'Scope divider and features of work',
  'module.CM-EQP': 'Equipment register',

  // The equipment register and handover. Tags are identifiers and are never
  // keyed; the refusals come from the instance's own catalogue.
  'equipment.title': 'Equipment register',
  'equipment.explain':
    'The installed equipment this project hands over: each unit, where it is, its documents, and whether it has been handed over.',
  'equipment.count': '{count} units',
  'equipment.count_one': '1 unit',
  'equipment.register': 'Register a unit',
  'equipment.register.save': 'Register',
  'equipment.find': 'Find by code',
  'equipment.find.open': 'Open',
  'equipment.back': 'Back to the register',
  'equipment.part_of': 'Part of {tag}',
  'equipment.field.tag': 'Tag',
  'equipment.field.tag_hint':
    'Exactly as it appears on the unit and the drawings. It cannot be changed once registered.',
  'equipment.field.description': 'Description',
  'equipment.field.type': 'Equipment type',
  'equipment.field.location': 'Location',
  'equipment.field.no_location': 'No location',
  'equipment.field.parent': 'Part of',
  'equipment.field.status': 'Status',
  'equipment.field.missing': 'Missing for handover',
  'equipment.field.manufacturer': 'Manufacturer',
  'equipment.field.model': 'Model',
  'equipment.field.serial': 'Serial number',
  'equipment.field.warranty': 'Warranty',
  'equipment.field.warranty_starts': 'Warranty starts',
  'equipment.field.warranty_ends': 'Warranty ends',
  'equipment.field.registered_by': 'Registered by',
  'equipment.status.planned': 'Planned',
  'equipment.status.delivered': 'Delivered',
  'equipment.status.installed': 'Installed',
  'equipment.status.commissioned': 'Commissioned',
  'equipment.status.handed_over': 'Handed over',
  'equipment.status.changed': 'Status set to {status}.',
  'equipment.kind.om_manual': 'O&M manual',
  'equipment.kind.warranty': 'Warranty',
  'equipment.kind.commissioning': 'Commissioning record',
  'equipment.kind.test_report': 'Test report',
  'equipment.kind.certificate': 'Certificate',
  'equipment.kind.other': 'Other',
  'equipment.missing.none': 'Nothing missing',
  'equipment.missing.list': 'Still needed for handover: {kinds}.',
  'equipment.section.unit': 'The unit',
  'equipment.section.status': 'Status and handover',
  'equipment.section.documents': 'Documents',
  'equipment.section.parts': 'Parts',
  'equipment.edit': 'Edit',
  'equipment.edit.done': 'Saved.',
  'equipment.edit.tag_fixed': 'The tag {tag} stays as it is.',
  'equipment.handover': 'Record handover',
  'equipment.handover.explain':
    'Records that the owner accepted this unit. It is recorded once and is not undone; any documents still missing are noted with it.',
  'equipment.handover.done': 'Handover recorded.',
  'equipment.handover.recorded': 'Handed over {when} by {who}.',
  'equipment.doc.none': 'No documents yet.',
  'equipment.doc.attach': 'Attach a document',
  'equipment.doc.kind': 'What it is',
  'equipment.doc.caption': 'Caption',
  'equipment.doc.file': 'File',
  'equipment.doc.done': 'Document attached.',
  'equipment.doc.download': 'Download',
  'equipment.doc.not_here': 'Not on this site computer yet',
  'equipment.manifest.open': 'Closeout manifest',
  'equipment.manifest.title': 'Closeout manifest',
  'equipment.manifest.explain':
    'For each equipment type, every unit and the handover documents it is still missing.',
  'equipment.manifest.required': 'Required of every unit: {kinds}.',
  'equipment.manifest.untyped': 'No type given',
  'equipment.manifest.summary': '{units} units, {complete} with every document, {handed} handed over',
  'equipment.unsent.explain':
    'This was sent and no answer came back, so it may already be saved. Sending it again is safe: it goes under the same key, so a save that landed is handed back rather than made twice. Discarding it lets you change it and send it as a new one.',
  'equipment.unsent.send_again': 'Send again',
  'equipment.unsent.discard': 'Discard and change it',
  'equipment.unsent.status': 'Status change to {status}, not answered.',
  'equipment.unsent.handover': 'Handover, not answered.',
  'equipment.unsent.attach': '{file} ({kind}), not answered.',

  'home.title': 'Overview',
  'home.subtitle': '{project} at {address}',
  'home.my_court': 'My actions',
  'home.card.court': 'My actions',
  'home.card.activity': 'Activity',
  'home.card.chart': 'Activity by day',
  'home.card.board': 'Leaderboard',
  'home.card.milestones': 'Next milestones',
  'home.card.photos': 'Recent photos',
  'home.card.forecast': "Today's forecast",
  'home.forecast': "Today's forecast",
  'home.customize.open': 'Customize',
  'home.customize.title': 'Customize your overview',
  'home.customize.main': 'Left column',
  'home.customize.side': 'Right column',
  'home.customize.up': 'Move {card} up',
  'home.customize.down': 'Move {card} down',
  'home.customize.kept': 'Your choice is kept in this browser and applies on every project you open here.',
  'home.customize.reset': 'Back to the default',
  'home.milestones.title': 'Next milestones',
  'home.milestones.all': 'Schedule',
  'home.milestones.partial':
    'Read the first {count} activities that finish from today; an earlier milestone may be among the rest. The schedule has them all.',
  'home.milestones.empty': 'No milestones are coming up in the current schedule.',
  'home.milestones.critical': 'Critical',
  'home.chart.title': 'Activity by day',
  'home.chart.other': 'Other',
  'home.chart.empty': 'No activity in the last 14 days.',
  'home.chart.empty_partial':
    'No activity in the whole days read, from {from}. Earlier days were not read, so they are not counted as quiet.',
  'home.chart.partial':
    'The activity read covers whole days only from {from}. Earlier days are shaded and not counted; they are not zero.',
  'home.chart.partial_none':
    'The activity read does not cover a whole day, so no day is counted. Open a module for its own records.',
  'home.chart.not_read': 'not read',
  'home.chart.grouped':
    'The three busiest modules have a line each; the others are added together as Other.',
  'home.chart.table': 'Show as a table',
  'home.chart.day': 'Day',
  'home.photos.title': 'Recent photos',
  'home.photos.all': 'All photos',
  'home.photos.empty': 'No photos yet.',
  'home.my_court.empty': 'Nothing is waiting on you.',
  'home.my_court.unreachable_one': 'One register could not be read; this list is incomplete.',
  'home.my_court.unreachable': '{count} registers could not be read; this list is incomplete.',
  'home.coming_soon.title': 'Preview',
  'home.coming_soon.body':
    'This tool is a preview. Its records are listed below, but its own screen is not finished yet, so you can look at them here and not yet work on them.',

  'help.open': 'Help for this screen',
  'engage.notifications.open': 'Notifications',
  'engage.notifications.title': 'Your notifications on this project',
  'engage.notifications.unread_only': 'Unread only',
  'engage.notifications.unread': 'Unread',
  'engage.notifications.mark_read': 'Mark as read',
  'engage.notifications.not_marked': 'That notification could not be marked read. It is not one of yours.',
  'engage.notifications.none': 'You have no notifications on this project.',
  'engage.notifications.none_unread': 'Nothing unread.',
  'engage.notifications.latest': 'Your latest {count} notifications. More may exist.',
  'help.title': 'Help',
  'help.empty': 'There is no help topic bound to this screen yet.',
  'help.fallback':
    'This page has not been translated yet, so it is shown in English.',
  'help.stale': 'This page may be out of date against the current English.',
  'help.read_more': 'Read more',
  'help.steps': 'Steps',
  'help.deep_link': 'Copy a link to this topic',
  'help.copied': 'Link copied.',
  'help.mark': 'Guidance',

  'engage.feed.title': 'Activity',
  'engage.feed.empty': 'Nothing has happened on this project yet.',
  'engage.feed.behind': 'Catching up…',
  'engage.feed.live': 'Live',
  'engage.feed.acting_as_backup': 'Acting as backup for {name}',
  'engage.feed.disconnected': 'Not live. Showing what was loaded.',
  'engage.feed.show_all': 'Show all {count}',
  'engage.feed.show_fewer': 'Show fewer',
  'engage.board.title': 'Leaderboard',
  'engage.board.window.week': 'This week',
  'engage.board.window.month': 'This month',
  'engage.board.empty': 'No points have been earned in this window.',
  'engage.board.you': 'You',
  'engage.board.shadow':
    'This board is in shadow mode: points are being counted but not published.',
  // The sentence is split so "here" can be the link that opens the rules. It no
  // longer says the rules are set per project: nothing in scoring reads a
  // project's stored weights, caps or penalties, so that was not true.
  'engage.board.rules_before': 'Points and board rules are shown ',
  'engage.board.rules_link': 'here',
  'engage.board.rules_after': '.',
  'engage.rules.title': 'Points and board rules',
  'engage.rules.points': 'How points are earned',
  'engage.rules.points_explain':
    'The same on every project. Points count once the work has stood for the days shown, and are taken back if it is reopened.',
  'engage.rules.action': 'Action',
  'engage.rules.points_column': 'Points',
  'engage.rules.settles': 'Counts after',
  'engage.rules.days': '{days} days',
  'engage.rules.coming': '{points} from {from}',
  'engage.rules.who': 'Who is on the board',
  'engage.rules.who_default': 'Everybody on this project earns points and is ranked.',
  'engage.rules.who_others': 'Every other role earns points and is ranked.',
  'engage.rules.ranks': 'earns points and is ranked',
  'engage.rules.earns_only': 'earns points but is not ranked',
  'engage.rules.neither': 'neither earns points nor is ranked',
  'engage.display.title': 'Leaderboard display',
  'engage.display.open': 'Show on a television',
  'engage.wall.title': 'Wall screen',
  'engage.wall.no_key': 'This screen has no key. Open the link it was given.',
  'engage.wall.refused': 'This screen\'s link is no longer valid. Ask for a new one.',
  'engage.wall.unreachable': 'Cannot reach the site. Showing the last board.',
  'engage.wall.unreachable_first': 'Cannot reach the site. Trying again every minute.',
  'engage.wall.waiting': 'Loading the board.',
  'engage.wall.bad_link':
    'This screen\'s link is not complete or has been changed. Open the link it was given again.',
  'engage.wall_screens.title': 'Wall screens',
  'engage.wall_screens.explain':
    'What each project\'s wall screens show under each name, beside the rank and the score. A change shows on that project\'s walls at their next refresh, within a minute.',
  'engage.wall_screens.refused': 'Only a Super Admin or the Global Admin can change wall screens.',
  'engage.wall_screens.none': 'There are no projects yet.',
  'engage.wall_screens.col_project': 'Project',
  'engage.wall_screens.col_crew': 'Crew',
  'engage.wall_screens.col_company': 'Company',
  'engage.wall_screens.crew_on': 'Show the crew on {project}\'s walls',
  'engage.wall_screens.company_on': 'Show the company on {project}\'s walls',
  'engage.wall_screens.saved': 'Saved for {project}.',
  'engage.points.mine': 'Your points',

  'feedback.button': 'Report a problem',
  'feedback.title': 'Report a problem',
  'feedback.body.label': 'What happened?',
  'feedback.body.placeholder': 'What you were doing, and what the screen did instead.',
  'feedback.kind.label': 'Kind',
  'feedback.kind.bug': 'Something is broken',
  'feedback.kind.suggestion': 'A suggestion',
  'feedback.kind.question': 'A question',
  'feedback.kind.praise': 'Something worked well',
  'feedback.context.title': 'Sent with this report',
  'feedback.context.route': 'Screen',
  'feedback.context.project': 'Project',
  'feedback.context.person': 'You',
  'feedback.context.version': 'Version',
  'feedback.context.errors': 'Recent errors',
  'feedback.context.none': 'No errors recorded.',
  'feedback.submit': 'Send report',
  'feedback.sent': 'Thank you. Report {number} was filed.',
  'feedback.failed': 'The report could not be filed. It is still in the box; try again.',
  'feedback.mine.title': 'Your reports',
  'feedback.mine.explain': 'What you reported on this project with the button on every screen, and where each one got to.',
  'feedback.mine.open': 'Your reports',
  'feedback.board.title': 'Feedback board',
  'feedback.board.explain': 'Every report filed on this project: bugs, suggestions, questions and praise. Oldest first.',
  'feedback.board.open': 'Feedback board',
  'feedback.mine.page_full': 'These are your latest {count} reports, not all of them. Older ones are not shown here.',
  'feedback.board.page_full':
    'These are the oldest {count} reports that match, not all of them. Newer ones are not shown here; narrow the filters to see them.',
  'feedback.count': '{count} reports',
  'feedback.count_one': '{count} report',
  'feedback.column.reference': 'Reference',
  'feedback.column.what': 'What was reported',
  'feedback.column.reporter': 'Reported by',
  'feedback.column.assignee': 'Assigned to',
  'feedback.column.filed': 'Filed',
  'feedback.filter.unassigned': 'Nobody assigned',
  'feedback.status.label': 'Status',
  'feedback.status.new': 'New',
  'feedback.status.triaged': 'Confirmed',
  'feedback.status.in_progress': 'In progress',
  'feedback.status.fixed': 'Fixed',
  'feedback.status.verified': 'Fix confirmed',
  'feedback.status.closed': 'Closed',
  'feedback.status.wont_fix': "Won't fix",
  'feedback.status.duplicate': 'Duplicate',
  'feedback.severity.label': 'Severity',
  'feedback.severity.critical': 'Critical',
  'feedback.severity.major': 'Major',
  'feedback.severity.minor': 'Minor',
  'feedback.priority.label': 'Priority',
  'feedback.priority.urgent': 'Urgent',
  'feedback.priority.high': 'High',
  'feedback.priority.normal': 'Normal',
  'feedback.priority.low': 'Low',
  'feedback.detail.online': 'Had a network',
  'feedback.detail.yes': 'Yes',
  'feedback.detail.no': 'No',
  'feedback.detail.resolution': 'Last note',
  'feedback.detail.reopen_until': 'Can be reopened until',
  'feedback.moves.title': 'What you can do',
  'feedback.moves.note': 'Note (optional)',
  'feedback.move.confirm': 'Confirm',
  'feedback.move.assign': 'Assign',
  'feedback.move.start': 'Start',
  'feedback.move.fix': 'Mark fixed',
  'feedback.move.close': 'Close',
  'feedback.move.reject': "Won't fix",
  'feedback.move.verify': "It's fixed",
  'feedback.move.reopen': 'Reopen',
  'feedback.move.duplicate': 'Mark as duplicate',
  'feedback.duplicate.of': 'Duplicate of',
  'feedback.replies.title': 'Conversation',
  'feedback.replies.none': 'Nobody has replied.',
  'feedback.replies.write': 'Reply',
  'feedback.replies.send': 'Send reply',
  'feedback.replies.sent': 'Reply sent.',
  'feedback.unsettled.reply':
    'The answer to sending your reply did not arrive, so whether it was saved is not known here.',
  'feedback.unsettled.reply_hint':
    'Sending it again carries the same key, so a reply that did land is handed back rather than posted twice. Anything typed in the box since is kept and is not sent with it.',
  'feedback.unsettled.reply_retry': 'Send the same reply again',
  'feedback.unsettled.reply_check': 'Check the report',

  'deficiency.title': 'Punch list',
  'deficiency.discovery_phase.design': 'Design',
  'deficiency.discovery_phase.procurement': 'Procurement',
  'deficiency.discovery_phase.construction': 'Construction',
  'deficiency.discovery_phase.commissioning': 'Commissioning',
  'deficiency.discovery_phase.turnover': 'Turnover',
  'deficiency.discovery_phase.warranty': 'Warranty',
  'deficiency.discovery_source.qaqc_inspection': 'QAQC inspection',
  'deficiency.discovery_source.punch_walk': 'Punch walk',
  'deficiency.discovery_source.observation': 'Observation',
  'deficiency.discovery_source.rfi': 'RFI',
  'deficiency.discovery_source.clash_detection': 'Clash detection',
  'deficiency.discovery_source.commissioning': 'Commissioning',
  'deficiency.discovery_source.owner_walk': 'Owner walk',
  'deficiency.discovery_source.warranty_call': 'Warranty call',
  'deficiency.count': '{count} records',
  'deficiency.count_one': '1 record',
  'deficiency.new': 'Add punch item',
  'deficiency.column.number': 'Number',
  'deficiency.column.title': 'Title',
  'deficiency.column.state': 'Status',
  'deficiency.column.severity': 'Severity',
  'deficiency.column.location': 'Location',
  'deficiency.column.assignee': 'Action required by',
  'deficiency.column.due': 'Due date',
  'deficiency.filter.state': 'Status',
  'deficiency.filter.severity': 'Severity',
  'deficiency.filter.record_type': 'Type',
  'deficiency.filter.rework': 'Rework only',
  'deficiency.filter.blocking': 'Blocks energization',
  'deficiency.filter.cause': 'Cause code',
  'deficiency.state.draft': 'Draft',
  'deficiency.state.issued': 'Issued',
  'deficiency.state.disputed': 'Disputed',
  'deficiency.state.ready_for_verification': 'Ready for verification',
  'deficiency.state.verified': 'Verified',
  'deficiency.state.closed': 'Closed',
  'deficiency.state.void': 'Void',
  'deficiency.severity.critical': 'Critical',
  'deficiency.severity.major': 'Major',
  'deficiency.severity.minor': 'Minor',
  'deficiency.record_type.deficiency': 'Deficiency',
  'deficiency.record_type.ccd': 'Construction Change Directive',
  'deficiency.field.title': 'Title',
  'deficiency.field.description': 'Description',
  'deficiency.field.location': 'Location',
  'deficiency.field.division': 'CSI division',
  'deficiency.field.cause_code': 'Cause code',
  'deficiency.field.severity': 'Severity',
  'deficiency.field.discovery_phase': 'Discovered in',
  'deficiency.field.discovery_source': 'Found by',
  'deficiency.field.assignee_company': 'Responsible company',
  'deficiency.field.assignee_person': 'Responsible person',
  'deficiency.field.manager': 'Punch item manager',
  'deficiency.field.due_date': 'Due date',
  'deficiency.detail.history': 'History',
  'deficiency.detail.photos': 'Photos',
  'deficiency.detail.history.empty': 'Nothing has happened to this record yet.',
  'deficiency.detail.photos.empty': 'No photos on this record.',
  'photo.alt': 'Site photograph',
  'photo.loading': 'Loading\u2026',
  'photo.waiting':
    'Waiting for this photograph. The record reached this box before the picture did, so it is still on its way.',
  'photo.failed': 'This photograph could not be loaded.',
  'photo.no_media': 'Taken on a device, with no thumbnail on this box.',
  // Photos and media, the module's own screen. `photos.page.*` rather than
  // `photos.*`, which is the module's server catalogue (event summaries,
  // refusals) and must not be reworded by a screen label.
  'photos.page.title': 'Photos and media',
  'photos.page.explain':
    'Upload, organize, and find project photos. A photo can appear in more than one album.',
  'photos.page.all': 'All photos',
  'photos.page.albums': 'Albums',
  'photos.page.album.new': 'New album',
  'photos.page.album.name': 'Album name',
  'photos.page.album.create': 'Create album',
  'photos.page.count': '{count} photos',
  'photos.page.count_one': '1 photo',
  'photos.page.newest_only': 'Showing the newest {n}. Narrow the filters to see older photos.',
  'photos.page.none_yet': 'No project photos yet. Upload photos to get started.',
  'photos.page.none_match': 'No photos match these filters.',
  'photos.page.no_caption': 'No caption',
  'photos.page.open': 'Open photo: {caption}',
  'photos.page.no_preview': 'No preview for this file. Open it to download the original.',
  'photos.page.unreadable':
    'Stored file \u00b7 preview unavailable. This file could not be read as a picture. Download the original to check it.',
  'photos.page.thumbnail_withheld':
    'Stored photo \u00b7 thumbnail not shown. Its thumbnail was made before thumbnails recorded which picture they came from. An administrator can redo it from the original.',
  'photos.page.redo_thumbnail': 'Redo thumbnail',
  'photos.page.redo_thumbnail.working': 'Redoing thumbnail\u2026',
  'photos.page.redo_thumbnail.made': 'Thumbnail redone from the original.',
  'photos.page.redo_thumbnail.current':
    'The thumbnail already shows this original. Nothing was changed.',
  'photos.page.awaiting_upload':
    'Photo pending: the record is here and the picture is on its way from the device.',
  'photos.page.filter.caption': 'Search captions',
  'photos.page.filter.by': 'Taken by',
  'photos.page.filter.trade': 'Trade',
  'photos.page.filter.from': 'Taken on or after',
  'photos.page.filter.to': 'Taken on or before',
  'photos.page.filter.range_reversed': 'End date must be on or after start date.',
  'photos.page.filter.state': 'Upload status',
  'photos.page.state.stored': 'Uploaded',
  'photos.page.state.awaiting_upload': 'Waiting to upload',
  'photos.page.state.failed': 'Upload failed',
  'photos.page.upload.title': 'Add photos',
  'photos.page.upload.choose': 'Upload photos',
  'photos.page.upload.take': 'Take a photo',
  'photos.page.upload.progress': '{sent} of {total} sent.',
  'photos.page.upload.clear_sent': 'Clear {count} sent',
  'photos.page.upload.clear_sent_one': 'Clear 1 sent',
  'photos.page.upload.state.queued': 'Waiting to send',
  'photos.page.upload.state.sending': 'Sending\u2026',
  'photos.page.upload.state.sent': 'Sent',
  'photos.page.upload.state.too_large': 'Too large for this site (32 MB at most).',
  'photos.page.upload.state.not_sent':
    'Not sent. The photo is recorded; sending again will not make a second copy.',
  'photos.page.gps.label': 'Photo location on this job',
  'photos.page.gps.retain': 'Photo location is kept on this project.',
  'photos.page.gps.discard':
    'SiteFocus does not record photo location on this project. Original files are kept as uploaded and may still contain location data saved by the camera.',
  'photos.page.gps.option.retain': 'Keep location',
  'photos.page.gps.option.discard': 'Do not record location',
  'photos.page.filter.place': 'Location',
  'photos.page.place.none': 'No Location',
  'photos.page.place.position_only': 'Placed by the position in the photo',
  'photos.page.place.unlisted': 'A location no longer listed',
  'photos.page.location.label': 'Location for these photos',
  'photos.page.location.add': 'Add a new location',
  'photos.page.location.inside': 'Inside',
  'photos.page.location.top': 'Top level of the project',
  'photos.page.location.kind': 'Kind',
  'photos.page.location.kind.building': 'Building',
  'photos.page.location.kind.level': 'Level',
  'photos.page.location.kind.room': 'Room',
  'photos.page.location.kind.work_area': 'Work area',
  'photos.page.location.name': 'Name',
  'photos.page.location.segment': 'Short code',
  'photos.page.location.segment_hint': 'How it reads in a path, such as B, L3 or 312. No slashes.',
  'photos.page.location.create': 'Add location',
  'photos.page.location.asked': '{name} ({segment})',
  'photos.page.location.unsettled': '{location} may already have been added.',
  'photos.page.location.unsettled_hint':
    'The answer did not arrive, so it is not known whether the location was made. Sending it again is safe: it is the same location under the same key, and it is made at most once. The boxes stay open for the next location, which can be added once this one is settled.',
  'photos.page.location.unsettled_retry': 'Send the same location again',
  'photos.page.view.label': 'Show photos',
  'photos.page.view.days': 'By day',
  'photos.page.view.places': 'By location',
  'photos.page.view.map': 'Map',
  'photos.page.map.title': 'Map',
  'photos.page.map.region': 'Map of these photos',
  'photos.page.map.empty': 'None of these photos has a position to put on the map.',
  'photos.page.detail.title': 'Photo',
  'photos.page.detail.download': 'Download original',
  'photos.page.detail.download_failed': 'The original could not be downloaded from this box.',
  'photos.page.detail.caption': 'Caption',
  'photos.page.detail.caption_by_ai': 'This caption was drafted by AI and accepted unchanged.',
  'photos.page.detail.draft_hint':
    'AI draft. Nothing is saved until you save it; edit it first if it is not right.',
  'photos.page.detail.save_caption': 'Save caption',
  'photos.page.detail.draft': 'Draft a caption with AI',
  'photos.page.detail.drafting': 'Generating caption\u2026',
  'photos.page.detail.suggestion': 'AI caption suggestion',
  'photos.page.detail.suggestion_use': 'Use this caption',
  'photos.page.detail.suggestion_discard': 'Discard suggestion',
  'photos.page.detail.conflict': 'A newer caption was saved',
  'photos.page.detail.conflict_newer': 'The caption is now: \u201c{caption}\u201d.',
  'photos.page.detail.conflict_take': 'Use the newer caption',
  'photos.page.detail.conflict_keep': 'Replace it with mine',
  'photos.page.detail.taken': 'Taken',
  'photos.page.detail.by': 'Taken by',
  'photos.page.detail.uploaded': 'Uploaded',
  'photos.page.detail.trade': 'Trade',
  'photos.page.detail.location': 'Location',
  'photos.page.detail.size': 'File size',
  'photos.page.detail.gps': 'Coordinates',
  'photos.page.detail.gps_discard':
    'Not recorded on this project. The original file may still contain location data.',
  'photos.page.detail.gps_retain': 'None recorded by the device',
  'photos.page.detail.add_to_album': 'Add to album',
  'photos.page.detail.add': 'Add',
  'photos.page.detail.added': 'Added to the album.',
  'photos.page.detail.places': 'Where this photo appears',
  'photos.page.detail.no_places': 'Not attached to any record you can see.',
  'photos.page.detail.pinned': 'pinned on a sheet',
  'photos.page.size_kb': '{n} KB',
  'photos.page.size_mb': '{n} MB',
  'photos.page.target.dailylog': 'Daily log',
  'photos.page.target.deficiency': 'Deficiency',
  'photos.page.target.rfi': 'RFI',
  'photos.page.target.inspection': 'Inspection',
  'photos.page.target.equipment': 'Equipment',
  'photos.page.target.drawing': 'Drawing',
  'photos.page.target.spec': 'Specification',
  'photos.page.bin.title': 'Deleted photos',
  'photos.page.bin.empty': 'Nothing you can restore has been deleted.',
  'photos.page.bin.deleted_on': 'Deleted {when} by {who}. Restore it to put it back everywhere it was.',
  'photos.page.bin.restore': 'Restore photo',
  'photos.page.decision.delete_unresolved': 'Your request to delete this photo may not have arrived.',
  'photos.page.decision.restore_unresolved': 'Your request to restore this photo may not have arrived.',
  'photos.page.decision.send_again': 'Send it again',
  'photos.page.decision.discard': 'Discard',
  'photos.page.delete.open': 'Delete photo',
  'photos.page.delete.title': 'Move this photo to Deleted photos?',
  'photos.page.delete.which': 'Taken {when} by {who}.',
  'photos.page.delete.places': 'It appears on {count} records you can see.',
  'photos.page.delete.places_one': 'It appears on 1 record you can see.',
  'photos.page.delete.no_places': 'It is not attached to any record you can see.',
  'photos.page.delete.consequence':
    'It will be removed from the gallery and albums. Records that already reference it will still show it. You can restore it later.',
  'photos.page.delete.reason': 'Reason',
  'photos.page.delete.confirm': 'Move to Deleted photos',
  'deficiency.photo.kind.issue_photo': 'Issue photo',
  'deficiency.photo.kind.completion_photo': 'Completion photo',
  'deficiency.photo.kind.verification_photo': 'Verification photo',
  'deficiency.detail.photos.all': 'Open the project photo list',
  'deficiency.detail.respond': 'Record a response',
  'deficiency.detail.transition': 'Change the state',
  'deficiency.detail.move': 'Move to',
  'deficiency.detail.move_button': 'Move',
  'deficiency.detail.reason': 'Reason',
  'deficiency.detail.moved': 'Moved to {state}.',
  'deficiency.created': 'Raised {number}.',
  'deficiency.new.draft_only':
    'This raises a draft. A deficiency cannot be issued without a photo of what is wrong, so add one above to issue it now \u2014 or raise the draft and attach the photo later.',
  'deficiency.new.will_issue':
    'This will be issued, not drafted: the photo is the evidence the register needs.',
  'deficiency.field.photo': 'Evidence photo',
  'deficiency.photo.take': 'Take a photo',
  'deficiency.photo.choose': 'Choose a file',
  'deficiency.photo.remove': 'Remove',
  'deficiency.photo.hint':
    'A photo of the defect. Taken now on a phone or tablet, or chosen from this device.',
  'deficiency.photo.uploading': 'Sending the photo\u2026',
  'deficiency.photo.too_large':
    'That photo is too large. The limit is 32 MB \u2014 take it again at a smaller size.',
  'deficiency.photo.not_sent':
    'The photo did not go. The deficiency was not raised, so nothing is half-written \u2014 try again, or remove the photo and raise it as a draft.',

  // Change management. Procore's words where the trade uses them (change
  // event, PCO, change order, CCD); no budget, commitment or cost-code wording,
  // because the module has none of those (CM-CHG-01).
  'changes.title': 'Change management',
  'changes.explain':
    'Every change on this job, from the first note that something may change to the signed change order.',
  'changes.new': 'Create change event',
  'changes.count': '{count} change events',
  'changes.count_one': '1 change event',
  'changes.days': '{count} working days',
  'changes.days_one': '1 working day',
  'changes.filter.state': 'State',
  'changes.filter.mine': 'Waiting on me',
  'changes.column.number': 'Number',
  'changes.column.title': 'Title',
  'changes.column.state': 'State',
  'changes.column.origin': 'Source',
  'changes.column.reason': 'Reason',
  'changes.column.court': 'Ball in court',
  'changes.column.manager': 'Manager',
  'changes.column.raised': 'Raised',
  'changes.column.rom': 'Estimated cost',
  'changes.state.draft': 'Draft',
  'changes.state.open': 'Open',
  'changes.state.closed': 'Closed',
  'changes.state.void': 'Withdrawn',
  'changes.origin.direct': 'Direct request',
  'changes.origin.rfi': 'RFI',
  'changes.origin.deficiency': 'Deficiency',
  'changes.origin.daily_log': 'Daily log',
  'changes.origin.meeting_action': 'Meeting action',
  'changes.origin.drawing_revision': 'Drawing revision',
  'changes.reason.owner_request': 'Owner request',
  'changes.reason.design_development': 'Design development',
  'changes.reason.error_or_omission': 'Error or omission',
  'changes.reason.field_condition': 'Field condition',
  'changes.reason.code_or_authority': 'Code or authority requirement',
  'changes.reason.coordination': 'Coordination',
  'changes.reason.other': 'Other',
  'changes.impact.to_be_determined': 'To be determined',
  'changes.impact.none': 'No time impact',
  'changes.impact.known': 'Known',
  'changes.contract.title': 'Contract value',
  'changes.contract.original': 'Original contract',
  'changes.contract.not_recorded': 'Not recorded',
  'changes.contract.approved': 'Approved changes',
  'changes.contract.orders': '({count} change orders)',
  'changes.contract.orders_one': '(1 change order)',
  'changes.contract.revised': 'Revised contract',
  'changes.contract.days': 'Approved time',
  'changes.section.what': 'The change',
  'changes.section.scope': 'Scope',
  'changes.field.title': 'Title',
  'changes.field.description': 'Description',
  'changes.field.origin': 'Source',
  'changes.field.reason': 'Reason',
  'changes.field.manager': 'Change manager',
  'changes.field.division': 'Division',
  'changes.field.quantity': 'Quantity',
  'changes.field.unit': 'Unit',
  'changes.field.rom_cost': 'Estimated cost ({currency})',
  'changes.field.rom_revenue': 'Estimated revenue ({currency})',
  'changes.field.rom_revenue_short': 'Estimated revenue',
  'changes.field.void_reason': 'Why it is withdrawn',
  'changes.field.time_impact': 'Time impact',
  'changes.field.days': 'Working days',
  'changes.field.days_optional': 'Working days (if known)',
  'changes.field.reviewer': 'Reviewer',
  'changes.field.approver': 'Approver',
  'changes.field.due_on': 'Due',
  'changes.field.due_on_optional': 'Due (leave empty for the contract default)',
  'changes.field.decision_note': 'Note',
  'changes.field.amount': 'Amount',
  'changes.field.amount_in': 'Amount ({currency})',
  'changes.field.nte': 'Not to exceed',
  'changes.field.nte_in': 'Not to exceed ({currency})',
  'changes.field.responsible': 'Owes the change order',
  'changes.field.directed_on': 'Directed on',
  'changes.cost_fields_off':
    'Estimated cost and revenue are switched off for this project. An administrator can turn them on in the change settings.',
  'changes.save_draft': 'Save as draft',
  'changes.raise_and_open': 'Create and open',
  'changes.raise_and_open_help':
    'Opening a change event notifies its change manager and lets it be priced. A draft is not announced to anybody until it is opened.',
  'changes.no_description': 'No description.',
  'changes.court': 'Ball in court',
  'changes.court.empty': 'Nobody is holding this change right now.',
  'changes.open': 'Open it',
  // A title of only spaces, on any record in the chain (D1-13).
  'changes.error.blank_title': 'Enter a change title.',
  'changes.void': 'Withdraw',
  'changes.void.confirm': 'Withdraw this change event',
  'changes.pco.title': 'Potential change orders',
  'changes.pco.empty': 'No price has been put on this change yet.',
  'changes.pco.new': 'Create proposed change',
  'changes.pco.save': 'Save the price',
  'changes.pco.lines': 'Lines ({currency})',
  'changes.pco.add_line': 'Add a line',
  'changes.pco.total': 'Total',
  'changes.pco.line.description': 'Line',
  'changes.pco.line.description_n': 'Line {n}',
  'changes.pco.line.company': 'Company',
  'changes.pco.line.amount': 'Amount',
  'changes.pco.line.amount_n': 'Amount {n}',
  'changes.pco.submit': 'Send for review',
  'changes.pco.accept': 'Accept the price',
  'changes.pco.reject': 'Reject the price',
  'changes.pco.state.draft': 'Draft',
  'changes.pco.state.submitted': 'In review',
  'changes.pco.state.accepted': 'Accepted',
  'changes.pco.state.rejected': 'Rejected',
  'changes.pco.state.withdrawn': 'Withdrawn',
  'changes.ccd.title': 'Construction change directives',
  'changes.ccd.empty': 'No work has been directed ahead of a price.',
  'changes.ccd.new': 'Direct the work now',
  'changes.ccd.explain':
    'A directive sends the crew to work before the price is agreed. Name who owes the change order that settles it.',
  'changes.ccd.issue': 'Issue the directive',
  'changes.ccd.state.draft': 'Draft',
  'changes.ccd.state.issued': 'Issued',
  'changes.ccd.state.resolved': 'Resolved',
  'changes.ccd.state.void': 'Withdrawn',
  'changes.co.title': 'Change orders',
  'changes.co.empty': 'No change order has been drafted.',
  'changes.co.needs_source':
    'A change order is drafted from an accepted price or an issued directive. There is neither on this change yet.',
  // Sources exist, but a live order already carries each of them (D1-10).
  'changes.co.all_settled': 'Every accepted price already has a change-order draft.',
  'changes.co.new': 'Draft a change order',
  'changes.co.save': 'Save the change order',
  'changes.co.from': 'Settles',
  'changes.co.supersedes': 'Corrects approved order',
  'changes.co.submit': 'Send for approval',
  'changes.co.signed_name': 'Your name, as signed',
  'changes.co.approve_explain':
    'Approving signs the change order for the owner. An approved change order cannot be edited; a correction is a new order that supersedes it.',
  'changes.co.approve': 'Approve and sign',
  'changes.co.reject': 'Send it back',
  'changes.co.signed': 'Signed',
  'changes.co.rejected_reason': 'Sent back because',
  // Withdrawing an order nobody can decide, such as one sent to the wrong
  // approver (D1-09).
  'changes.co.void': 'Withdraw the order',
  'changes.co.void_reason': 'Why the order is withdrawn',
  'changes.co.void.confirm': 'Withdraw this change order',
  'changes.co.state.draft': 'Draft',
  'changes.co.state.submitted': 'Awaiting approval',
  'changes.co.state.approved': 'Approved',
  'changes.co.state.rejected': 'Sent back',
  'changes.co.state.void': 'Withdrawn',
  'changes.decision.approved': 'Approved',
  'changes.decision.rejected': 'Rejected',
  'changes.trail.title': 'Decisions',
  'changes.trail.record': 'Record',
  'changes.trail.decision': 'Decision',
  'changes.trail.who': 'By',
  'changes.trail.when': 'When',
  'changes.trail.figure': 'Figure decided',
  'changes.attachments.title': 'Supporting documents',
  'changes.attachments.empty': 'Nothing attached yet.',
  'changes.attachments.record': 'Attach to',
  'changes.attachments.label': 'Label',
  'changes.attachments.file': 'File',
  'changes.attachments.kb': '{count} KB',
  'changes.attachments.kb_one': '1 KB',
  'changes.links.title': 'Related records',
  'changes.links.source': 'Caused this change',
  'changes.links.affects': 'Affected by this change',
  'changes.links.absent': 'not on this computer',
  'changes.link.kind.rfi': 'RFI',
  'changes.link.kind.deficiency': 'Deficiency',
  'changes.link.kind.daily_log_entry': 'Daily log entry',
  'changes.link.kind.meeting_action': 'Meeting action',
  'changes.link.kind.drawing_revision': 'Drawing revision',
  'changes.link.kind.spec_section': 'Specification section',
  'changes.link.kind.submittal': 'Submittal',
  'changes.link.kind.schedule_activity': 'Schedule activity',
  'changes.settings.open': 'Change settings',
  'changes.settings.title': 'Change settings',
  'changes.settings.cost_fields': 'Enable estimated cost and revenue fields for change events',
  'changes.settings.cost_fields_help':
    'Off by default. While it is off, change events do not show or accept an estimated cost or revenue.',
  'changes.settings.currency': 'Currency (three letters, such as USD)',
  'changes.settings.original': 'Original contract value',
  'changes.settings.saved': 'The change settings are saved.',
  // Shown instead of the form to anybody the instance would refuse (D1-16).
  'changes.settings.read_only':
    'Only an administrator of change management, or somebody given the permission to set the contract value, can change these settings.',
  // A creation sent and never answered: whether the record exists is not
  // known here, so the sentence claims neither, and the retry is the same ask.
  'changes.event.unconfirmed':
    'The answer to raising this change did not arrive, so whether it was raised is not known here.',
  'changes.event.open_unconfirmed':
    'The change was raised, and the answer to opening it did not arrive, so whether it is open is not known here.',
  'changes.pco.unconfirmed':
    'The answer to saving this price did not arrive, so whether it was saved is not known here.',
  'changes.ccd.unconfirmed':
    'The answer to issuing this directive did not arrive, so whether it was issued is not known here.',
  'changes.co.unconfirmed':
    'The answer to saving this change order did not arrive, so whether it was saved is not known here.',
  'changes.unconfirmed_hint':
    'Sending it again sends exactly what was sent the first time, under the same key, so a record that was made is handed back rather than made twice. The boxes are locked until that is settled, because a changed value would not reach a record that already exists.',
  'changes.send_the_same_again': 'Send the same again',
  'changes.create_refused_hint':
    'Nothing was saved. What you typed is still here; correct it and save again.',
  'changes.co.kept': 'Kept as you typed it, not taken from what the order settles.',
  // Raised and not opened: the change exists, so what follows is about that
  // change, and raising another is a separate, deliberate step.
  'changes.event.open_refused': '{number} was raised. Opening it was refused, so it is not open.',
  'changes.event.not_opened': '{number} was raised and has not been opened yet.',
  'changes.event.open_not_known':
    '{number} was raised. Whether it was opened is not known here.',
  'changes.event.raised_hint':
    'Trying again asks to open this same change; nothing new is raised. The boxes stay locked because they describe a change that already exists.',
  'changes.event.open_again': 'Try opening it again',
  'changes.event.go_to_raised': 'Go to {number}',
  'changes.event.raise_another': 'Raise a different change',
  'changes.refused':
    'You do not have access to this any more, so it is not shown. If that has changed, try again.',

  'rfi.title': 'RFIs',
  'rfi.count': '{count} RFIs',
  'rfi.count_one': '1 RFI',
  'rfi.new': 'Create RFI',
  'rfi.column.number': 'Number',
  'rfi.column.subject': 'Subject',
  'rfi.column.state': 'Status',
  'rfi.column.court': 'Action required by',
  'rfi.column.due': 'Due date',
  'rfi.column.manager': 'RFI manager',
  'rfi.section.question': 'The question',
  'rfi.section.who': 'Who answers it, and by when',
  'rfi.column.raised': 'Raised',
  'rfi.revision': 'rev {n}',
  'rfi.explain': 'Questions to the design team about the drawings and specifications, who holds each one, and when the answer is due.',
  'rfi.filter.state': 'Status',
  'rfi.filter.mine': 'Requires my action',
  'rfi.state.draft': 'Draft',
  'rfi.state.open': 'Open',
  'rfi.state.answered': 'Answered',
  'rfi.state.closed_draft': 'Closing answer written',
  'rfi.state.closed': 'Closed',
  'rfi.state.reopened': 'Reopened',
  'rfi.field.subject': 'Subject',
  'rfi.field.question': 'Question',
  'rfi.field.manager': 'RFI manager',
  'rfi.field.assignee': 'Assignee (required to issue)',
  'rfi.field.company': 'Their company',
  'rfi.field.division': 'CSI division',
  'rfi.field.due_on': 'Response due date',
  'rfi.detail.court': 'Action required by',
  'rfi.detail.court.empty': 'Nobody — this RFI is not open.',
  'rfi.detail.question': 'Question',
  'rfi.detail.responses': 'Responses',
  'rfi.detail.responses.empty': 'No responses yet.',
  'rfi.detail.official': 'Official answer',
  'rfi.detail.issue': 'Issue',
  'rfi.detail.respond': 'Submit response',
  'rfi.detail.respond.label': 'Your response',
  'rfi.related.title': 'Related records',
  'rfi.related.none': 'No related records.',
  'rfi.related.incoming_one': 'Another RFI links to this one.',
  'rfi.related.incoming': '{count} other RFIs link to this one.',
  'rfi.related.pinned_kind': 'Pinned on drawing',
  'rfi.related.pinned_revision': 'Rev {revision}',
  'rfi.related.pinned_set': 'in {set}',
  'rfi.related.pinned_no_revision': 'placed before the sheet was issued',
  'rfi.related.open_drawing': 'Open drawing',
  'rfi.related.open_drawing_named': 'Open drawing {number}',
  'rfi.related.pins_failed': 'The drawings this RFI is pinned to could not be loaded.',
  'rfi.link.kind.rfi': 'RFI',
  'rfi.link.kind.submittal': 'Submittal',
  'rfi.link.kind.drawing_sheet': 'Drawing',
  'rfi.link.kind.drawing_revision': 'Drawing revision',
  'rfi.link.kind.spec_section': 'Spec section',
  'rfi.link.kind.equipment': 'Equipment',
  'rfi.link.kind.deficiency': 'Deficiency',
  'rfi.link.kind.correspondence': 'Correspondence',
  'rfi.link.kind.change_event': 'Change event',
  'rfi.link.kind.observation': 'Observation',
  'rfi.created': 'Drafted {number}.',
  'rfi.issued': 'Issued {number}.',
  'rfi.responded': 'Response submitted.',
  'rfi.save_draft': 'Save draft',
  // A draft can be finished on its own record rather than raised again, which
  // is all a draft saved without an assignee could do before.
  'rfi.detail.edit': 'Edit draft',
  'rfi.detail.edit.save': 'Save changes',
  'rfi.detail.edit.saved': 'Draft saved.',
  'rfi.detail.needs_assignee': 'This RFI needs an assignee before it can be issued.',
  'rfi.detail.add_assignee': 'Add assignee',
  // A write sent from this record whose outcome nothing has established: sent
  // again after its answer was lost and refused, or replaced by a newer one
  // while its answer was out. It may be on the record, so it is said rather
  // than dropped, and only the person sends it again.
  'rfi.detail.unresolved.edit':
    'Changes sent to this draft may or may not have been saved, and nothing since has said which. They set the subject to "{subject}".',
  'rfi.detail.unresolved.issue':
    'This RFI may or may not have been issued, and nothing since has said which.',
  'rfi.detail.unresolved.again': 'Send them again',
  'rfi.detail.unresolved.let_go': 'Let them go',
  'rfi.detail.unresolved.sent': 'The earlier changes were sent and answered.',
  // The date issuing sets when nobody names one, said before Issue rather than
  // found on the record afterwards. The date is the server's, so the sentence
  // and the record agree.
  'rfi.detail.due_preview':
    'Response due {date} if you issue today: {days} calendar days, rolled to the next working day.',
  'rfi.detail.due_preview.working': 'Response due {date} if you issue today: {days} working days.',

  // -- submittals -------------------------------------------------------------
  //
  // The module had no screen of its own and fell through to the generated
  // module page, so it had no strings either: a nineteen-column table named
  // after the database. Every label below is a word somebody on a site uses.
  'submittals.title': 'Submittals',
  'submittals.explain':
    'Product data, shop drawings and samples sent for review, where each one is in its review, and who holds it.',
  'submittals.count': '{count} submittals',
  'submittals.count_one': '1 submittal',
  // The register pages, so what the caption counts is what is on screen, not
  // what the project has. Said plainly, because "24 submittals" over a register
  // that has more is the kind of number somebody carries into a meeting.
  'submittals.showing_so_far': 'Showing the first {count}. There are more.',
  'submittals.show_more': 'Show more',
  'submittals.showing_all': 'Showing all {count}.',

  // The reverse of the link the register already draws: reading a section,
  // which packages are against it. Named for what it is rather than "linked
  // records", because the pin list beside it on the same screen is called that
  // and answers a different question.
  'submittals.citing.title': 'Submittals citing this section',
  // The sentence that keeps the two lists apart. A citation is a field on the
  // submittal naming this section; a pin is a point somebody placed on a page
  // of one revision. Saying so is cheaper than somebody acting on a mark-up
  // that was never made.
  'submittals.citing.note':
    'These name this section on the submittal itself. They are not pins placed on a page, and they are not tied to a revision.',
  'submittals.citing.none': 'No submittal cites this section.',
  // A refusal, which is not a failure and not an empty list. Its own sentence
  // because the instance was asked and declined: nothing went wrong, and what it
  // had answered with before is no longer this browser's to show. Says that the
  // section itself is untouched, so nobody reads a withdrawn list as lost work.
  'submittals.citing.refused':
    'This list is not being shown, because the submittal register would not answer for this section. Nothing about the section or its packages has changed.',
  // The register's own em dash for a package that has not been numbered yet.
  // Its state chip on the same row already says what it is.
  'submittals.citing.unnumbered': '—',
  // Counted rather than "showing all": this list is the whole answer for the
  // section, so the number is the number of packages against it.
  'submittals.citing.count': '{count} submittals cite this section.',
  'submittals.citing.count_one': 'One submittal cites this section.',
  'submittals.new': 'Create submittal',
  'submittals.by_section': 'By specification section',
  'submittals.unnumbered': 'Not yet numbered',
  // Revision 0 is never printed as "rev 0"; see the register. This is for 1 up.
  'submittals.revision_short': 'Rev {n}',
  'submittals.column.number': 'Number',
  'submittals.column.title': 'Title',
  'submittals.column.section': 'Specification section',
  'submittals.column.type': 'Type',
  'submittals.column.state': 'Status',
  'submittals.column.court': 'Action required by',
  'submittals.column.manager': 'Submittal manager',
  'submittals.column.required_on_site': 'Needed on site',
  'submittals.column.float': 'Review float',
  'submittals.filter.state': 'Status',
  'submittals.filter.mine': 'Requires my action',
  'submittals.filter.sort': 'Order by',
  'submittals.sort.number': 'Number',
  'submittals.sort.required_on_site': 'Date needed on site',
  'submittals.state.draft': 'Draft',
  'submittals.state.in_review': 'In review',
  'submittals.state.returned': 'Returned for revision',
  'submittals.state.closed': 'Closed',
  // The decision, which is a separate field from the state on purpose: "the
  // review finished" and "the review said yes" are different facts.
  'submittals.disposition.approved': 'Approved',
  'submittals.disposition.approved_as_noted': 'Approved as noted',
  'submittals.disposition.revise_and_resubmit': 'Revise and resubmit',
  'submittals.disposition.rejected': 'Rejected',
  'submittals.disposition.for_record': 'For record',
  'submittals.type.product_data': 'Product data',
  'submittals.type.shop_drawing': 'Shop drawing',
  'submittals.type.sample': 'Sample',
  'submittals.type.mock_up': 'Mock-up',
  'submittals.type.certificate': 'Certificate',
  'submittals.type.test_report': 'Test report',
  'submittals.type.om_manual': 'Operation and maintenance manual',
  'submittals.type.warranty': 'Warranty',
  'submittals.type.other': 'Other',
  // Working days, and negative means the date is already at risk. The plural
  // reads for both signs: "-3 working days of float" is the sentence somebody
  // says in the meeting.
  'submittals.float_days': '{count} working days',
  'submittals.float_days_one': '1 working day',
  // Not nought. No date to be needed on site by means nothing can be late yet.
  'submittals.float_unset': 'No date set',
  'submittals.lead_days': '{count} days',
  'submittals.lead_days_one': '1 day',
  'submittals.pages': '{count} pages',
  'submittals.pages_one': '1 page',
  'submittals.bytes': '{n} B',

  'rfi.routes.title': 'RFI routes',
  'rfi.routes.sub':
    'Who takes each question, in order, and what each step is for. A route applies to an RFI when it is raised, by choice or as the project default.',
  'rfi.routes.withheld': 'This project\'s RFI routes are no longer shown to you.',
  'rfi.routes.gone_choice': 'Route no longer offered',
  'rfi.routes.gone':
    'The route you chose is no longer offered to you, so nothing is sent until you choose again. Everything else you typed is kept.',
  'rfi.routes.none': 'This project has no saved RFI routes. New RFIs take the assignees you name, or the MEP-first route.',
  'rfi.routes.default': 'default',
  'rfi.routes.new': 'New route',
  'rfi.routes.make_default': 'Make this the default route',
  'rfi.routes.start_from': 'Start a new route from this one',
  'rfi.routes.retire': 'Retire this route',
  'rfi.routes.unchanging':
    'A saved route does not change. RFIs already on it finish the route they started.',
  'rfi.routes.not_for_mep':
    'It does not start with the MEP Coordinator, so MEP work does not take it.',
  'rfi.routes.unconfirmed':
    'The route may not have been saved; the connection dropped before the answer came back.',
  'rfi.routes.unconfirmed_named':
    'The route {name} may not have been saved; the connection dropped before the answer came back.',
  'rfi.routes.try_again': 'Send it again',
  'rfi.routes.name': 'Route name',
  'rfi.routes.step_n': 'Step {n}',
  'rfi.routes.purpose_label': 'What this step is for',
  'rfi.routes.purpose.technical_answer': 'Technical response',
  'rfi.routes.purpose.coordination': 'Coordination review',
  'rfi.routes.purpose.technical_confirmation': 'Technical confirmation',
  'rfi.routes.purpose.finalization': 'Manager finalization',
  'rfi.routes.purpose.signature': 'Signature',
  'rfi.routes.action.respond': 'Respond',
  'rfi.routes.action.review': 'Review',
  'rfi.routes.action.approve': 'Approve',
  'rfi.routes.holder_kind': 'Held by',
  'rfi.routes.holder': 'Who holds the step',
  'rfi.routes.kind.role': 'Role',
  'rfi.routes.kind.person': 'Person',
  'rfi.routes.rule_label': 'Who must answer',
  'rfi.routes.rule.all': 'Everyone on it',
  'rfi.routes.rule.any': 'Any one of them',
  'rfi.routes.days_label': 'Working days',
  'rfi.routes.days': '{days} working days',
  'rfi.routes.add_step': 'Add a step',
  'rfi.routes.remove_step': 'Remove this step',
  'rfi.routes.save': 'Save the route',
  'rfi.routes.field': 'Route',
  'rfi.routes.standard': 'Standard route',
  'rfi.routes.project_default': 'Project default ({name})',
  'rfi.routes.instead_of_assignee': 'The route decides who answers, so no assignee is named.',
  'submittals.routes.title': 'Review routes',
  'submittals.routes.sub':
    'Saved review routes for this project: stages left to right, the review groups in each, and the result each stage reaches.',
  'submittals.routes.withheld': 'This project\'s routes are no longer shown to you.',
  'submittals.routes.gone_choice': 'Route no longer offered',
  'submittals.routes.parallel':
    'Another edit was published with the same number while two site computers could not reach each other. Both are kept, and each submittal stays on the one it was put on; new submittals take this one.',
  'submittals.routes.gone':
    'The route you chose is no longer offered to you, so nothing is sent until you choose again. Everything else you typed is kept.',
  'submittals.routes.none': 'This project has no saved routes. New submittals take the standard route.',
  'submittals.routes.default': 'default',
  'submittals.routes.new': 'New route',
  'submittals.routes.edit': 'Edit',
  'submittals.routes.make_default': 'Make this the default route',
  'submittals.routes.retire': 'Retire this route',
  'submittals.routes.version': 'Version {version}. Submittals already on this route keep the version they started on.',
  'submittals.routes.next_version':
    'Saving publishes version {version}. Submittals already on this route keep theirs.',
  'submittals.routes.not_for_mep':
    'It does not start with the MEP Coordinator, so MEP work does not take it.',
  'submittals.routes.unconfirmed':
    'The save did not come back, so it is not known here whether it was taken. Sending it again is safe.',
  'submittals.routes.unconfirmed_named':
    'The save of {name} did not come back, so it is not known here whether it was taken. Sending it again is safe.',
  'submittals.routes.try_again': 'Send it again',
  'submittals.routes.name': 'Route name',
  'submittals.routes.stage_n': 'Stage {n}',
  'submittals.routes.stage_name': 'Stage name',
  'submittals.routes.group_name': 'Review group name',
  'submittals.routes.result': 'Result',
  'submittals.routes.rule_label': 'Who must answer',
  'submittals.routes.rule.all': 'Everyone required',
  'submittals.routes.rule.any': 'Any one of them',
  'submittals.routes.days_label': 'Working days',
  'submittals.routes.days': '{days} working days',
  'submittals.routes.informational': 'For information only',
  'submittals.routes.kind.role': 'Role',
  'submittals.routes.kind.team': 'Team',
  'submittals.routes.kind.person': 'Person',
  'submittals.routes.seat_kind': 'Seat type',
  'submittals.routes.seat_holder': 'Who holds the seat',
  'submittals.routes.required': 'Required',
  'submittals.routes.add_seat': 'Add a seat',
  'submittals.routes.remove_seat': 'Remove this seat',
  'submittals.routes.add_group': 'Add a review group',
  'submittals.routes.remove_group': 'Remove this group',
  'submittals.routes.add_stage': 'Add a stage',
  'submittals.routes.remove_stage': 'Remove this stage',
  'submittals.routes.save': 'Save the route',
  'submittals.routes.publish': 'Publish the new version',
  'submittals.routes.field': 'Review route',
  'submittals.routes.standard': 'Standard route',
  'submittals.routes.project_default': 'Project default ({name})',
  'submittals.register.title': 'Submittal register',
  'submittals.register.sub':
    'Grouped by specification section, with the review float on every line \u2014 what is at risk, rather than what exists.',
  'submittals.register.candidates': '{count} proposed register items',
  'submittals.register.candidates_one': '1 proposed register item',
  'submittals.register.candidates_note':
    'are waiting for somebody to accept or reject them. Reviewing them is not in this build yet; the count is here so the backlog is visible.',

  'submittals.field.title': 'Title',
  'submittals.field.manager': 'Submittal manager',
  'submittals.field.type': 'Type',
  'submittals.field.company': 'Responsible contractor',
  'submittals.field.section': 'Specification section',
  'submittals.field.division': 'CSI division',
  'submittals.field.required_on_site': 'Needed on site by',
  'submittals.field.lead_time': 'Lead time in days',
  'submittals.field.fabrication_start': 'Fabrication starts',
  'submittals.field.delivery': 'Delivery',
  'submittals.field.private': 'Private',
  'submittals.field.response': 'Your decision',
  'submittals.field.comment': 'Comments',
  'submittals.field.reason': 'Why',
  'submittals.field.hand_to': 'Hand it to',
  'submittals.field.revision_reason': 'Why this cycle ended',
  'submittals.field.caption': 'What this file is',
  'submittals.field.file': 'Add a file',
  // An action dispatched on a record the reader has since left. Named where
  // they are now, because the alternative -- holding it silently in a store
  // they cannot see -- looks exactly like having thrown it away.
  'app.unsettled_elsewhere': '{count} actions on other records have not been settled.',
  'app.unsettled_elsewhere_one': 'An action on another record has not been settled.',
  'app.unsettled_elsewhere_item': '{action} on {number}',
  'app.unsettled_elsewhere_withheld': 'An action on a record you can no longer see is kept, and is offered again once you can.',
  'app.unsettled_action.dailylog-entry': 'Adding a daily log entry',
  'app.unsettled_action.photo-location': 'Adding a location',
  'app.unsettled_action.submittal-create': 'Raising a submittal',
  'app.unsettled_action.submittal-submit': 'Sending a draft for review',
  'app.unsettled_action.submittal-revision': 'Opening a revision',
  'app.unsettled_action.submittal-procurement': 'Saving the procurement dates',
  'app.unsettled_action.submittal-respond': 'Sending a review response',
  'app.unsettled_action.submittal-reassign': 'Handing a step on',
  'app.unsettled_action.submittal-comment': 'Posting a comment',
  'app.unsettled_action.submittal-upload': 'Attaching a file',
  'app.unsettled_action.feedback-reply': 'Replying on a feedback report',
  'app.unsettled_action.drawing-series': 'Naming a drawing set',
  'app.unsettled_action.drawing-filing': 'Filing an upload under a drawing set',
  'app.unsettled_action.system-computer-create': 'Adding a machine to the system drawing',
  'app.unsettled_action.system-computer-edit': 'Saving a machine on the system drawing',
  'app.unsettled_action.system-computer-retire': 'Retiring a machine from the system drawing',
  'app.unsettled_action.field-day': 'Marking a day on the field look ahead',
  'app.unsettled_action.field-task': 'Changing a task on the field look ahead',
  'app.unsettled_action.field-task-create': 'Adding a task to the field look ahead',
  'app.unsettled_action.field-section-create': 'Adding a section to the field look ahead',
  'app.unsettled_action.field-site-note': 'Writing a whole-site note on the field look ahead',
  'submittals.field.distribution_list': 'Distribution list',
  'submittals.field.review_due': 'Review due',
  'submittals.more_fields': 'More fields',
  'submittals.fewer_fields': 'Fewer fields',
  'submittals.save_draft': 'Save as draft',
  'submittals.submit_for_review': 'Submit for review',
  'submittals.no_sections':
    'This project has no specification sections to choose from. A submittal does not need one.',
  'submittals.division_hint':
    'Left blank, the review route is built from the specification section\u2019s division.',
  'submittals.private_hint':
    'A private submittal is visible to its manager, its participants and whoever is on its distribution. Nobody else on the job can open it or its files.',
  'submittals.revision_reason_hint':
    'Stored against the cycle that is closing, because that is the cycle it explains.',

  // The record's tabs, and the two banners above its name.
  'submittals.tab.general': 'General',
  'submittals.tab.related': 'Related items',
  'submittals.tab.history': 'Change history',
  'submittals.banner.reviewed': 'Review workflow complete',
  'submittals.banner.reviewed.detail': "Every step of this revision's review has been answered.",
  'submittals.banner.distributed': 'Distributed',
  'submittals.banner.distributed.on': 'First issued to its distribution on {when}',
  'submittals.detail.status': 'Status',
  'submittals.detail.workflow': 'Review workflow',
  'submittals.detail.workflow.empty':
    'No review steps. The project has named nobody for this scope, so there is nothing to submit to yet.',
  'submittals.detail.court': 'Action required by',
  'submittals.detail.court.empty': 'Nobody \u2014 this submittal is not in review.',
  'submittals.detail.procurement': 'Procurement',
  'submittals.detail.files': 'Files',
  'submittals.detail.files.empty': 'Nothing attached yet.',
  'submittals.detail.history': 'Revision history',
  'submittals.detail.comments': 'General comments',
  // Said under the heading, because the difference is what the card is for: a
  // reviewer's comment is inside the step it answered, and these answer none.
  'submittals.comments.not_a_review':
    'Kept apart from the review. A comment here is not a review response: it approves nothing and does not change the workflow, the ball in court or any date.',
  'submittals.comments.none': 'Nobody has commented yet.',
  'submittals.comments.add': 'Add a comment',
  'submittals.comments.post': 'Post comment',
  'submittals.detail.history.empty': 'This submittal has had no review cycles yet.',
  'submittals.detail.distribution': 'Distribution',
  'submittals.detail.links': 'Drawings and specifications',
  'submittals.links.none': 'This submittal is not pinned to any drawing or specification section.',
  'submittals.links.not_available':
    'You do not have the markup tool on this project, so the links cannot be read here.',
  'submittals.links.unreadable': 'A document you cannot open from here.',
  'submittals.links.elsewhere': 'A document this application has no screen for.',
  'submittals.links.kind.sheet': 'Drawing',
  'submittals.links.kind.section': 'Specification section',
  'submittals.links.linked_on': 'Linked on {revision}',
  'submittals.links.linked_on_a_revision': 'Linked on the revision recorded',
  'submittals.links.no_revision': 'No revision recorded',
  'submittals.links.superseded': 'Since reissued',
  'submittals.detail.respond': 'Respond',
  'submittals.detail.send_response': 'Send',
  'submittals.detail.reassign': 'Hand this step on',
  'submittals.detail.name_reviewer': 'Name a reviewer',
  'submittals.detail.download': 'Download',
  'submittals.detail.distribute': 'Add to the distribution',
  'submittals.edit_procurement': 'Change the dates',
  'submittals.new_revision': 'New revision',
  'submittals.open_revision': 'Open revision',

  'submittals.step.heading': 'Step {order} \u2014 {name}',
  'submittals.step.default_name': 'Review',
  'submittals.step.open': 'Open now',
  'submittals.step.closed': 'Done',
  'submittals.step.waiting': 'Not started',
  'submittals.step.due': 'due {on}',
  'submittals.step.completed': 'finished {at}',
  // A step whose seat the project has not filled is waiting on a role, not on
  // nobody. Saying which role is the difference between the two.
  'submittals.step.unfilled_role': 'waiting on a {role} this project has not named',
  'submittals.semantics.all': 'Every required reviewer must respond',
  'submittals.semantics.any': 'The first response from a required reviewer completes this step',
  'submittals.participant.optional': 'optional',
  'submittals.participant.awaited': 'Not yet',
  'submittals.participant.handed_on': 'handed on',
  'submittals.participant.never_answered': 'Never answered',
  'submittals.returns_the_cycle':
    'This ends the review cycle wherever it has got to. The remaining steps close and the package goes back to whoever raised it.',
  'submittals.optional_does_not_carry':
    'Somebody optional on this step is not waited on: their answer is recorded and does not move the review. Revise and resubmit sends the package back whoever sends it.',
  // A step with nobody required on it is not the ordinary case, and the two
  // response rules do opposite things there, so each says its own thing. Under
  // "every required reviewer", a step requiring none is already satisfied and
  // the first answer closes it. Under "the first required reviewer", the count
  // is over required people and there are none, so no answer ever completes it.
  'submittals.no_required_reviewer.all':
    'Nobody on this step is being waited on, so the first answer finishes it, whatever it says.',
  'submittals.no_required_reviewer.any':
    'Nobody on this step is being waited on, and this step only finishes on a required reviewer answering, so no answer here moves it on. Revise and resubmit still sends the package back.',

  'submittals.revision.first': 'First issue',
  'submittals.revision.current': 'the cycle running now',
  // A draft's files are on the record's first cycle before anybody has
  // reviewed anything, so they are not labelled as a cycle until it starts.
  'submittals.revision.draft': 'Draft attachments',
  'submittals.revision.undecided': 'No decision yet',
  'submittals.revision.opened': 'Opened',
  'submittals.revision.closed': 'Closed',
  'submittals.revision.why_it_ended': 'Why it ended',
  'submittals.revision.no_steps': 'No review steps were recorded for this cycle.',
  'submittals.revision.nobody_on_step': 'Nobody was on this step.',

  'submittals.upload.progress': '{done} of {total} sent \u2014 {percent}%',
  'submittals.upload.stalled':
    'Nothing has arrived for a while. The estimate is being withheld rather than allowed to grow.',
  // "Choose the same file again" was advice for something the browser will not
  // do: picking the same file leaves the input's value unchanged, so `onChange`
  // never fires. The panel keeps the file and offers the button below instead.
  'submittals.upload.resumable':
    'The transfer is still open. Sending it again carries on from where it stopped rather than starting over.',
  'submittals.upload.retry': 'Send {name} again',
  // The other half of the same failure, and one sentence for four different
  // refusals -- expired, abandoned, unknown, and a transfer opened for another
  // file -- so it says only what is true of all four: this one cannot be carried
  // on. It used to say the part already sent "has been cleared away", which is
  // what an expiry does and is not established by a transfer the box has never
  // heard of or one opened for a different file, where nothing was sent at all.
  // The refusal's own sentence, printed above this one, is where the particular
  // reason belongs.
  'submittals.upload.gone':
    'This transfer cannot be resumed. The file is still here, and sending it again starts a new transfer from the beginning.',
  'submittals.upload.restart': 'Send {name} from the start',
  // No instruction to choose the file again: the panel keeps the file and the
  // button below does it, and picking the same file in the chooser does nothing
  // at all -- which is the advice this whole pair of sentences replaced.
  'submittals.upload.another_file':
    'That transfer was opened for a different file, so nothing has been sent.',
  'submittals.upload.not_advancing':
    'This box stopped taking the file and is not saying why. Nothing further has been sent. The transfer is still open, so sending it again carries on from where it stopped.',
  'submittals.file.not_here': 'Not on this box yet',
  'submittals.markup.open': 'Mark up',
  'submittals.markup.view': 'View markup',
  'submittals.markup.back': 'Back to the submittal',
  'submittals.markup.file': '{name}, {revision}',
  'submittals.markup.missing': 'That file is not on this submittal.',
  'submittals.markup.no_markup_access':
    'You can open this submittal but not its markup. Download the file from the submittal instead.',
  'submittals.markup.not_drawable':
    'This file is not a PDF, so it cannot be marked up here. Download it from the submittal instead.',
  'submittals.markup.page': 'Page {page} of {count}',
  'submittals.markup.previous': 'Previous page',
  'submittals.markup.next': 'Next page',

  'submittals.distribution.not_a_review':
    'Being on the distribution is not being a reviewer. Nobody here can approve anything, and no reply from them is an approval.',
  'submittals.distribution.no_lists':
    'There is no distribution list made for this job. An administrator makes them in the directory.',
  'submittals.distribution.members': '{count} people on this list',
  'submittals.distribution.members_one': '1 person on this list',
  // Two acts, two buttons, each saying how many people it is about. One button
  // that meant "these three" or "all twenty" depending on a field nobody could
  // see is how twenty people ended up on a private record.
  'submittals.distribution.add_chosen': 'Add the {count} people chosen',
  'submittals.distribution.add_chosen_one': 'Add the 1 person chosen',
  //: Shown while the button is disabled, so it names the act without claiming
  //: a count that is not there yet.
  'submittals.distribution.add_chosen_none': 'Add the people chosen',
  'submittals.distribution.add_whole_list': 'Add the whole list ({count} people)',
  'submittals.distribution.add_whole_list_one': 'Add the whole list (1 person)',
  'submittals.distribution.added': 'Added {count} people.',
  'submittals.distribution.added_one': 'Added 1 person.',
  // Still about the action, and still narrow. The route answers with the people
  // it added, and it adds nobody both when they were all there already and when
  // none of them was eligible to be distributed to at all. The list below now
  // tells those two apart -- the four names are there or they are not -- so the
  // sentence does not have to guess, and it still does not.
  'submittals.distribution.none_added': 'No recipients were added.',

  // Who the record has already gone to.
  'submittals.distribution.current': 'Already issued to',
  'submittals.distribution.current_count': '{count} recipients',
  'submittals.distribution.current_count_one': '1 recipient',
  'submittals.distribution.nobody_yet': 'This has not been issued to anybody yet.',
  // The provenance on a row, in a person's words: which list they came off and
  // who put them on the record.
  'submittals.distribution.from_list': 'From {name}',
  'submittals.distribution.no_list': 'Added directly',
  // The record stored a list and its name will not resolve -- it has been
  // deleted, or it belongs to another customer. Either way the provenance is
  // unavailable rather than absent, and "Added directly" would be inventing a
  // fact the record does not hold.
  'submittals.distribution.list_unavailable': 'Source list unavailable',
  // A recipient this customer's directory cannot answer for. The row is the
  // record's own and stands; the name is the directory's and there is none to
  // show here.
  'submittals.distribution.unknown_contact': 'A recipient outside this customer\u2019s directory',
  // The list below is the last answer the record gave, and the read that would
  // have refreshed it failed. Said rather than left to look current: a press
  // that succeeded beside a stale list is how "Added 1 person" ends up next to
  // a list that does not have them on it.
  'submittals.distribution.read_stale':
    'This list is the last answer from the record and could not be refreshed just now, so it may be out of date.',
  'submittals.distribution.added_by': 'Added by {who}',
  // The directory has since retired them. The row on the record stands, because
  // taking somebody out of the directory does not un-issue what they were sent;
  // this is the other half of that, and whoever reads a distribution needs both.
  'submittals.distribution.retired': 'No longer in the directory',

  // The partial success. Named rather than shown as a bare failure, because
  // "that did not work" beside a form full of text reads as "nothing was
  // saved", and something was: the record exists, with the title it was created
  // under, and the boxes on screen no longer describe it.
  //
  // What it says about the *submission* is deliberately narrow. A submit whose
  // answer never arrived may have been committed on the instance, so "it is
  // still a draft and has no number yet" is a claim this browser cannot make --
  // it would be read as "nothing to undo" by whoever reads it next. Sending
  // again is safe whichever way it went, because the submit carries the same
  // idempotency key and a submission that landed is handed back rather than
  // repeated, and that is what settles the question.
  'submittals.saved_not_sent':
    'The submittal was saved as a draft titled \u201c{title}\u201d. The answer to sending it for review did not arrive, so whether it went is not known here.',
  'submittals.saved_not_sent_hint':
    'Sending it again is safe: it carries the same key, so a submission that did land is handed back rather than made twice. Opening the draft shows where it actually stands. The boxes above are locked because that draft is already saved and editing them here would not change it. Raising a separate submittal does not drop this one: the unanswered submission stays on the draft\u2019s own screen and on the line of held work at the top.',
  // And the other outcome of that same send, which is not an uncertain one:
  // the instance declined it and said why, and its session rolled back on the
  // way out -- so nothing went, the draft is exactly as it was, and pressing
  // again is a send rather than a replay. The card stays because the draft is
  // real and still unsent; only what it claims changes.
  'submittals.saved_not_sent_refused':
    'The submittal was saved as a draft titled \u201c{title}\u201d. Sending it for review was declined, so it has not gone.',
  'submittals.saved_not_sent_refused_hint':
    'Nothing about the draft changed. Put right what the message above asks for and send it again, or open the draft to work on it. The boxes above are locked because that draft is already saved and editing them here would not change it.',
  'submittals.send_the_saved_draft': 'Send it for review again',
  'submittals.open_the_saved_draft': 'Open the saved draft',
  'submittals.raise_a_separate_one': 'Raise a separate submittal',

  // The create whose answer never arrived, which is the earlier and worse of
  // the two uncertainties: there is no record id to offer, and the instance may
  // hold a submittal this screen has never seen. So the wording claims nothing
  // in either direction, the boxes stay locked, and the retry replays the
  // payload that was actually sent rather than whatever is on screen now.
  'submittals.create_unconfirmed':
    'The answer to saving this submittal did not arrive, so whether it was saved is not known here.',
  'submittals.create_unconfirmed_hint':
    'Trying again sends exactly what was sent the first time, under the same key, so a submittal that did save is handed back rather than made twice. The boxes are locked until that is settled, because a changed value would not reach a record that already exists. Raising a separate submittal waits for that answer too: a register shows what is there, not whether this ask is what put it there.',
  'submittals.create_refused_hint':
    'Nothing was saved. What you typed is still here; correct it and save again.',
  'submittals.try_the_save_again': 'Try saving it again',
  'submittals.open_the_submittal_list': 'Open the submittal list',

  // A write that was sent and never answered. One pair per action, because the
  // sentence has to name the thing whose outcome is unknown -- "that did not
  // work" is both wrong (it may well have worked) and useless (which of the
  // four things on this screen?). Each hint says the same three facts in the
  // action's own words: sending it again is safe because the key goes with it,
  // the boxes are shut because a changed value would not reach a record the
  // instance already holds, and the way to make a different decision is to
  // settle this one first and then act on the record as it stands.
  'submittals.unsettled.response':
    'The answer to sending your review response did not arrive, so whether it was recorded is not known here.',
  'submittals.unsettled.response_hint':
    'Sending it again carries the same key, so a response that did land is handed back rather than recorded twice. The decision and the comment are locked until that is settled, because changing them here would not change a response the instance already has. To answer differently, settle this one first: the step will offer whatever it then accepts. Reading the record back shows it as it stands, which is not the same as knowing this attempt is what put it there.',
  'submittals.unsettled.response_retry': 'Send the same response again',
  'submittals.unsettled.response_check': 'Check what the record says',

  'submittals.unsettled.comment':
    'The answer to posting your comment did not arrive, so whether it was saved is not known here.',
  'submittals.unsettled.comment_hint':
    'Sending it again carries the same key, so a comment that did land is handed back rather than posted twice. The box is locked until that is settled, because changing the words here would not change a comment the instance already has.',
  'submittals.unsettled.comment_retry': 'Send the same comment again',
  'submittals.unsettled.comment_check': 'Check the comments',

  'submittals.unsettled.hand_off':
    'The answer to handing this step on did not arrive, so whether it was recorded is not known here.',
  'submittals.unsettled.hand_off_hint':
    'Sending it again carries the same key, so a hand-off that did land is handed back rather than made twice. Who it goes to and why are locked until that is settled. To hand it to somebody else, settle this one first: the seat may already have moved.',
  'submittals.unsettled.hand_off_retry': 'Hand it on again',
  'submittals.unsettled.hand_off_check': 'Check what the record says',

  'submittals.hand_off.recorded': 'The hand-off was recorded.',
  'submittals.hand_off.recorded_hint': 'It went to {to} at {at}.',
  'submittals.read_has_ended':
    'This record is no longer yours to open. What is below is the copy from before the hand-off, so it will not refresh and the actions on it have been withdrawn.',
  'submittals.read_has_ended_without_copy':
    'This record is no longer yours to open, and there is no copy of it here to show. What happened to it is above, and the actions on it have been withdrawn.',
  'submittals.upload.left_open':
    '{name} ({size}) was still being sent when this record closed to you.',
  'submittals.upload.not_finished':
    '{name} ({size}) was stopped before it was finished, so none of it is on the record.',
  'submittals.upload.stored_away': '{name} ({size}) was stored on the record before it closed to you.',
  'submittals.upload.never_sent':
    '{name} ({size}) was never taken, and nothing of it is on the record.',
  'submittals.upload.left_open_hint':
    'It was not cancelled. Whether the file reached the record cannot be settled from here, because sending the rest of it would be writing to a record that is no longer yours. Ask somebody who can open the record to check before it is sent again.',
  'submittals.detail.files.not_readable':
    'The files on this record are no longer yours to see.',

  'submittals.unsettled.create':
    'The answer to raising this submittal did not arrive, so whether it was raised is not known here.',
  'submittals.unsettled.create_hint':
    'Sending again carries the same key, so a submittal that was raised is handed back rather than raised twice. The boxes are locked until that is settled, because the instance answers a key it already has with the record it already made and would apply none of a changed field.',
  'submittals.unsettled.create_retry': 'Raise it again',
  'submittals.unsettled.create_check': 'Check what the register says',

  'submittals.unsettled.submit':
    'The answer to sending this draft for review did not arrive, so whether it was sent is not known here.',
  'submittals.unsettled.submit_hint':
    'Sending again carries the same key, so a draft that did go is handed back rather than started through review twice. Sending a draft is what opens the review cycle and puts it in somebody else\u2019s court, so it is not something to press twice on a guess.',
  'submittals.unsettled.submit_retry': 'Send it for review again',
  'submittals.unsettled.submit_check': 'Check what the record says',

  'submittals.unsettled.revision':
    'The answer to opening a revision did not arrive, so whether it was opened is not known here.',
  'submittals.unsettled.revision_hint':
    'Trying again carries the same key, so a revision that did open is handed back rather than opened twice. The reason is locked until that is settled, because it belongs to the cycle this action closed. To give a different reason, settle this one first: the cycle it would have closed may already be closed.',
  'submittals.unsettled.revision_retry': 'Open the revision again',
  'submittals.unsettled.revision_check': 'Check what the record says',

  'submittals.unsettled.procurement':
    'The answer to saving these dates did not arrive, so whether they were saved is not known here.',
  'submittals.unsettled.procurement_hint':
    'Saving again sends the same dates under the same key, so dates that did save are handed back rather than written twice. The boxes are locked until that is settled, because a changed date would not reach a record the instance already has. Saving different dates is a later edit, and it needs this one settled first.',
  'submittals.unsettled.procurement_retry': 'Save the same dates again',
  'submittals.unsettled.procurement_check': 'Check what the record says',

  'submittals.submitted': 'Submitted for review as {number}.',
  'submittals.revised': 'Revision {n} opened.',
  'submittals.procurement_saved': 'Dates saved.',
  'submittals.permits_installation':
    'This decision permits installation. The equipment register may expect this product on site.',
  'submittals.does_not_permit_installation':
    'This decision does not permit installation. The product is not approved for the work.',
  'submittals.review_setup_required':
    'Review setup is required before this submittal can be submitted.',
  'submittals.no_workflow_warning':
    'This submittal has no review steps, so submitting it will be refused. The project has to name an MEP Coordinator or an engineer of record for this scope first.',
  'submittals.procurement.shifts_review':
    'This project shifts the review dates when the date needed on site moves.',
  'submittals.procurement.does_not_shift_review':
    'The review dates will not move. The dates a reviewer was given do not change because a fabricator\u2019s date did.',
  'submittals.due_source.contract': 'from the contract terms',
  'submittals.due_source.default': 'the default review period',
  'submittals.due_source.manual': 'set by hand',
  'submittals.due_source.procurement': 'from the date needed on site',

  'court.title': 'Ball in court',
  'court.empty': 'Nothing is waiting on you here.',
  // Custom fields: one mechanism every register uses. A register names its
  // kind of record; these strings never name one.
  'customfields.columns.open': 'Columns',
  'customfields.columns.title': 'Columns',
  'customfields.columns.explain':
    "Choose which of this project's custom fields show as columns for you. Your choice is yours alone and follows you to any device.",
  'customfields.columns.none': 'This register has no custom fields yet.',
  'customfields.columns.follow_default': 'Use the project default',
  'customfields.columns.make_default': 'Make this the project default',
  'customfields.columns.default_saved': 'Project default saved.',
  // A register's own columns and its row height, chosen in the same drawer.
  'register.settings.row_height': 'Row height',
  'register.settings.row_height.small': 'Small',
  'register.settings.row_height.medium': 'Medium',
  'register.settings.row_height.large': 'Large',
  'register.settings.columns': "This register's columns",
  'customfields.manage.title': 'Manage fields',
  'customfields.manage.explain':
    'Fields are added for everybody on this project, are always optional, and start hidden as columns.',
  'customfields.manage.label': 'Name',
  'customfields.manage.type': 'Type',
  'customfields.manage.options': 'Options, one per line',
  'customfields.manage.add': 'Add field',
  'customfields.manage.added': 'Field added.',
  'customfields.manage.edit': 'Edit',
  'customfields.manage.up': 'Move up',
  'customfields.manage.down': 'Move down',
  'customfields.manage.retire': 'Retire',
  'customfields.manage.restore': 'Restore',
  'customfields.manage.retired': 'retired',
  'customfields.type.text': 'Text',
  'customfields.type.number': 'Number',
  'customfields.type.date': 'Date',
  'customfields.type.yes_no': 'Yes or no',
  'customfields.type.choice': 'Choice from a list',
  'customfields.panel.title': 'Custom fields',
  'customfields.panel.none': 'This register has no custom fields yet.',
  'customfields.panel.saved': 'Saved.',
  'customfields.panel.choose': 'Not set',
  'customfields.panel.not_in_list': '{value} (no longer in the list)',
  // In place of the fields, their values and the controls that would change
  // them, when the register declined to answer for them. Says nothing recorded
  // has changed, so nobody reads a withdrawn panel as lost work.
  'customfields.refused':
    'These custom fields are not being shown, because the register would not answer for them. Nothing recorded in them has changed.',
  'customfields.yes': 'Yes',
  'customfields.no': 'No',

  'meetings.title': 'Meetings',
  'meetings.new': 'Schedule meeting',
  'meetings.schedule': 'Schedule',
  'meetings.count_one': '1 meeting',
  'meetings.count': '{count} meetings',
  'meetings.filter.state': 'Status',
  'meetings.filter.series': 'Series',
  'meetings.column.number': 'Number',
  'meetings.column.title': 'Meeting',
  'meetings.column.held': 'Meeting date',
  'meetings.column.state': 'Status',
  'meetings.column.minutes': 'Minutes',
  'meetings.column.open': 'Open items and actions',
  'meetings.column.chair': 'Meeting chair',
  'meetings.state.scheduled': 'Scheduled',
  'meetings.state.held': 'Held',
  'meetings.state.published': 'Published',
  'meetings.state.cancelled': 'Cancelled',
  'meetings.series.title': 'Recurring meetings',
  'meetings.series.back': 'Back to the meetings',
  'meetings.series.link': 'Recurring meetings',
  'meetings.series.existing': 'The series on this project',
  'meetings.series.none': 'This project has no recurring meetings yet.',
  'meetings.series.from': 'Proposing dates from {date}',
  'meetings.series.no_pattern': 'Proposes no dates',
  'meetings.series.when': 'When {title} meets',
  'meetings.series.as_needed': 'A series that meets as needed proposes no dates.',
  'meetings.series.starts_on': 'The pattern starts',
  'meetings.series.starts_hint': 'Clear this and the series stops proposing dates.',
  'meetings.series.weekdays': 'Which days of the week',
  'meetings.series.monthly': 'Which day of the month',
  'meetings.series.on_day': 'On a fixed day of the month',
  'meetings.series.day_of_month': 'Day of the month',
  'meetings.series.on_weekday': 'On a weekday of the month',
  'meetings.series.which_week': 'Which one',
  'meetings.series.which_weekday': 'Which weekday of the month',
  'meetings.series.ends': 'How it ends',
  'meetings.series.ends_never': 'It keeps going',
  'meetings.series.ends_on': 'On a date',
  'meetings.series.ends_after': 'After a number of meetings',
  'meetings.series.ends_date': 'The last date',
  'meetings.series.ends_count': 'How many meetings',
  'meetings.series.save_pattern': 'Save when it meets',
  'meetings.series.proposed': 'Dates it proposes',
  'meetings.series.proposed_hint':
    'Nothing is in the diary until you tick a date and schedule it.',
  'meetings.series.no_dates': 'This series proposes no dates in the next few weeks.',
  'meetings.series.book_one': 'Schedule 1 date',
  'meetings.series.book': 'Schedule {count} dates',
  'meetings.series.new': 'Start a series',
  'meetings.series.name': 'What it is called',
  'meetings.series.kind': 'What kind of meeting',
  'meetings.series.how_often': 'How often it meets',
  'meetings.series.who_chairs': 'Who runs it',
  'meetings.series.usual_place': 'Where it usually is',
  'meetings.series.usual_time': 'What time it usually starts',
  'meetings.series.create': 'Create the series',
  'meetings.kind.oac': 'Owner, architect and contractor',
  'meetings.kind.subcontractor_coordination': 'Subcontractor coordination',
  'meetings.kind.safety': 'Safety',
  'meetings.kind.precon': 'Preconstruction',
  'meetings.kind.design': 'Design',
  'meetings.kind.other': 'Other',
  'meetings.weekday.0': 'Monday',
  'meetings.weekday.1': 'Tuesday',
  'meetings.weekday.2': 'Wednesday',
  'meetings.weekday.3': 'Thursday',
  'meetings.weekday.4': 'Friday',
  'meetings.weekday.5': 'Saturday',
  'meetings.weekday.6': 'Sunday',
  'meetings.week.1': 'The first',
  'meetings.week.2': 'The second',
  'meetings.week.3': 'The third',
  'meetings.week.4': 'The fourth',
  'meetings.week.last': 'The last',
  'meetings.cadence.weekly': 'Weekly',
  'meetings.cadence.biweekly': 'Fortnightly',
  'meetings.cadence.monthly': 'Monthly',
  'meetings.cadence.as_needed': 'As needed',
  'meetings.minutes.revision': 'Revision {n}',
  'meetings.minutes.none': 'Not issued',
  'meetings.open.items_one': '1 item',
  'meetings.open.items': '{count} items',
  'meetings.open.actions_one': '1 action',
  'meetings.open.actions': '{count} actions',
  'meetings.court.title': 'My meeting actions',
  'meetings.court.empty': 'You have no assigned meeting actions.',
  'meetings.field.series': 'Meeting series',
  'meetings.field.one_off': 'Single meeting',
  'meetings.field.from_series':
    'The title, the chair and who is invited come from the series.',
  'meetings.field.title': 'Title',
  'meetings.field.chair': 'Meeting chair',
  'meetings.field.meeting_date': 'Date',
  'meetings.field.start_time': 'Start time',
  'meetings.field.location': 'Location',
  'meetings.detail.about': 'About this meeting',
  'meetings.detail.scribe': 'Scribe',
  'meetings.detail.agenda': 'Agenda',
  'meetings.detail.agenda.empty': 'Nothing is on the agenda yet.',
  'meetings.detail.item_title': 'Item',
  'meetings.detail.item_body': 'What it is about',
  'meetings.detail.add_item': 'Add to the agenda',
  'meetings.detail.item_added': 'Added to the agenda.',
  'meetings.detail.add_note': 'Add a note',
  'meetings.detail.note_label': 'What was said',
  'meetings.detail.save_note': 'Save the note',
  'meetings.detail.note_added': 'Note recorded.',
  'meetings.detail.close_item': 'Close the item',
  'meetings.detail.item_closed': 'Item closed.',
  'meetings.detail.carried_one': 'Carried forward once',
  'meetings.detail.carried': 'Carried forward {count} times',
  'meetings.item_state.open': 'Open',
  'meetings.item_state.closed': 'Closed',
  'meetings.detail.actions': 'Actions',
  'meetings.detail.actions.empty': 'This meeting has raised no actions.',
  'meetings.detail.action_body': 'What has to be done',
  'meetings.detail.assignee': 'Who is doing it',
  'meetings.detail.due_on': 'Needed by',
  'meetings.detail.raise_action': 'Raise the action',
  'meetings.detail.action_raised': 'Action raised.',
  'meetings.detail.complete': 'Mark it done',
  'meetings.detail.action_completed': 'Action marked done.',
  'meetings.action_state.open': 'Open',
  'meetings.action_state.done': 'Done',
  'meetings.action_state.cancelled': 'Cancelled',
  'meetings.completed_by.person': 'Marked done by a person',
  'meetings.completed_by.linked_record': 'Closed by the record it produced',
  'meetings.detail.attendance': 'Attendance',
  'meetings.detail.attendance.empty': 'Nobody has been marked yet.',
  'meetings.detail.who': 'Who attended',
  'meetings.detail.status': 'How they attended',
  'meetings.detail.record': 'Record it',
  'meetings.detail.attendance_recorded': 'Attendance recorded.',
  'meetings.detail.optional': 'Not required',
  'meetings.attendance_status.invited': 'Invited',
  'meetings.attendance_status.present': 'Present',
  'meetings.attendance_status.absent': 'Absent',
  'meetings.attendance_status.excused': 'Excused',
  'meetings.detail.minutes': 'Minutes',
  'meetings.detail.minutes_label': 'The minutes',
  'meetings.detail.save_minutes': 'Save the draft',
  'meetings.detail.minutes_saved': 'Draft saved. Nothing has gone out.',
  'meetings.detail.marks_held':
    'Saving the minutes or recording attendance marks this meeting as held.',
  'meetings.detail.no_minutes':
    'Minutes with nothing in them cannot be issued: an empty document reads as though the meeting decided nothing.',
  'meetings.detail.publish': 'Issue the minutes',
  'meetings.detail.reissue': 'Reissue the minutes',
  'meetings.detail.reissue_note': 'Why it is being reissued',
  'meetings.detail.reissue_why':
    'Required from the second revision on. Earlier revisions are kept, so a reader can see what changed and why.',
  'meetings.detail.published': 'Issued as revision {n}.',
  'meetings.detail.revisions.empty': 'These minutes have not been issued.',
  'meetings.detail.links': 'Linked records',
  'meetings.detail.links.empty': 'This meeting references nothing else yet.',
  'meetings.relation.reference': 'References it',
  'meetings.relation.generated': 'Produced it',

  // -- the drawing set and the specification book --------------------------
  // The document itself, which both modules serve the same way. 503 is drawn
  // as waiting rather than as failure: on a site with two boxes and a cloud
  // peer, the record reaching a node before the file does is ordinary.
  'document.loading': 'Fetching the document\u2026',
  'document.waiting': 'The record is here and the document is on another box. Try again shortly.',
  'document.failed': 'The document could not be fetched.',
  'document.download': 'Download',
  'document.opened_at': 'Opened at page {page}',
  'document.not_drawable': 'This document is not a PDF, so it can only be saved.',

  'drawings.title': 'Drawings',
  'drawings.subtitle': 'Current set',
  'drawings.discipline': 'Discipline',
  'drawings.any_discipline': 'All disciplines',
  'drawings.narrow': 'Search drawings',
  'drawings.no_set': 'No drawings published yet.',
  'drawings.none': 'No drawings match your filters.',
  'drawings.number': 'Number',
  'drawings.sheet_title': 'Title',
  'drawings.area': 'Area',
  'drawings.revision': 'Revision',
  'drawings.no_revision': '—',
  'drawings.sheet.missing': 'Drawing not found.',
  'drawings.sheet.revision': 'Rev {revision}',
  'drawings.sheet.open_current': 'Open Rev {revision}',
  'drawings.sheet.scale': 'Scale',
  'drawings.sheet.no_scale': 'Not set',
  'drawings.sheet.size': 'Sheet size',
  'drawings.sheet.inches': '{width} × {height} in',
  'drawings.sheet.no_size': '—',
  'drawings.sheet.no_revision': 'No published revision.',
  'drawings.scale.heading': 'Sheet scale',
  'drawings.scale.current':
    'Rev {revision}: {scale}',
  'drawings.scale.none':
    'No scale set for Rev {revision}.',
  'drawings.scale.set': 'Set scale',
  'drawings.scale.change': 'Change scale',
  'drawings.scale.calibrate': 'Calibrate from a known distance',
  'drawings.scale.choose_legend': 'Scale from the drawing',
  'drawings.scale.preset': 'Scale',
  'drawings.scale.pick_one': 'Select a scale',
  'drawings.scale.family.architectural': 'Architectural',
  'drawings.scale.family.engineering': 'Engineering',
  'drawings.scale.family.metric': 'Metric',
  'drawings.scale.other': 'Custom…',
  'drawings.scale.as_printed': 'As printed',
  'drawings.scale.feet_per_inch': 'Feet per inch',
  'drawings.scale.means': '1" = {feet}\'',
  'drawings.scale.save': 'Save',
  'drawings.scale.save_for': 'Save for Rev {revision}',
  'drawings.scale.cancel': 'Cancel',
  'drawings.scale.again': 'Start over',
  'drawings.scale.calibrate_legend': 'Calibrate',
  'drawings.scale.calibrate_how':
    'Click two points a known distance apart, then enter the distance. Keyboard: arrows move the cross-hair, Enter picks a point.',
  'drawings.scale.picked': '{count} of 2 points',
  'drawings.scale.measured': '{inches} in apart on the sheet',
  'drawings.scale.too_short':
    'Points are too close. Pick two points farther apart.',
  'drawings.scale.no_unit':
    'This drawing can\'t be calibrated. Select a scale instead.',
  'drawings.scale.known': 'Distance',
  'drawings.scale.unit': 'Unit',
  'drawings.scale.unit.ft': 'feet',
  'drawings.scale.unit.in': 'inches',
  'drawings.scale.unit.m': 'meters',
  'drawings.scale.pick_here': 'Click two points on the drawing.',
  'drawings.embedded.heading': 'PDF annotations',
  'drawings.embedded.show': 'Show PDF annotations',
  'drawings.embedded.reading': 'Loading annotations…',
  'drawings.embedded.none': 'No PDF annotations on this page.',
  'drawings.embedded.count_one':
    '1 annotation from the original PDF.',
  'drawings.embedded.count':
    '{count} annotations from the original PDF.',
  'drawings.embedded.drawn': 'Showing: {kinds}',
  'drawings.embedded.drawn_hidden':
    'Hidden: {kinds}',
  'drawings.embedded.not_drawn':
    'Can\'t display: {kinds}. Open the PDF to see them.',
  'drawings.embedded.hidden_by_file_one':
    '1 annotation is hidden in the PDF.',
  'drawings.embedded.hidden_by_file':
    '{count} annotations are hidden in the PDF.',
  'drawings.embedded.flattened':
    'Flattened markups are part of the drawing and can\'t be hidden.',
  'drawings.banner.superseded': 'Superseded. This isn\'t the current revision.',

  // Loading a set, correcting what was read off it, and issuing it.
  'drawings.edit.toggle': 'Edit',
  'drawings.edit.done': 'Done editing',
  'drawings.edit.help': 'Changes save when you press Enter or leave the row. Press Esc to undo.',
  'drawings.edit.saving': 'Saving…',
  'drawings.edit.saved': 'Saved',
  'drawings.edit.state': 'Save status',
  'drawings.edit.no_discipline': 'No discipline',
  'drawings.edit.select_row': 'Select {number}',
  'drawings.edit.select_all': 'Select all',
  'drawings.edit.bulk_label': 'Set discipline for selected',
  'drawings.edit.bulk_pick': 'Select a discipline',
  'drawings.edit.bulk_apply': 'Apply to {count} sheets',
  'drawings.edit.bulk_apply_one': 'Apply to 1 sheet',
  'drawings.edit.bulk_completed': 'Changed ({count}): {numbers}.',
  'drawings.edit.bulk_refused': 'Not changed ({count}): {numbers}.',
  'drawings.edit.bulk_uncertain': 'Couldn\'t confirm ({count}): {numbers}.',
  'drawings.edit.bulk_unsent': 'Not sent ({count}): {numbers}.',
  'drawings.edit.bulk_again': 'Send the unconfirmed ones again',
  'drawings.edit.bulk_dismiss': 'Dismiss',
  'drawings.edit.row_unsettled': 'Couldn\'t confirm this change.',
  'drawings.edit.row_again': 'Send again',
  'drawings.edit.open': 'Open',
  'drawings.edit.open_sheet': 'Open {number}',
  'drawings.columns.resize': 'Resize the {column} column',
  'drawings.open_sets': 'Drawing sets',
  'drawings.sets.title': 'Drawing sets',
  'drawings.sets.subtitle':
    'All sets uploaded to this project.',
  'drawings.sets.name': 'Set name',
  'drawings.sets.revision_label': 'Revision',
  'drawings.sets.revision': 'Rev {revision}',
  'drawings.sets.area': 'Area',
  'drawings.sets.area_default': '—',
  'drawings.sets.issued_on': 'Drawing date',
  'drawings.sets.no_date': '—',
  'drawings.sets.file': 'PDF',
  'drawings.sets.load': 'Upload',
  'drawings.sets.loading': 'Uploading…',
  'drawings.sets.pages': 'Pages',
  'drawings.sets.state': 'Status',
  'drawings.sets.state.in_review': 'In review',
  'drawings.sets.state.published': 'Published',
  'drawings.sets.none': 'No sets uploaded yet.',
  'drawings.sets.help.name':
    'For example: Issued for Construction, Bid Set, Addendum 2.',
  'drawings.sets.help.revision':
    'As shown in the title block, like C, IFC or Addendum 2. Applies to every sheet unless you change it.',
  'drawings.sets.help.area':
    'Optional. Use only if sheet numbers repeat across buildings or phases, like Building B.',
  'drawings.sets.help.issued':
    'Optional. The date on the drawings.',
  'drawings.sets.help.file':
    'One PDF with all sheets. We\'ll read the sheet number and title from each title block.',
  'drawings.sets.received': '{name} uploaded. We\'re reading the sheets now. You can leave this page.',
  'drawings.sets.open_received': 'View set',
  'drawings.progress.where': 'Status',
  'drawings.progress.step.uploading': 'Uploading',
  'drawings.progress.step.queued': 'Queued',
  'drawings.progress.step.processing': 'Processing',
  'drawings.progress.step.ready_for_review': 'Ready to review',
  'drawings.progress.step.published': 'Published',
  'drawings.progress.sending': 'Uploading {sent} of {total} MB ({percent}%)',
  'drawings.progress.percent': '{percent}%',
  'drawings.progress.dismiss': 'Dismiss',
  'drawings.progress.checking':
    'Connection lost. Checking whether the upload finished…',
  'drawings.progress.candidate':
    'A set named {name} was just added. Check it before uploading again.',
  'drawings.progress.open_candidate': 'View set',
  'drawings.progress.open_candidate_n': 'View set {n}',
  'drawings.progress.not_found':
    '{name} isn\'t in the list yet. Check again before uploading again.',
  'drawings.progress.unchecked_list':
    'Couldn\'t load the sets list. Check again before uploading again.',
  'drawings.progress.unchecked_before':
    'Couldn\'t confirm the upload. Look for {name} in the list before uploading again.',
  'drawings.progress.check_again': 'Check again',
  'drawings.progress.queued': 'Uploaded. Waiting to process {total} pages.',
  'drawings.progress.reading': 'Read {done} of {total} sheets ({percent}%).',
  'drawings.progress.reading_page': 'Processing page {page}',
  'drawings.progress.current.opening': 'Opening file…',
  'drawings.progress.current.finishing': 'Finishing…',
  'drawings.progress.failed_short': '{count} failed',
  'drawings.progress.ready_note':
    'Ready to review. Every page has been attempted and read. Check the sheets, fix anything wrong, then publish.',
  'drawings.progress.ready_note_failed':
    'Ready to review. Every page has been attempted, and {count} could not be read. Fix them before you publish.',
  'drawings.progress.failed': 'Pages that couldn\'t be read: {pages}.',
  'drawings.progress.failed_recover':
    'Retry them, enter their numbers by hand, or exclude them if they aren\'t sheets.',
  'drawings.progress.retry': 'Retry',
  'drawings.progress.retrying': 'Retrying…',
  'drawings.box.frame_pending':
    'Loading page {page}…',
  'drawings.box.frame_failed':
    'Couldn\'t load page {page}.',
  'drawings.box.frame_retry': 'Try again',
  'drawings.box.heading': 'Title block',
  'drawings.box.help':
    'Draw a box around the sheet number and title. We\'ll read every page in the same spot. Apply it to the whole set or just this page. Your edits are kept.',
  'drawings.box.page': 'Page',
  'drawings.box.own': 'Page {page} uses its own box.',
  'drawings.box.follows_set': 'Uses the set\'s box.',
  'drawings.box.keys':
    'Drag to draw. Drag the box to move it, or a corner to resize. Keyboard: arrows move, Alt+arrows resize, Delete clears.',
  'drawings.box.apply_set': 'Apply to all pages',
  'drawings.box.apply_page': 'Apply to page {page} only',
  'drawings.box.also': 'Also pages',
  'drawings.box.also_example': 'e.g. 4, 9-12',
  'drawings.box.also_bad': 'Enter pages from 1 to {count}, like 4, 9-12.',
  'drawings.box.clear': 'Clear the box',
  'drawings.box.past_crop':
    'The box for page {page} goes past the edge of the sheet.',
  'drawings.box.fit_set': 'Fit to visible area (all pages)',
  'drawings.box.fit_page': 'Fit to visible area (page {page})',
  'drawings.box.start_default': 'Reset the box',
  'drawings.box.back_to_set': 'Use the set\'s box for page {page}',
  'drawings.box.loading': 'Loading…',
  'drawings.box.no_preview':
    'Couldn\'t display this page.',
  'drawings.box.not_here': 'The file isn\'t available here yet. Try again.',
  'drawings.box.box_label': 'Title block area: left {left}%, top {top}%, right {right}%, bottom {bottom}%',
  'drawings.box.close': 'Done',
  'drawings.processing.page_unreadable': 'Couldn\'t read this page. Retry or enter the sheet number.',
  'drawings.sets.scanned':
    'This PDF is a scan with no text, so sheet numbers and titles need to be entered by hand.',

  'drawings.review.title': 'Review the set',
  'drawings.review.still_reading': 'Still processing. Rows fill in as pages finish.',
  'drawings.review.recover_help':
    'Lots of sheets wrong? The title block is somewhere else. Use "Title block" to move the box. Just a few? Fix them in the row. Exclude covers and indexes.',
  'drawings.review.number_help':
    'As shown in the title block, like A-101. Used to match new revisions to existing sheets, so it must be unique in the set.',
  'drawings.review.title_help':
    'As shown in the title block, like Level 2 Floor Plan.',
  'drawings.review.issue_wait': 'You can publish once all pages are processed.',
  'drawings.review.waiting': 'Waiting…',
  'drawings.review.waiting_chip': 'Pending',
  'drawings.review.failed_chip': 'Failed',
  'drawings.review.own_box': 'Custom box',
  'drawings.review.box_on_page': 'Title block on page {page}',
  'drawings.review.page': 'Page',
  'drawings.review.read_as': 'Detected',
  'drawings.review.read_nothing': 'Nothing detected',
  'drawings.review.state': 'Status',
  'drawings.review.confidence': '{percent}% match',
  'drawings.review.check_this': 'Review',
  'drawings.review.corrected': 'Edited',
  'drawings.review.use_read': 'Use detected value for page {page}',
  'drawings.rename.sheet_open': 'Edit sheet',
  'drawings.rename.sheet_explain':
    'Revisions, links and markups stay with the sheet. The PDF isn\'t changed.',
  'drawings.rename.number': 'Sheet number',
  'drawings.rename.title': 'Sheet title',
  'drawings.rename.set_open': 'Rename set',
  'drawings.rename.set_name': 'Set name',
  'drawings.rename.save': 'Save',
  'drawings.rename.cancel': 'Cancel',
  'drawings.review.matched': 'Replaces a sheet',
  'drawings.review.new': 'New sheet',
  'drawings.review.ignored': 'Excluded',
  'drawings.review.ignore_page': 'Exclude page {page}',
  'drawings.review.put_back_page': 'Include page {page}',
  'drawings.review.number_on_page': 'Sheet number on page {page}',
  'drawings.review.title_on_page': 'Sheet title on page {page}',
  'drawings.review.all_read': 'All {count} pages read.',
  'drawings.review.unsure': '{count} of {total} pages need review.',
  'drawings.review.nothing_read':
    'Couldn\'t find any sheet numbers. Move the title block box or enter them by hand.',
  'drawings.review.issue': 'Publish',
  'drawings.review.issuing': 'Publishing…',
  'drawings.review.issue_note':
    'All sheets are published together. Older revisions are kept as superseded.',
  'drawings.review.issued_one': '1 sheet published.',
  'drawings.review.issued': '{count} sheets published.',
  'drawings.review.open_register': 'View drawings',
  'drawings.review.already_issued':
    'This set is published and can\'t be edited.',
  'drawings.review.region_open': 'Title block is in a different spot',
  'documents.upload.outcome_unknown':
    'The connection dropped before the node answered, so it is not known whether the file arrived. Look for it in the list before sending it again.',
  'documents.upload.session_ended': 'An upload from a session that has ended was set aside.',
  'drawings.review.region_left': 'Left edge',
  'drawings.review.region_top': 'Top edge',
  'drawings.review.region_right': 'Right edge',
  'drawings.review.region_bottom': 'Bottom edge',
  'drawings.review.region_apply': 'Re-read set',
  'drawings.review.region_reading': 'Re-reading all pages…',

  // The history of a document, shared by drawings and specifications because
  // the question is the same on both.
  'documents.history.title': 'Revisions',
  'documents.history.revision': 'Revision {revision}',
  'documents.history.current': 'Current',
  'documents.history.superseded': 'Superseded',
  'documents.history.by': 'issued by {name}',
  'documents.history.someone_else': 'somebody no longer on this project',
  'documents.history.replaced_by': 'Replaced by revision {revision}.',
  'documents.history.in_set': 'In {name}',
  'documents.history.in_book': 'In {name}, pages {from} to {to}',
  'documents.history.failed': 'The revision history could not be read.',

  // A correction to machine-read intake that did not reach the node. The words
  // typed into the row stay in it and the retry sends the same correction
  // rather than a second one; see `documents/corrections.ts`.
  'documents.correction.unsaved': 'Not saved yet',
  'documents.correction.send_again': 'Send again',

  'viewer.tools': 'Drawing view',
  'viewer.zoom_in': 'Zoom in',
  'viewer.zoom_out': 'Zoom out',
  'viewer.zoom_level': '{percent}%',
  'viewer.rotate': 'Turn the sheet a quarter turn',
  'viewer.rotate_short': 'Turn',

  'markup.layers': 'Markup',
  'markup.none': 'Nobody has marked up this revision.',
  'markup.your_draft': 'Your draft, nobody else can see it',
  'markup.layer_failed': 'This layer could not be read, so its marks are not on the sheet.',
  'markup.layer_refresh': 'Try reading this layer again',
  'markup.layer_stale':
    'This layer could not be read again. Its marks are as they were the last time it was read.',
  'markup.on_other_revisions': 'On other revisions',
  'markup.bound_explanation':
    'Marks stay on the revision they were drawn on. These belong to a revision that is not the one open.',
  'markup.another_revision': 'Another revision',

  'markup.tools': 'Markup tools',
  'markup.actions': 'Markup actions',
  'markup.tool.select': 'Select',
  'markup.tool.ink': 'Freehand',
  'markup.tool.line': 'Line',
  'markup.tool.arrow': 'Arrow',
  'markup.tool.polyline': 'Connected lines',
  'markup.tool.rectangle': 'Box',
  'markup.tool.ellipse': 'Oval',
  'markup.tool.polygon': 'Shape',
  'markup.tool.cloud': 'Revision cloud',
  'markup.tool.text': 'Note',
  'markup.tool.callout': 'Callout',
  'markup.tool.numbered_notes': 'Numbered notes',

  // Marks the node has not taken. Kept with their words and sent again under
  // the key they were made with, so a request the node answered on a link that
  // dropped is one mark and not two. See `documents/Unsent.tsx`.
  // "Not saved yet" was a claim this screen cannot make. A request the node
  // took and could not answer for leaves the mark on the sheet and the browser
  // none the wiser, so the heading says what is actually known — that these
  // have not been confirmed — and each row says what its own last attempt
  // established.
  'markup.unsent.held_one': 'One mark has not been confirmed.',
  'markup.unsent.held': '{count} marks have not been confirmed.',
  'markup.unsent.send_again': 'Send again',
  // Offered only where nothing was stored, and it says so.
  'markup.unsent.discard': 'Discard',
  // Offered where the answer never came back. "Stop offering this" was the
  // right sentence about the list and the wrong one about the action: it threw
  // the action away, and the action is the only copy of something that may be
  // on the drawing. The row goes out of the way and stays sendable, and the
  // label says so.
  'markup.unsent.set_aside': 'Set aside',
  'markup.unsent.aside_one': 'One is set aside and still held.',
  'markup.unsent.aside': '{count} are set aside and still held.',
  'markup.unsent.restore': 'Put back',
  'markup.unsent.outcome_held': 'Not sent yet.',
  'markup.unsent.outcome_sending': 'Sending…',
  'markup.unsent.outcome_refused': 'The node refused this. Nothing was stored.',
  'markup.unsent.outcome_unknown':
    'No answer came back, so this may already be on the drawing. Sending again is safe and will not make a second mark.',
  // A refusal of a *retry* says nothing about the attempt whose answer was
  // lost. The node may have taken that one and failed to say so, so this row
  // stays uncertain and is never offered Discard.
  'markup.unsent.outcome_unknown_refused':
    'The node refused the last attempt, but an earlier one went out without an answer, so this may already be on the drawing. Sending again is safe and will not make a second mark.',
  'markup.unsent.on_another_revision_one':
    'One unconfirmed mark was drawn on another revision. Open that revision to send it.',
  'markup.unsent.on_another_revision':
    '{count} unconfirmed marks were drawn on other revisions. Open the revision each was drawn on to send it.',
  // Changes to a mark already on the sheet. A separate vocabulary from the
  // unsent marks above on purpose: what is at risk here is the edit, not the
  // mark, and the uncertain sentence has to say so rather than promise that
  // sending again will not make a second mark.
  'markup.unsaved.held_one': 'One change has not been saved.',
  'markup.unsaved.held': '{count} changes have not been saved.',
  'markup.unsaved.shape': 'Moved or resized',
  'markup.unsaved.words': 'Words changed',
  'markup.unsaved.save_again': 'Save again',
  'markup.unsaved.put_back': 'Put the mark back',
  'markup.unsaved.stop_holding': 'Stop holding this',
  'markup.unsaved.waiting': 'A later change to this mark is waiting for this one.',
  'markup.unsaved.aside_one': 'One change is set aside and still held.',
  'markup.unsaved.aside': '{count} changes are set aside and still held.',
  'markup.unsaved.outcome_held': 'Not saved yet.',
  'markup.unsaved.outcome_sending': 'Saving\u2026',
  'markup.unsaved.outcome_refused': 'The node refused this change. The mark is as it was.',
  'markup.unsaved.outcome_unknown':
    'No answer came back, so the mark may already have this change. Saving again looks first and only writes if the change is not there.',
  'markup.unsaved.outcome_unknown_refused':
    'The node refused the last attempt, but an earlier one went out without an answer, so the mark may already have this change. Saving again looks first and only writes if the change is not there.',
  'markup.unsaved.on_another_revision_one':
    'One unsaved change was made on another revision. Open that revision to save it.',
  'markup.unsaved.on_another_revision':
    '{count} unsaved changes were made on other revisions. Open the revision each was made on to save it.',
  'markup.retype': 'Change the words',
  'pins.unsent.held_one': 'One pin has not been confirmed.',
  'pins.unsent.held': '{count} pins have not been confirmed.',
  'pins.unsent.on_another_revision_one':
    'One unconfirmed pin was placed on another revision. Open that revision to send it.',
  'pins.unsent.on_another_revision':
    '{count} unconfirmed pins were placed on other revisions. Open the revision each was placed on to send it.',
  'markup.kind.other': 'Mark',
  'markup.undo': 'Undo',
  'markup.redo': 'Redo',
  'markup.delete': 'Remove',
  'markup.remove_mark': 'Remove this {kind}',
  'markup.publish': 'Share with the project',
  'markup.saving': 'Saving\u2026',
  'markup.draft_name': 'Markup',
  'markup.finish_shape': 'Finish the shape',
  'markup.cancel_shape': 'Start again',
  'markup.in_progress': '{count} point(s) placed',
  'markup.hint.select': 'Tap a mark to select it. Your own marks can be removed.',
  'markup.hint.freehand': 'Drag to draw. On a tablet one finger draws and two fingers zoom.',
  'markup.hint.span': 'Drag from one corner to the other.',
  'markup.hint.vertices': 'Tap each corner, then finish the shape.',
  'markup.hint.callout':
    'Drag from the thing the callout is about to where its words should go. The arrow points where the drag began.',
  'markup.note.title_callout': 'Write the callout',
  'markup.note.label_callout': 'What the callout says',
  'markup.note.title_numbered': 'Write the first note',
  'markup.note.label_numbered':
    'Note 1. Add more notes, and place numbered markers on the drawing, once the list is on the sheet.',
  'markup.numbered.title': 'Numbered notes',
  'markup.numbered.explain':
    'Each marker shows the number of its note, and numbers follow the order of the list: removing a note renumbers the list and its markers together.',
  'markup.numbered.note_label': 'Note {number}',
  'markup.numbered.markers_one': 'One marker on the drawing.',
  'markup.numbered.markers': '{count} markers on the drawing.',
  'markup.numbered.place': 'Place markers for note {number}',
  'markup.numbered.placing':
    'Tap the drawing where note {number} applies. Each tap places one marker. Markers go on this sheet only.',
  'markup.numbered.stop_placing': 'Done placing markers',
  'markup.numbered.move_up': 'Move note {number} up',
  'markup.numbered.move_down': 'Move note {number} down',
  'markup.numbered.remove_last_marker': 'Remove the last marker for note {number}',
  'markup.numbered.remove': 'Remove note {number}',
  'markup.numbered.remove_with_marker': 'Remove note {number} and its marker',
  'markup.numbered.remove_with_markers': 'Remove note {number} and its {count} markers',
  'markup.numbered.save': 'Save the notes',
  'markup.numbered.new_label': 'Another note',
  'markup.numbered.add': 'Add the note',
  'markup.numbered.full': 'This list holds as many notes as one list can.',
  'markup.note.title': 'Write the note',
  'markup.note.label': 'What the note says',
  'markup.note.save': 'Put it on the sheet',
  'markup.note.cancel': 'Cancel',
  'markup.export': 'Download this sheet',
  'markup.export.choice': 'What to put in the file',
  'markup.export.with': 'With SiteFocus markup',
  'markup.export.without': 'Without SiteFocus markup',
  'markup.export.none_chosen': 'The file will be this sheet with no SiteFocus markup drawn into it.',
  'markup.export.embedded_travel':
    'Either way, the annotations embedded in the original PDF come with the file exactly as the original holds them, whether or not they are shown on this screen, and anything flattened into the drawing is part of the page.',
  'markup.export.embedded_count_one':
    'This page of the original carries 1 embedded annotation. It comes with the file either way, whether or not it is shown on this screen.',
  'markup.export.embedded_count':
    'This page of the original carries {count} embedded annotations. They come with the file either way, whether or not they are shown on this screen.',
  'markup.export.embedded_none':
    'This page of the original carries no embedded annotations. Anything flattened into the drawing itself is part of the page and comes with it.',
  'markup.export.plain': 'The file will be this sheet as it was issued.',
  'markup.export.with_layers_one':
    'The file will be this sheet with the shared layer shown here drawn into it.',
  'markup.export.with_layers':
    'The file will be this sheet with the {count} shared layers shown here drawn into it.',
  'markup.export.draft_left_out':
    'Your draft is not in it. Share it with the project first if it should be.',
  'markup.export.making': 'Making the file.',
  'markup.export.failed': 'The file could not be made. Nothing about the sheet has changed.',

  // Records pinned where they apply, and the revision each was pinned on.
  'pins.title': 'Linked records',
  'pins.none': 'No record is pinned to this sheet.',
  'pins.none_on_section': 'No record is linked to this section.',
  'pins.open': 'Open {record}',
  'pins.pin_a_record': 'Pin a record',
  'pins.place_here': 'Tap the drawing where the record applies',
  'pins.placing': 'Tap the drawing where it applies.',
  'pins.stop_placing': 'Never mind',
  'pins.choose_record': 'Which record is this?',
  'pins.kind_label': 'Kind of record',
  'pins.kind.rfi': 'RFI',
  'pins.kind.submittals': 'Submittal',
  'pins.kind.deficiency': 'Deficiency',
  'pins.find': 'Find it',
  'pins.nothing_found': 'No record here matches that.',
  'pins.on_page': 'Page {page}',
  'pins.placed_on': 'Pinned on {revision}',
  'pins.placed_on_another': 'Pinned on another revision',
  'pins.open_that_revision': 'Open that revision',
  'pins.stays_where_it_was':
    'A pin keeps the revision it was placed on. Reissuing the sheet leaves it where it was rather than sliding it onto a drawing nobody put it on; if the thing moved, place a new pin here.',

  'markup.unsupported':
    'Stamps and measurements are stored by the canvas but cannot be drawn on this screen yet: a stamp needs a code from this project\u2019s palette and a measurement needs the scale it was taken at.',

  'specs.title': 'Specifications',
  'specs.subtitle': 'Current specifications. Select a section number to open it.',
  'specs.division': 'Division',
  'specs.any_division': 'All divisions',
  'specs.narrow': 'Search section number or title',
  'specs.no_book': 'No specifications published yet.',
  'specs.none': 'No section here matches that.',
  'specs.number': 'Section',
  'specs.section_title': 'Title',
  'specs.pages': 'Pages',
  'specs.revision': 'Revision',
  'specs.no_revision': 'None issued',
  'specs.section.missing': 'That section is not on this project.',
  'specs.section.revision': 'Revision {revision}',
  'specs.section.open_current': 'Open revision {revision}',
  'specs.section.pages': 'Pages in the book',
  'specs.section.page_range': '{from} to {to}',
  'specs.section.no_pages': 'Not recorded',
  'specs.section.no_revision': 'This section has no issued revision, so there is nothing to open.',

  // Walking the pages of one section on its markup canvas.
  'specs.canvas.page': 'Page {page} of the book, {at} of {count} in this section',
  'specs.canvas.previous': 'Previous page of this section',
  'specs.canvas.next': 'Next page of this section',

  // Taking one section away as its own file, cut out of the book it is in.
  'specs.export': 'Download this section',
  'specs.export.plain':
    'The file will be pages {from} to {to} of the book, as this revision was issued.',
  'specs.export.making': 'Making the file.',
  'specs.export.failed':
    'The file could not be made. Nothing about the section or the book has changed.',
  'specs.export.unavailable':
    'This section cannot be taken away on its own. Open the book above to read it.',
  'specs.banner.superseded': 'This is not the current revision of this section.',

  // Loading a book, correcting where it was cut, and issuing it.
  'specs.open_books': 'Specification books',
  'specs.books.title': 'Specification books',
  'specs.books.subtitle':
    'Uploaded specification books. All included sections are published together.',
  'specs.books.name': 'Book name',
  'specs.books.revision_label': 'Revision',
  'specs.books.revision': 'Revision {revision}',
  'specs.books.area': 'Specification area',
  'specs.books.area_default': 'default',
  'specs.books.issued_on': 'Document issue date',
  'specs.books.no_date': 'No date on it',
  'specs.books.file': 'Specification PDF',
  'specs.books.load': 'Upload specification book',
  'specs.books.loading': 'Reading every page.',
  'specs.books.pages': 'Pages',
  'specs.books.state': 'State',
  'specs.books.state.in_review': 'In review',
  'specs.books.state.published': 'Issued',
  'specs.books.none': 'No book has been loaded on this job yet.',
  'specs.books.scanned':
    'There is no text in this file, so nothing could be read off it. It is a scan: every section number, title and page range will have to be typed in.',

  'specs.review.title': 'Review the book',
  'specs.review.section_number': 'Section',
  'specs.review.section_title': 'Title',
  'specs.review.pages': 'Pages',
  'specs.review.read_as': 'Detected metadata',
  'specs.review.state': 'In the issue',
  'specs.review.read_nothing': 'Nothing was read here.',
  'specs.review.confidence': 'Needs verification',
  'specs.review.check_this': 'Check this',
  'specs.review.corrected': 'Corrected',
  'specs.review.matched': 'Replaces a section',
  'specs.review.new': 'New section',
  'specs.review.ignored': 'Not a section',
  'specs.review.ignore_at': 'Leave the section at page {page} out',
  'specs.review.put_back_at': 'Put the section at page {page} back in',
  'specs.review.number_at_page': 'Section number starting on page {page}',
  'specs.review.title_at_page': 'Section title starting on page {page}',
  // A range, and both ends of it are corrected by hand often enough to need
  // names a screen reader can tell apart.
  'specs.review.first_page_of': 'First page of {section}',
  'specs.review.last_page_of': 'Last page of {section}',
  'specs.review.all_read':
    '{count} sections processed. Review the section numbers and titles before publishing.',
  'specs.review.all_read_one': '1 section processed. Review the section number and title before publishing.',
  'specs.review.unsure': '{count} of {total} sections need checking.',
  'specs.review.nothing_read':
    'No section header was recognised in this file. Type each section in, or load a book that has text in it.',
  'specs.review.issue': 'Issue this book',
  'specs.review.issuing': 'Issuing.',
  'specs.review.issue_note':
    'Every section at once. Each one supersedes its previous revision, which is kept and can still be opened.',
  'specs.review.issued_one': 'One section issued.',
  'specs.review.issued': '{count} sections issued.',
  'specs.review.open_register': 'Open the register',
  'specs.review.already_issued':
    'This book has been issued, so it can no longer be corrected. What went out is below.',

  // -- observed presence ---------------------------------------------------
  // Every string here is written to keep one distinction alive: this module
  // shows where people were, not what they did and not what they were paid
  // for. A label reading "hours worked" would be a claim the source cannot
  // make and the reader cannot check.
  'workforce.title': 'Workforce',
  'workforce.subtitle': 'Worker and company presence, based on the latest badge-reader data.',
  'workforce.presence_is_not_labour':
    'Presence data does not measure hours worked, installed work, or schedule progress.',
  'workforce.not_observed': 'No data',
  'workforce.open_trends': 'See presence over time',
  'workforce.open_overview': 'Back to the site now',

  'workforce.freshness.observed_at': 'Last observed {when}.',
  'workforce.freshness.stale': 'These figures are old',
  'workforce.freshness.stale_body':
    'The source samples every fifteen minutes and nothing has arrived since, so this is not the site as it stands now.',
  'workforce.freshness.never': 'No presence data received',
  'workforce.freshness.never_body':
    'Counts are unavailable until presence data is received. Missing data does not mean zero workers.',
  'workforce.freshness.import_failed': 'The last import failed',
  'workforce.freshness.import_failed_body': 'The workforce source could not be reached.',
  'workforce.freshness.nothing_changed':
    'Nothing already imported was changed, so the figures below are the last good ones rather than a gap.',

  'workforce.now.heading': 'Latest recorded presence',
  'workforce.now.workers': 'Workers detected',
  'workforce.now.organizations': 'Companies present',
  'workforce.today.heading': 'Recorded today',
  'workforce.today.workers': 'Workers seen',
  'workforce.today.organizations': 'Companies seen',
  'workforce.roster.heading': 'Registered workforce',
  'workforce.roster.note':
    'Workers registered in the connected system. This is not the planned crew or attendance count.',
  'workforce.roster.workers': 'Workers on the roster',
  'workforce.roster.organizations': 'Companies on the roster',

  'workforce.freshness.read_at': 'Read from the workforce platform at {when}.',
  'workforce.freshness.no_source_time':
    'The platform did not say when that figure was current, so its age is the age of the reading.',
  'workforce.sync.heading': 'Syncing',
  'workforce.sync.now': 'Sync now',
  'workforce.sync.again': 'Sync',
  'workforce.sync.start': 'Sync now for the first time',
  // Said of a mapping that has been saved and never switched on. It is
  // deliberately not the unsynced sentence: nothing here stopped being
  // refreshed, because nothing here has ever been read.
  'workforce.sync.not_enabled':
    'Saved, and not yet importing. Nothing has been read from the platform for this project. Sync when the credential is in place on the importing node.',
  // Deliberately three strings rather than one "Synced". An import can come
  // back under HTTP 200 having failed, and reading a figure establishes when we
  // read it and nothing whatever about its age upstream, so neither "it worked"
  // nor "as of now" is ever safe to assert from the fact that a call returned.
  'workforce.sync.fetched': 'Synced. {reports} and {workers} came back.',
  'workforce.sync.fetched_nothing': 'Synced. The platform had nothing new, so nothing changed.',
  // This line said "the platform says these figures were current at {when}".
  // That was wrong: the moment behind it is the newest update the platform
  // stamped on the reports that came back, and a report update time is not a
  // guarantee about the figures inside it.
  'workforce.sync.as_of': 'The platform last updated these reports at {when}.',
  'workforce.sync.age_unknown':
    'The platform did not say how current these figures are, so their age is unknown.',
  'workforce.sync.failed_reason_unknown': 'The platform did not say why.',
  'workforce.sync.failed':
    'Not synced. The figures below are the ones already held and were not refreshed.',
  'workforce.sync.reports_one': '{count} report',
  'workforce.sync.reports': '{count} reports',
  'workforce.sync.workers_one': '{count} worker',
  'workforce.sync.workers': '{count} workers',
  // Said instead of the Sync button while a mapping has never imported and is
  // still missing a half somebody else supplies. Pressing Sync too early used
  // to switch the source on and then be refused by the import, which left a row
  // the setup form would no longer touch; the server refuses that link now, and
  // this is the screen saying so before somebody meets the refusal.
  'workforce.sync.not_ready': 'This setup is not finished, so there is nothing to sync yet.',
  'workforce.sync.not_ready_importer': 'No machine has been named to do the importing.',
  'workforce.sync.not_ready_credential':
    'No environment variable has been named for the credential.',
  'workforce.sync.not_ready_body':
    'Finish the setup above. Sync appears once the mapping names both, and it is still a deliberate press.',
  'workforce.sync.needs_admin':
    'Syncing and unsyncing are project manager actions. Ask somebody who administers this project.',
  'workforce.unsync.open': 'Unsync',
  'workforce.unsync.disclaimer':
    'Unsyncing stops this project being refreshed from the workforce platform. Everything already imported stays and goes on being shown, with the date it stopped against it. Nothing is deleted. Syncing again resumes from the last day already held.',
  'workforce.unsync.phrase_label': 'Type {phrase} to confirm',
  'workforce.unsync.accept': 'I understand this project will stop being refreshed.',
  'workforce.unsync.submit': 'Unsync this project',
  'workforce.unsync.done': 'Unsynced. Nothing was removed; it stopped being refreshed.',
  'workforce.unsync.stopped':
    'Unsynced. The figures below stopped being refreshed and are not current.',
  'workforce.unsync.stopped_on':
    'Unsynced on {when}. The figures below stopped being refreshed then and are not current.',
  'workforce.setup.heading': 'Setup',
  'workforce.setup.mapping': 'Mapped to',
  'workforce.setup.mapping_value': '{project} \u2192 {vendor} project {ref}',
  'workforce.setup.mapping_unset': 'No project named on the platform yet',
  'workforce.setup.imports_on': 'Imported by',
  'workforce.setup.imports_on_nobody': 'No machine named yet',
  // The variable's NAME is deliberately absent from all four of these now. It
  // is not a secret, and it was here for a reason -- somebody checking their
  // deployment needs to know which variable to set -- but the owner's rule is
  // that credentials and environment-variable names stay off the ordinary
  // project setup screen, and the person reading this one is a construction
  // manager who cannot act on a variable name anyway. What they can act on is
  // which machine, and whether it is set: that is what these say.
  'workforce.setup.credential_set':
    'The platform credential is set on {origin}.',
  'workforce.setup.credential_missing': 'Not configured',
  'workforce.setup.credential_missing_body':
    'The platform credential is not set on {origin}. Until it is, a sync will ' +
    'be refused and nothing here will change. Everything else on this page can ' +
    'be reviewed without it. Whoever looks after that machine sets it there.',
  'workforce.setup.credential_unreadable': 'This machine cannot read its own credential',
  'workforce.setup.credential_unreadable_body':
    '{origin} was set up to hold the platform credential itself, and cannot ' +
    'read the place it keeps it. Whether it still holds one is not known from ' +
    'here, so a sync will be refused until that is fixed. Nothing on this page ' +
    'needs changing: whoever looks after that machine checks the credential ' +
    'directory there.',
  'workforce.setup.credential_unknown': 'Not known from here',
  'workforce.setup.credential_unknown_body':
    'This project imports on {origin}, which is not the machine answering this ' +
    'page, so whether the credential is set there is not something this screen ' +
    'can see.',
  'workforce.register.heading': 'Connect workforce data',
  'workforce.register.finish': 'Finish setting this up',
  'workforce.register.finish_intro':
    'This mapping is saved and not yet importing. Fill in what is missing and save; it stays switched off until you Sync.',
  'workforce.register.save_finish': 'Save the setup',
  'workforce.register.intro':
    'Saving records where to look and switches nothing on. The mapping is saved not yet importing, and Sync, on its own card, is what starts it.',
  'workforce.register.this_project': 'This project',
  'workforce.register.name': 'Connection name',
  'workforce.register.vendor': 'Platform',
  'workforce.register.vendor_xano': 'Xano',
  'workforce.register.vendor_none': 'None yet',
  'workforce.register.vendor_note':
    'Choose None yet to record the mapping before the platform is settled. Nothing imports until a platform is chosen.',
  'workforce.register.base_url': 'Workforce server URL',
  'workforce.register.source_ref': 'Job identifier on the workforce platform',
  'workforce.register.source_ref_note':
    'Copy the project identifier exactly from the workforce platform.',
  'workforce.register.code': 'Pairing code',
  'workforce.register.code_note':
    'A short-lived code issued by an administrator on the other platform for this job. It works once. Naming a job is not the same as being entitled to read it, so this is what establishes that you are.',
  'workforce.register.check': 'Check the code',
  'workforce.register.matched': 'The platform calls this job {name}',
  'workforce.register.matched_note':
    'That is what the other platform says the identifier you entered refers to. If it is not this job, change the identifier and use a fresh code. Nothing has been saved yet.',
  'workforce.register.import_origin': 'Import server',
  'workforce.register.import_origin_note':
    'Select the server that will import workforce data. It must be able to reach the workforce platform.',
  'workforce.register.import_origin_unknown': 'Select an import server',
  // Placement, and only placement. This used to say a machine on site holds
  // what the cloud sends it and has no route out, so naming it would import
  // nothing -- an inference about a deployment's network drawn from a flag that
  // says where a machine sits. One deployment's only node reports itself on
  // site and reaches the platform perfectly well.
  'workforce.register.import_origin_on_site':
    '{origin} is configured as a machine on site. That says where it is, not what it can reach: whether it has a route to the platform is a fact about this deployment, which this screen cannot see. If it has none, the import will fail and say so.',
  'workforce.register.import_origin_none_note':
    'No importing machine is named, so nothing will read the platform for this project and no scheduled import can pick it up. The mapping still saves, and this can be set once the deployment has settled which machine imports.',
  'workforce.register.time_zone': 'Reporting time zone',
  'workforce.register.time_zone_note':
    "The project's own zone, as the platform reports it. Report dates are local to it.",
  'workforce.register.save': 'Save connection',
  'workforce.register.no_import':
    'Saves the connection switched off: nothing is imported now, and no scheduled import will run until you select Sync.',
  // The owner's condition on showing this module: real verified data, or an
  // explicit not-connected state, and any fixture data clearly labelled. This
  // is the third. It says invented rather than demonstration, because a
  // demonstration is the setting and invented is the property that matters.
  'workforce.fixture.heading': 'These figures were invented, not observed',
  'workforce.fixture.body':
    'They come from the calculation fixture ({names}), which writes a scenario whose numbers are known in advance so the arithmetic on these screens can be checked against them. Nobody was counted on a site. Nothing here says the workforce platform is connected.',
  'workforce.fixture.body_retained':
    'They come from the calculation fixture ({names}), which writes a scenario whose numbers are known in advance so the arithmetic on these screens can be checked against them. Nobody was counted on a site. It has been unsynced, which stopped the refresh and removed nothing, so the figures below are still the ones the fixture wrote.',
  // Where the figures come from is a question this screen can fail to answer,
  // and a screen that stays quiet about it has answered "nothing invented"
  // without having read anything.
  'workforce.provenance.checking': 'Checking where these figures come from\u2026',
  'workforce.provenance.unavailable': 'Where these figures come from could not be read',
  'workforce.provenance.unavailable_body':
    'The list of sources on this project did not load, so nothing below can be shown as observed rather than invented. Reload the screen, and tell an administrator if it keeps happening.',
  'workforce.sources.heading': 'Data source',
  'workforce.sources.none':
    'No workforce source is registered on this project, so nothing is being imported.',
  'workforce.sources.name': 'Source',
  'workforce.sources.vendor': 'Platform',
  'workforce.sources.imports_on': 'Imported by',
  'workforce.sources.imported_through': 'Imported through',
  'workforce.sources.last_good': 'Last good import',
  'workforce.sources.never_imported': 'Nothing yet',

  'workforce.trends.title': 'Presence over time',
  'workforce.trends.subtitle':
    'One row a day, from the revision of that day\u2019s report that is in force.',
  'workforce.trends.from': 'From',
  'workforce.trends.to': 'To',
  'workforce.trends.window_backwards': 'The end of the window is before its start.',
  // Said while one of the two boxes is not a complete day, which is every
  // keystroke of retyping one: a date input reports the empty string until
  // its three segments compose a date. Not the backwards warning, because
  // an unfinished window is not a window in the wrong order; and not
  // silence, because the figures on the screen stop being read and the
  // reader is owed the reason.
  'workforce.trends.window_unfinished': 'One end of the window is not a complete date yet.',
  'workforce.trends.window': '{from} to {to}',
  'workforce.trends.distinct_workers': 'Different people',
  'workforce.trends.distinct_note': 'Counted once each, not added up across the days.',
  // Named by the owner on #171: the tile says what the figure is time *of*,
  // and the note under it says the unit and what the figure is not. "Hours"
  // alone reads as an amount of work owed to somebody, which this is not.
  'workforce.trends.presence_hours': 'Observed time on site',
  // The three states of a window total. "Not available" rather than a dash,
  // and never a nought: a nought in this position claims the site was empty,
  // which is a claim nobody made.
  'workforce.trends.unavailable': 'Not available',
  'workforce.trends.unavailable_note':
    'No report is held for any day in this window, so there is nothing to count. It does not mean nobody was here.',
  'workforce.trends.people_at_least': 'At least {count}',
  'workforce.trends.presence_at_least': 'At least {hours}',
  'workforce.trends.floor_note':
    'A floor, not a total: no report is held for {count} of the days in this window, and a day nobody reported can only add to this.',
  'workforce.trends.presence_note':
    'In hours, summed across the people observed. Time on site, not hours worked or paid.',
  'workforce.trends.days_held': 'Days with a report',
  'workforce.trends.days_missing': '{count} days in this window have none.',
  'workforce.trends.chart': 'Observed time on site, a day at a time',
  'workforce.trends.chart_alt':
    'A column for each of {days} days, the tallest being {hours} hours.',
  'workforce.trends.chart_scale': 'The tallest column is {hours} hours, on {day}.',
  'workforce.trends.missing_heading': 'Days with no report',
  'workforce.trends.missing_body':
    'These dates are not drawn as zero, because a report that never arrived and a site nobody turned up to are different things:',
  'workforce.trends.none': 'No day in this window has a report yet.',
  'workforce.trends.day': 'Day',
  'workforce.trends.workers': 'Workers',
  'workforce.trends.hours': 'Hours',
  'workforce.trends.peak': 'Most at once',
  'workforce.trends.organizations': 'Companies',
  'workforce.trends.revision': 'From revision',
  'workforce.trends.by_trade': 'By trade',
  'workforce.trends.by_organization': 'By company',
  'workforce.trends.no_breakdown': 'Nothing to break down in this window.',
  'workforce.selection.only': 'the only one',
  'workforce.selection.newest': 'the newest for that day',
  'workforce.selection.latest_source_update': 'the newest for that day',
  'workforce.selection.superseded_on_arrival': 'an older revision, kept but not counted',

  'workforce.plan.title': 'The plan against who turned up',
  'workforce.plan.subtitle':
    'What the crew committed to for these days, and how many people were observed on site.',
  'workforce.plan.open': 'Compare with the committed plan',
  'workforce.plan.people_not_hours': 'This compares people, not hours',
  'workforce.plan.people_not_hours_body':
    'The only place anybody states how many people are expected is the crew size on a lookahead line. The master schedule carries durations and dates and no crew at all, so a planned-hours figure would have to be invented before it could be shown here.',
  'workforce.plan.day': 'Day',
  'workforce.plan.planned': 'Committed',
  'workforce.plan.observed': 'Observed',
  'workforce.plan.difference': 'Difference',
  'workforce.plan.plan_name': 'From the plan',
  'workforce.plan.no_commitment': 'Nothing committed',
  'workforce.plan.no_commitment_note': 'No published plan covers this day.',
  'workforce.plan.no_report': 'Not observed',
  'workforce.plan.no_report_note': 'No report is held for this day.',
  'workforce.plan.withheld': 'Withheld',
  'workforce.plan.at_least_under': 'At least {count} fewer than committed',
  'workforce.plan.at_least_note':
    '{count} committed lines on this day carry no crew size. Filling them in can only raise what was committed, so the shortfall can only grow.',
  'workforce.plan.withheld_note':
    '{count} of the committed lines on this day carry no crew size, so the plan is incomplete and a difference would read as a crew that did not turn up.',
  'workforce.plan.over': '{count} more than committed',
  'workforce.plan.under': '{count} fewer than committed',
  'workforce.plan.level': 'As committed',
  'workforce.plan.incomplete_heading': 'Part of the plan says nothing about crew size',
  'workforce.plan.incomplete_body':
    '{count} committed lines carry no crew size. Nobody typed a number, so none is shown and the days they fall on do not report a difference. They are not counted as nobody.',
  'workforce.plan.windows_heading': 'The plans these days come from',
  'workforce.plan.window_dates': '{from} to {to}',
  'workforce.plan.window_published': 'Published {when}',
  'workforce.plan.window_unpublished': 'Not published',
  'workforce.plan.window_lines': 'Committed lines',
  'workforce.plan.window_blank': 'Without a crew size',
  'workforce.plan.none':
    'No published plan covers any day in this window, so there is nothing to compare against yet. A lookahead has to be published, not just drafted.',
  'workforce.plan.not_per_activity': 'Not broken down by activity, on purpose',
  'workforce.plan.not_per_activity_body':
    'A badge passing a reader says somebody was on site. It does not say what they were doing, and splitting the day across its activities would be arithmetic on a fact nobody has. What the crew wrote down against each activity is in the daily log, and By activity compares that with the plan.',

  // By activity: the committed plan against the field report. Two figures a
  // person typed, about the same work, on the same day.
  'workforce.activity.title': 'The plan against what the crew reported',
  'workforce.activity.subtitle':
    'One row per activity per day: how many people were committed to it, and how many the crew wrote down against it in the daily log.',
  'workforce.activity.open': 'By activity',
  'workforce.activity.typed_figures': 'Both figures here were typed by a person',
  'workforce.activity.typed_figures_body':
    'Committed is the crew size on a published lookahead line. Reported is the crew on daily log entries that named the activity, leaving out any entry somebody rejected. Observed presence is not on these rows.',
  'workforce.activity.activity': 'Activity',
  'workforce.activity.committed': 'Committed',
  'workforce.activity.reported': 'Reported',
  'workforce.activity.difference': 'Difference',
  'workforce.activity.unnamed': 'Not linked to an activity',
  'workforce.activity.unknown_activity': 'Activity not on this machine',
  'workforce.activity.unknown_activity_note':
    'The schedule here has never held it. It may be on a machine that has the current issue.',
  'workforce.activity.nothing_committed': 'Nothing committed',
  'workforce.activity.nothing_committed_note': 'No published line covers this activity today.',
  'workforce.activity.nothing_reported': 'Nothing reported',
  'workforce.activity.nothing_reported_note': 'No daily log entry named it today.',
  'workforce.activity.no_crew_stated': 'No crew stated',
  'workforce.activity.no_crew_stated_note':
    '{count} entries named it and carried no crew line, so no figure was written.',
  'workforce.activity.over': '{count} more than committed',
  'workforce.activity.under': '{count} fewer than committed',
  'workforce.activity.level': 'As committed',
  'workforce.activity.at_least_under': 'At least {count} fewer than committed',
  'workforce.activity.at_least_over': 'At least {count} more than committed',
  'workforce.activity.floor_note':
    'One side of this is incomplete and can only grow, so the gap is a floor rather than a measurement.',
  'workforce.activity.withheld': 'Withheld',
  'workforce.activity.withheld_note':
    'The plan or the log is incomplete here, and filling either in could close the gap.',
  'workforce.activity.none':
    'Nothing has been committed or reported against an activity in this window yet. A lookahead has to be published, and a daily log entry has to name an activity.',
  'workforce.activity.days_heading': 'Observed on site, and what the log accounted for',
  'workforce.activity.day': 'Day',
  'workforce.activity.observed': 'Observed on site',
  'workforce.activity.reported_named': 'Reported against an activity',
  'workforce.activity.reported_unnamed': 'Reported against none',
  'workforce.activity.no_report': 'Not observed',
  'workforce.activity.no_report_note': 'No report is held for this day.',
  'workforce.activity.nothing_logged': 'Nothing logged',
  'workforce.activity.nothing_logged_note': 'No entry of this kind was written.',
  'workforce.activity.do_not_subtract': 'These two columns do not subtract',
  'workforce.activity.do_not_subtract_body':
    'Observed counts distinct people. Reported counts a crew against an activity, so somebody who spent a morning on one and an afternoon on another is on two entries. The difference between them is not a number of people, and no figure here pretends it is.',
  'workforce.activity.incomplete_heading': 'Some figures on this screen are incomplete',
  'workforce.activity.incomplete_body':
    '{lines} committed lines carry no crew size, and {entries} entries named an activity with no crew line. Both can only be revised upwards, which is why a difference is withheld rather than shown short.',
  'workforce.activity.no_hours': 'No hours figure appears here',
  'workforce.activity.no_hours_body':
    'The daily log carries a time on each crew line, and nobody has yet written down whether that time is per person or for the crew — the two differ by the headcount. Until that is settled no hours figure is shown, because a figure whose unit is unknown reads as authoritative anyway.',

  // -- the predictor index --------------------------------------------------
  //
  // Every string here is written on the assumption that the reader will act on
  // the number. So the withheld case is a sentence rather than a dash, an
  // excluded signal says what it could not count, and nothing in this block
  // describes the score as a measurement of anybody.
  'workforce.index.title': 'Planning and reporting score',
  'workforce.index.subtitle':
    'A four-week summary of plan completion, staffing coverage, and field reporting, calculated from the project\'s lookahead plans, field reports and badge-reader presence using the measures below.',
  'workforce.index.open': 'Planning and reporting score',
  'workforce.index.window': '{from} to {to}',
  'workforce.index.score_heading': 'The reading',
  'workforce.index.out_of': 'Out of 100. Check it against the rows below.',
  'workforce.index.withheld': 'Score unavailable: insufficient data',
  'workforce.index.withheld_body':
    'Too little has gone into this for a number to mean anything. The rows below show what is there, so you can see which signal is thin.',
  'workforce.index.withheld.too_few_signals':
    'At least two of the three measures need data before a score can be calculated.',
  'workforce.index.withheld_short': 'Withheld',
  'workforce.index.signals_heading': 'Score components',
  'workforce.index.signal': 'Signal',
  'workforce.index.ratio': 'Ratio',
  'workforce.index.of': 'Of',
  'workforce.index.weight': 'Weight',
  'workforce.index.not_counted': 'Not counted',
  'workforce.index.fraction': '{top} of {bottom}',
  'workforce.index.signal.promises_kept': 'Plan commitments completed',
  'workforce.index.signal.promises_kept.note':
    'Of the lookahead promises somebody has judged, how many were kept. The only one of the three that is a person’s judgement of finished work.',
  'workforce.index.signal.manning': 'Recorded staffing vs. planned staffing',
  'workforce.index.signal.manning.note':
    'Of the people the lookahead committed for a day, how many the badge readers saw. Attendance, not output: it says the plan was manned, never what was installed.',
  'workforce.index.signal.work_reported': 'Planned activities with field reports',
  'workforce.index.signal.work_reported.note':
    'Of the activities committed for a day, how many the field report says anything at all about. Silence against committed work usually shows here before it shows in a missed promise.',
  'workforce.index.nothing_to_count': 'No eligible records in this period',
  'workforce.index.nothing_to_count_note':
    'There was nothing of this kind on the project in this period, so this measure is not counted as zero.',
  'workforce.index.below_minimum': 'Too few to read',
  'workforce.index.below_minimum_note':
    'Fewer than {minimum}, which is not enough for a ratio to mean anything.',
  'workforce.index.too_few_days': 'Too few days to read',
  'workforce.index.too_few_days_note':
    '{had} of the days in this window had both a complete plan and a report held, and {days} are needed. One good day is a day, not a fortnight of manning.',
  'workforce.index.no_weight': 'None',
  'workforce.index.no_weight_note': 'Excluded: insufficient data.',
  'workforce.index.weight_raised': '{stated}% → {actual}%',
  'workforce.index.weight_raised_note':
    'Raised, because a signal that could not be computed was dropped and the rest were shared out over what was left.',
  'workforce.index.none_excluded': 'None',
  'workforce.index.excluded.not_assessed':
    'Promises nobody has judged. A date going past is not a broken promise, so these are set aside rather than counted against the job.',
  'workforce.index.excluded.days_without_a_complete_plan_or_a_report':
    'Days with no report held, or whose plan has a blank crew size. Dividing by a committed total that is itself a floor would make the manning look better than it was.',
  'workforce.index.excluded.committed_lines_naming_no_activity':
    'Committed lines naming no activity. There is nothing for a field report to name back, so there is nothing to count.',
  'workforce.index.no_model': 'How this score is calculated',
  'workforce.index.no_model_body':
    'The score is a weighted mean of the three measures above, each a count of recorded project data. No AI model is used. Review the component values and source records to understand the result.',
  'workforce.index.not_a_person': 'This is about the project, never about a person',
  'workforce.index.not_a_person_body':
    'This view shows project-level totals over a range of dates. It does not measure individual productivity, payroll, or overall construction progress.',
  'workforce.index.history_heading': 'Score history',
  'workforce.index.no_history': 'No score history yet',
  'workforce.index.no_history_body':
    'A score is recorded once a day from now on, so a trend becomes visible over the coming weeks.',
  'workforce.index.mixed_weights':
    'Some readings below were computed under a different set of weights. They are not comparable with the current one, so nothing joins them.',
  'workforce.index.reading_for': 'Four weeks to',
  'workforce.index.reading_score': 'Reading',
  'workforce.index.reading_taken': 'Taken',
  'workforce.index.other_weights': 'Calculation version: {version}.',
  'workforce.index.weights_version':
    'Calculation version: {version}. Scores calculated with different versions should not be compared directly.',
  // SiteFocus: QAQC-LOTO — the window onto the other application. Every state
  // below is a different sentence on purpose: "we could not tell you" must
  // never be able to read as "there is nothing locked out".
  'qaqcloto.title': 'QA/QC and lockout',
  'qaqcloto.not_authorization':
    'These records do not authorize field work, lock removal, or equipment energization. Follow the site\'s approved authorization procedures.',
  'qaqcloto.tabs': 'View',
  'qaqcloto.tab.lockouts': 'Lockouts',
  'qaqcloto.tab.equipment': 'Equipment',
  'qaqcloto.tab.quality': 'Quality',
  'qaqcloto.section.lockouts': 'Lockout',
  'qaqcloto.section.quality': 'Quality',
  'qaqcloto.section.unavailable': 'This part could not be answered for ({reason}). No figures are shown for it.',
  // The link screen. What is typed here is an identifier, an address and the
  // NAME of a credential on the server -- never a credential.
  'qaqcloto.link.title': 'Link to SiteFocus: QAQC-LOTO',
  'qaqcloto.link.open': 'Link QA/QC project',
  'qaqcloto.link.make': 'Link this project',
  'qaqcloto.link.relink': 'Link it to a different project',
  'qaqcloto.link.intro':
    'Enter the QA/QC project ID and server details, then select Find project. Review the returned project before confirming the connection. Nothing is saved until you confirm.',
  'qaqcloto.link.field.project_id': 'QA/QC project ID',
  'qaqcloto.link.field.base_url': 'QA/QC server URL',
  'qaqcloto.link.field.base_url_hint':
    'Enter the HTTPS server address without a path. Example: https://qaqc.example.com',
  'qaqcloto.link.field.secret_name': 'Credential environment variable',
  'qaqcloto.link.field.secret_name_hint':
    'Enter the server environment variable that contains the credential. Do not enter the credential itself.',
  'qaqcloto.link.check': 'Find project',
  'qaqcloto.link.candidate_heading': 'What their application says that project is',
  'qaqcloto.link.confirm': 'Link this project',
  'qaqcloto.link.confirm_hint':
    'Linking asks them again at the moment it writes, and writes nothing if the code or the name has moved since this reading.',
  'qaqcloto.link.linked_heading': 'This project is linked',
  'qaqcloto.link.unlink': 'Remove the link',
  'qaqcloto.link.unlink_confirm':
    'The panel will say this project is not linked and will show no register at all. Nothing in SiteFocus: QAQC-LOTO changes.',
  'qaqcloto.link.credential_installed': '(installed on this machine)',
  'qaqcloto.link.credential_missing': '(not installed on this machine)',
  'qaqcloto.link.confirmed_at': 'Confirmed {when}.',
  'qaqcloto.link.written': 'The link was written.',
  'qaqcloto.link.not_refreshed':
    'This is the mapping as it was written. It could not be read back just now, which says nothing about the write.',
  'qaqcloto.link.none': 'Not set',
  // An attempt that was sent and neither accepted nor refused. It keeps the
  // destination it was sent to, so that trying again cannot quietly become a
  // different mapping because the form was edited in the meantime.
  'qaqcloto.link.outstanding': 'An earlier attempt to write this mapping has not been accounted for.',
  'qaqcloto.link.outstanding_sending': 'This mapping has been sent and has not been answered yet.',
  'qaqcloto.link.outstanding_body':
    'It was sent for {project} at {url}, using the credential named {secret}, and whether it was written is not known. Trying again sends that same mapping under the same key, so it cannot become a second, different link. What is typed in the form above is not what it would send, and nothing else can be read back, linked or unlinked from this screen until this attempt is settled one way or the other.',
  'qaqcloto.link.outstanding_retry': 'Try that same write again',
  'qaqcloto.link.outstanding_discard': 'Discard that attempt',
  'qaqcloto.state.not_linked': 'This project is not linked to SiteFocus: QAQC-LOTO',
  'qaqcloto.state.not_linked_body':
    'This project isn\'t linked to a QA/QC project yet. A project manager can set up the connection.',
  'qaqcloto.state.not_configured': 'This machine cannot read SiteFocus: QAQC-LOTO',
  'qaqcloto.state.not_configured_body':
    'The link is set and this machine has not been given what it needs to use it. An administrator installs it on the server.',
  'qaqcloto.state.credential_named': 'The credential it is looking for is called {name}.',
  'qaqcloto.state.unavailable': 'SiteFocus: QAQC-LOTO could not be read',
  'qaqcloto.state.never_nothing': 'Nothing is shown rather than an empty register.',
  'qaqcloto.state.retry_after': 'Try again in {seconds} seconds.',
  'qaqcloto.state.read_at': 'Last asked {when}.',
  'qaqcloto.state.correlation': 'Their reference for this request is {id}.',
  'qaqcloto.freshness.as_of': 'Their records as of {when}.',
  'qaqcloto.freshness.stale': 'This answer is old',
  'qaqcloto.freshness.stale_body':
    'Open SiteFocus: QAQC-LOTO before acting on it.',
  'qaqcloto.freshness.future': 'This answer is dated in the future',
  'qaqcloto.freshness.future_body':
    'It says it was read at {when}, which has not happened yet. Treat it as a fault and check the clocks.',
  'qaqcloto.freshness.unknown': 'This answer carries no usable time',
  'qaqcloto.freshness.unknown_body':
    'Every one of their payloads carries the moment they read their own records, and this one did not. Do not act on the figures.',
  'qaqcloto.incomplete': 'This is only part of the register',
  'qaqcloto.incomplete_body':
    'What is below is some of it. Open SiteFocus: QAQC-LOTO for the whole register.',
  'qaqcloto.counts_disagree':
    'Their breakdown does not add up to their own total, so something is missing from it. Open SiteFocus: QAQC-LOTO.',
  'qaqcloto.none_locked_out': 'Nothing is locked out on this project.',
  'qaqcloto.open_there': 'Open in SiteFocus: QAQC-LOTO',
  'qaqcloto.open_project': 'Open the project there',
  'qaqcloto.answered_by': 'Answered by {node}.',
  'qaqcloto.no_link': 'No link',
  'qaqcloto.coming_off': '(coming off)',
  'qaqcloto.nobody_assigned': 'Nobody assigned',
  'qaqcloto.unnamed_person': 'Person {id}, name not known',
  'qaqcloto.waiting_counts': '{keys} keys, {acks} acknowledgements',
  'qaqcloto.record_unreadable': 'Their record could not be read',
  'qaqcloto.lock_state_unknown': 'lock state not known',
  'qaqcloto.figure.unreadable_records':
    '{count} records SiteFocus: QAQC-LOTO could not read. Nothing is claimed about them, and that is not the same as equipment with no method recorded.',
  'qaqcloto.method_not_recorded': 'No lockout method recorded',
  'qaqcloto.point_state': '{position}, {locked}',
  'qaqcloto.locked': 'locked',
  'qaqcloto.not_locked': 'not locked',
  'qaqcloto.not_a_point': 'Not an isolation point',
  'qaqcloto.column.number': 'Number',
  'qaqcloto.column.name': 'Name',
  'qaqcloto.column.status': 'State',
  'qaqcloto.column.applied': 'Applied',
  'qaqcloto.column.carrying': 'Carrying it',
  'qaqcloto.column.waiting': 'Waiting',
  'qaqcloto.column.equipment': 'Equipment',
  'qaqcloto.column.method': 'Lockout method',
  'qaqcloto.column.point': 'Isolation point',
  'qaqcloto.column.changed': 'State changed',
  'qaqcloto.figure.live': '{count} locked out now',
  'qaqcloto.figure.removing': '{count} coming off',
  'qaqcloto.figure.keys': '{count} keys not returned',
  'qaqcloto.figure.acks': '{count} acknowledgements outstanding',
  'qaqcloto.figure.unknown_method': '{count} pieces of equipment with no lockout method recorded',
  'qaqcloto.figure.inspections_open': '{count} inspections open',
  'qaqcloto.figure.holds': '{count} holds open',
  'qaqcloto.figure.deficiencies': '{count} deficiencies open',
  // One lockout, opened from the list. Everything that would *do* anything to
  // it is a link into the application that owns it.
  'qaqcloto.plan.heading': 'One lockout',
  'qaqcloto.plan.back': 'Back to the lockout list',
  'qaqcloto.plan.open': 'Points',
  'qaqcloto.plan.applied': 'Applied {when}.',
  'qaqcloto.plan.no_applied_time':
    'No time recorded for when this was applied. That is not a statement that it never was.',
  'qaqcloto.plan.carried_by': 'Carried by {who}.',
  'qaqcloto.plan.keys': 'Keys not returned: {keys}.',
  'qaqcloto.plan.no_keys': 'No keys outstanding.',
  'qaqcloto.plan.boundary': 'What their trace made of the boundary',
  'qaqcloto.plan.deenergized': '{count} de-energized',
  'qaqcloto.plan.still_energized': '{count} still energized',
  'qaqcloto.plan.unknown': '{count} their trace could not decide about',
  'qaqcloto.plan.unknown_body':
    'Their trace could not place that equipment on either side of the boundary. That is not the same as equipment being safe, and it is not a figure to work from.',
  'qaqcloto.plan.points': 'Isolation points',
  'qaqcloto.plan.no_points':
    'They record no isolation points on this lockout. That is their record of the plan, not a statement that anything is isolated.',
  'qaqcloto.plan.points_disagree':
    'They count {counted} points on this lockout and sent {sent}. Both numbers are shown and neither is preferred.',
  'qaqcloto.plan.unnamed_equipment': 'Equipment {id}, name not known',
  'qaqcloto.plan.nobody_recorded': 'Nobody recorded',
  // Two columns because their contract answers two different questions, and
  // one column headed 'Locks' could only have answered one of them while
  // looking like it answered both.
  'qaqcloto.column.locks_recorded': 'Locks this plan recorded',
  'qaqcloto.column.locks_installed': 'Isolation record now',
  'qaqcloto.plan.none_recorded': 'This plan has not recorded a lock here.',
  'qaqcloto.plan.locks_not_answered':
    'The revision CM read does not report what this plan recorded here.',
  'qaqcloto.plan.installed_not_answered':
    'The revision CM read does not report the current isolation record for this point.',
  'qaqcloto.plan.installed_none_listed':
    'Attributed to this plan, with no lock listed.',
  'qaqcloto.plan.installed_not_attributed':
    'The record attributes no lock of this plan here. The lockout may have been removed, or another plan may hold this point.',
  'qaqcloto.plan.installed_no_record':
    'No record exists for this point. That is not the same as no locks installed.',
  'qaqcloto.plan.installed_unrecorded':
    'The record attributes this point to this plan and lists no locks. Nobody recorded which.',
  'qaqcloto.plan.installed_unreadable':
    'CM could not read this point record: {field}.',
  'qaqcloto.plan.locks_are_records':
    'Both lock columns are records kept by the other application. The left one is what this plan recorded and it stays after the locks come off. Neither is an observation of the equipment, and neither authorises field work.',
  'qaqcloto.column.applied_by': 'Applied by',
  'qaqcloto.breakdown.inspections': 'Inspections by state',
  'qaqcloto.breakdown.severity': 'Deficiencies by severity',
  // Their refusal codes, each with its own sentence. The operator action
  // differs between them, which is why they are not collapsed into one.
  'qaqcloto.reason.unknown': 'The reason was not one we recognise.',
  'qaqcloto.reason.timeout': 'They did not answer in time.',
  'qaqcloto.reason.unreachable': 'Their server could not be reached from this machine.',
  'qaqcloto.reason.invalid_payload': 'Their answer was not in a shape we can read.',
  'qaqcloto.reason.instance_mismatch':
    'The answer came from a different SiteFocus: QAQC-LOTO than the one this project was linked against. Do not act on it.',
  'qaqcloto.reason.project_mismatch':
    'The records came back under a different project than the one this project was linked against. Do not act on them.',
  'qaqcloto.reason.project_unverified':
    'Their answer does not say which project these records are from, so nothing is shown. This is not an empty register.',
  'qaqcloto.reason.unexpected_status': 'They refused in a way their contract does not describe.',
  'qaqcloto.reason.unauthenticated': 'This machine has no credential they accept.',
  'qaqcloto.reason.credential_revoked': 'Their credential for this project has been revoked.',
  'qaqcloto.reason.credential_expired': 'Their credential for this project has expired.',
  'qaqcloto.reason.forbidden_scope': 'The credential is not allowed to read that part of their system.',
  'qaqcloto.reason.project_not_found': 'They have no such project, or the credential may not read it.',
  'qaqcloto.reason.not_found': 'They have no such record.',
  'qaqcloto.reason.unsupported_version': 'They no longer serve the version of their interface this build asks for.',
  'qaqcloto.reason.cursor_invalid': 'The place we were reading from is no longer valid.',
  'qaqcloto.reason.bad_request': 'They refused the request as malformed.',
  'qaqcloto.reason.method_not_allowed': 'Their interface is read-only and refused the request.',
  'qaqcloto.reason.rate_limited': 'They are being asked too often.',
  'qaqcloto.reason.module_unavailable': 'That part of their system is not running on the node that answered.',
  'qaqcloto.reason.store_unreadable': 'They cannot read their own records for that resource.',
  'qaqcloto.reason.internal_error': 'Something failed on their side.',
  'qaqcloto.reason.cursor_loop': 'They kept handing back the same page.',
  'qaqcloto.reason.page_budget': 'The register is longer than one screen read will walk.',
  'workforce.index.trend_alt': '{count} daily readings, each out of 100, oldest on the left.',
  'scope.title': 'Scope divider and features of work',
  'scope.subtitle':
    'The division list every register picks from, who answers for each part of it, the definable features of work, and what nobody answers for yet.',
  'scope.tab.divisions': 'Divisions and scopes',
  'scope.tab.features': 'Features of work',
  'scope.tab.coverage': 'Coverage',
  'scope.actions': 'Actions',
  'scope.state.retired': 'Retired',
  'scope.masterformat.add': 'Add the MasterFormat divisions',
  'scope.masterformat.note':
    'Adds every MasterFormat division this project does not have yet. Divisions already on the list are left exactly as they are.',
  'scope.masterformat.done': '{added} divisions added; {kept} already on the list were left as they were.',
  'scope.divisions.none': 'This project has no divisions yet.',
  'scope.divisions.number': 'Number',
  'scope.divisions.title': 'Title',
  'scope.divisions.trade': 'Trade',
  'scope.divisions.scopes': 'Scopes',
  'scope.divisions.undivided': 'Not divided',
  'scope.divisions.split_summary': '{count} scopes, {covered} with a contractor',
  'scope.divisions.add': 'Add a division',
  'scope.divisions.add_submit': 'Add division',
  'scope.splits.none': 'This division is not divided into scopes yet.',
  'scope.splits.code': 'Code',
  'scope.splits.name': 'Name',
  'scope.splits.responsible': 'Responsible contractor',
  'scope.splits.nobody': 'Nobody yet',
  'scope.splits.version': 'Version',
  'scope.splits.history': 'History',
  'scope.splits.edit': 'Edit',
  'scope.splits.editing': 'Editing {code}, version {version}',
  'scope.splits.reason': 'Why',
  'scope.splits.changed': 'Changed by',
  'scope.splits.save': 'Save as a new version',
  'scope.splits.retire': 'Retire this scope',
  'scope.splits.restore': 'Restore this scope',
  'scope.splits.moved':
    'Somebody saved version {current} of this scope while you were editing version {sent}. Saving sends version {sent}, which will be refused; close this form and open the latest version to make your change.',
  'scope.splits.add': 'Divide this division',
  'scope.splits.add_submit': 'Add scope',
  'scope.features.none': 'This project has no features of work yet.',
  'scope.features.code': 'Code',
  'scope.features.title': 'Title',
  'scope.features.division': 'Division',
  'scope.features.split': 'Scope',
  'scope.features.section': 'Specification section',
  'scope.features.section_unread': 'Not shown',
  'scope.features.sections_unread':
    'The specification sections could not be read, so none can be chosen or shown here. The features of work themselves are as listed.',
  'scope.features.add': 'Add a feature of work',
  'scope.features.add_submit': 'Add feature',
  'scope.features.retire': 'Retire',
  'scope.coverage.divisions_without_contractor': 'Divisions nobody answers for',
  'scope.coverage.features_without_scope': 'Features of work in no division',
  'scope.coverage.sections_without_feature': 'Specification sections no feature of work names',
  'scope.coverage.nothing': 'None.',
  'scope.coverage.not_answered': 'Not answered here yet',
  'scope.held.sending': 'Sending {name}…',
  'scope.held.unsettled': '{name} was sent and no answer came back, so it may or may not have been saved.',
  'scope.held.hint':
    'Send it again to find out. It goes with the same key and the same contents, so if it was saved the first time it is not saved twice.',
  'scope.held.send_again': 'Send again',
  'repository.title': 'Document repository',
  'repository.subtitle':
    'Every document on this job that is not a drawing or a specification, filed in folders. Each upload is kept as a version.',
  'repository.folders': 'Folders',
  'repository.documents': 'Documents',
  'repository.index': 'Index',
  'repository.top': 'All folders',
  'repository.path': 'Where this is',
  'repository.private': 'Private',
  'repository.private_note': 'This folder is private. Only the people named on it, and the repository administrators, can see it.',
  'repository.no_folders': 'No folders here yet.',
  'repository.no_documents': 'No documents here yet.',
  'repository.folder.new': 'New folder',
  'repository.folder.make': 'Make folder',
  'repository.search': 'Search names',
  'repository.search.submit': 'Search',
  'repository.search.covers': 'Searches document names and the names of the folders they are in. The text inside documents is not searched.',
  'repository.search.here': 'Search in this folder',
  'repository.scope': 'Where to look',
  'repository.scope.project': 'The whole project',
  'repository.column.folder': 'Folder',
  'repository.column.name': 'Name',
  'repository.column.type': 'Type',
  'repository.column.version': 'Version',
  'repository.column.revision': 'Revision',
  'repository.column.file': 'File',
  'repository.column.size': 'Size',
  'repository.column.updated': 'Uploaded',
  'repository.column.actions': 'Actions',
  'repository.version_of': '{number} of {count}',
  'repository.version_count': '{count} versions',
  'repository.version_count_one': '{count} version',
  'repository.current': 'current',
  'repository.history': 'Versions',
  'repository.download': 'Download',
  'repository.size.bytes': '{count} bytes',
  'repository.size.kb': '{count} KB',
  'repository.size.mb': '{count} MB',
  'repository.upload.title': 'Upload a document',
  'repository.upload.file': 'File',
  'repository.upload.type': 'Kind of document (optional)',
  'repository.upload.revision': 'Revision shown on the document (optional)',
  'repository.upload.note': 'Note (optional)',
  'repository.upload.submit': 'Upload',
  'repository.upload.sending': 'Uploading…',
  'repository.upload.same_name': 'A file with the same name as a document in this folder is added to that document as a new version.',
  'repository.version.title': 'Upload a new version',
  'repository.access.title': 'Who can open this folder',
  'repository.access.private': 'Private',
  'repository.access.people': 'People who can open it',
  'repository.access.people_note':
    "You are always included in this folder's list. Restrictions from private folders above still apply. Private folders inside this one can restrict access further. Repository administrators retain access.",
  'repository.access.inherited': 'A folder above this one is private, so only people on its list can open this one as well.',
  'repository.access.save': 'Save',
  'repository.withheld': 'This is no longer shown, because the last time it was read the answer was that you cannot see it.',
  'repository.withheld.again': 'Read it again',
  'repository.unsettled.folder':
    'The new folder was sent and no answer came back, so it may or may not have been made.',
  'repository.unsettled.access':
    'The change to who can open this folder was sent and no answer came back, so it may or may not have been saved.',
  'repository.unsettled.upload':
    'The upload was sent and no answer came back, so it may or may not have been filed.',
  'repository.unsettled.hint':
    'Sending it again sends exactly the same thing under the same key, so it cannot be done twice and nothing written since is undone. The fields stay shut until it is answered.',
  'repository.unsettled.folder_retry': 'Send the same folder again',
  'repository.unsettled.access_retry': 'Send the same change again',
  'repository.unsettled.upload_retry': 'Send the same upload again',
  'repository.unsettled.check': 'Read the folder again',
  'repository.tree': 'Folder tree',
  'repository.tree.unread': 'The folders could not be read.',
  'repository.toolbar': 'Folder actions',
  'repository.toolbar.new_folder': 'New folder…',
  'repository.toolbar.upload': 'Upload…',
  'repository.toolbar.new_version': 'New version…',
  'repository.list': 'Contents',
  'repository.list.empty': 'This folder is empty.',
  'repository.list.folder': 'Folder',
  'repository.column.modified': 'Modified',
  'repository.column.versions': 'Versions',
  'repository.sort': 'Sort by {column}',
  'repository.details': 'Details',
  'repository.details.kind': 'Kind',
  'repository.details.where': 'Folder',
  'repository.details.current': 'Current version',
  'repository.details.for': 'Details of {name}',
  'repository.details.close': 'Close the details',
  'repository.details.open': 'Open',
  'repository.preview': 'Preview',
  'repository.preview.show': 'Show a preview',
  'repository.preview.of': 'Preview of {name}',
  'repository.preview.none': 'There is no preview for this kind of file. Download it to open it.',
  'repository.drop.hint': 'Drop files here to upload them to this folder, or use Upload… to pick several. Up to {count} at a time.',
  'repository.drop.pick': 'Files to upload',
  'repository.drop.title': 'Uploads',
  'repository.drop.done': 'Uploaded',
  'repository.drop.refused': 'Not uploaded',
  'repository.drop.waiting': 'Waiting',
  'repository.drop.over_limit': 'One drop takes up to {count} files. These were not sent:',
  'repository.drop.folders': 'Folders cannot be dropped here. Open the folder and drop the files inside it, after making a folder of the same name.',
  'models.title': 'Models',
  'models.subtitle':
    'Navisworks and Revit models for this job. Each upload is kept as a version, and the newest is at the top.',
  'models.online_only':
    'Models open online only. This server cannot reach Autodesk, which converts and draws them; open SiteFocus online to upload or view one.',
  'models.none': 'No models have been uploaded to this job yet.',
  'models.name': 'Name',
  'models.file': 'File',
  'models.version': 'Version',
  'models.version_number': 'V{n}',
  'models.size': 'Size',
  'models.version_added': 'Version added',
  'models.no_versions': 'No versions',
  'models.download': 'Download',
  'models.download_named': 'Download {file}',
  'models.earlier.show': 'Earlier versions ({n})',
  'models.earlier.hide': 'Hide earlier versions',
  'models.state': 'State',
  'models.actions': 'Actions',
  'models.open': 'Open',
  'models.retry': 'Try again',
  'models.delete': 'Delete',
  'models.delete_model': 'Delete model',
  'models.delete.confirm_model': 'Delete the model {name} and these versions?',
  'models.delete.confirm_version': 'Delete this version of {name}?',
  'models.delete.recoverable': 'The files are kept and an administrator can restore it.',
  'models.delete.go': 'Delete',
  'models.delete.cancel': 'Keep it',
  'models.delete.version_line': '{file}, uploaded {when}',
  'models.delete.unanswered_model':
    'The deletion of the model {name} was sent and no answer came back. Sending it again deletes nothing twice.',
  'models.delete.unanswered_version':
    'The deletion of {file} from {name} was sent and no answer came back. Sending it again deletes nothing twice.',
  'models.delete.send_again': 'Send again',
  'models.delete.refused_model':
    'The deletion of the model {name} was sent and no answer came back, and sending it again was refused. It may already have happened. Check again to hear what became of it: nothing is deleted twice.',
  'models.delete.refused_version':
    'The deletion of {file} from {name} was sent and no answer came back, and sending it again was refused. It may already have happened. Check again to hear what became of it: nothing is deleted twice.',
  'models.delete.check_again': 'Check again',
  'models.state.queued': 'Queued',
  'models.state.sending': 'Sending to Autodesk',
  'models.state.converting': 'Converting',
  'models.state.ready': 'Ready',
  'models.state.failed': 'Failed',
  'models.upload.title': 'Upload a model',
  'models.sorting.source': 'Whose model',
  'models.sorting.source.none': 'Not chosen',
  'models.sorting.source.design': 'Design model',
  'models.sorting.source.contractor': 'Contractor model',
  'models.sorting.scope': 'What it covers',
  'models.sorting.scope.none': 'Not chosen',
  'models.sorting.scope.discipline': 'One discipline or trade',
  'models.sorting.scope.integrated': 'Integrated model',
  'models.sorting.discipline': 'Discipline or trade',
  'models.sorting.edit': 'Sort',
  'models.sorting.save': 'Save',
  'models.sorting.cancel': 'Cancel',
  'models.sorting.unanswered':
    'The sorting of {name} was sent and no answer came back. Sending it again sorts it once, as it was sent.',
  'models.sorting.send_again': 'Send again',
  'models.sorting.refused':
    'The sorting of {name} was sent and no answer came back, and sending it again was refused. It may already have happened. Check again to hear what became of it: it is sorted once, as it was sent.',
  'models.sorting.check_again': 'Check again',
  'models.group.design': 'Design models',
  'models.group.contractor': 'Contractor models',
  'models.group.unsorted': 'Not sorted yet',
  'models.group.integrated': 'Integrated',
  'models.group.no_scope': 'Coverage not chosen',
  'models.upload.target': 'Add to',
  'models.upload.new_model': 'Create a New Model',
  'models.upload.name': 'Model name',
  'models.upload.file': 'Model file',
  'models.upload.file_note':
    'A Navisworks NWD or NWC file, or a Revit RVT file, of 2 GB or less. Save an NWF as an NWD in Navisworks first. A Revit file is shown without the models it links to.',
  'models.upload.file_kept': 'To send: {name}',
  'models.upload.sending': 'Sent {sent} of {total} MB',
  'models.upload.submit': 'Upload',
  'models.upload.outcome_unknown':
    'The connection dropped before the answer came. If the file arrived, it is in the list.',
  'models.viewer.label': 'The model, drawn by Autodesk',
  'models.viewer.unchanged':
    'This is the model as it was saved. Nothing done here changes the model or the file.',
  'models.viewer.unreachable':
    'The Autodesk viewer could not be loaded. Models open online only; check this computer can reach Autodesk.',
  'models.viewer.load_failed': 'Autodesk could not open this model.',
  'schedule.title': 'Schedule',
  'schedule.empty.importer':
    'No schedule has been imported for this project. Import the export from your scheduling program to start.',
  'schedule.empty.reader': 'No schedule has been imported for this project yet.',
  'schedule.no_current': 'No issue of the schedule is current. Choose one from the list.',
  'schedule.version.label': 'Issue',
  'schedule.version.selector': 'Schedule version',
  'schedule.version.issue': 'Issue {sequence}',
  'schedule.version.draft': 'Draft',
  'schedule.version.current': 'Current',
  'schedule.version.imported': 'Imported {when}',
  'schedule.version.data_date': 'data date {date}',
  'schedule.version.make_current': 'Make this the current schedule',
  'schedule.version.unresolved':
    'Making {label} the current schedule has had no answer. Sending it again sends the same choice, so it cannot go over a choice made since.',
  'schedule.version.send_again': 'Send that choice again',
  'schedule.filter.critical': 'Critical path only',
  'schedule.filter.lookahead': 'Look ahead',
  'schedule.filter.lookahead_weeks': 'Weeks',
  'schedule.filter.lookahead_from': 'Starting',
  'schedule.filter.lookahead_window':
    'Activities planned to be under way from {from} to {to}, chosen and drawn by their planned dates. Work with no planned dates, or planned to finish before {from}, is not listed here even if it is unfinished.',
  'schedule.filter.lookahead_invalid':
    'Choose a start date and a whole number of weeks from 1 to {most}. Nothing is listed until they make a look ahead.',
  'schedule.filter.lookahead_enter':
    'Enter a valid start date and a whole number from 1 to {most} weeks to show activities.',
  'schedule.float.computed':
    'This export did not carry float, so float and the critical path were worked out here from its dates and links.',
  'schedule.float.calculated':
    'Calculated by SiteFocus from imported dates and dependencies; float was not included in the source file.',
  'schedule.count': '{count} activities',
  'schedule.count_one': '1 activity',
  'schedule.column.code': 'ID',
  'schedule.column.name': 'Activity',
  'schedule.column.start': 'Start',
  'schedule.column.finish': 'Finish',
  'schedule.column.duration': 'Duration',
  'schedule.column.complete': 'Complete',
  'schedule.column.float': 'Total float',
  'schedule.column.bars': 'Timeline',
  'schedule.days': '{days} d',
  'schedule.critical': 'Critical',
  'schedule.more': 'Show more activities',
  'schedule.back': 'Schedule',
  'schedule.lookahead.open': 'Look ahead',
  'schedule.lookahead.title': 'Superintendent look ahead',
  'schedule.field.title': 'Field look ahead',
  'schedule.views': 'Schedules',
  'schedule.view.cpm': 'CPM',
  'schedule.view.field': 'Field',
  'schedule.field.task': 'Task description',
  'schedule.field.subcontractor': 'Subcontractor',
  'schedule.field.day_note': 'Whole site',
  'schedule.field.no_tasks': 'No tasks in this section.',
  'schedule.field.cpm_ref': 'Linked to CPM activity {code} {name}, for reference only',
  'schedule.field.cpm_gone': '{code}: no longer in the CPM schedule',
  'schedule.field.cpm_critical': 'CPM critical',
  'schedule.field.back_week': 'Back 1 week',
  'schedule.field.this_week': 'This week',
  'schedule.field.forward_week': 'Forward 1 week',
  'schedule.field.window': '{from} to {to}',
  'schedule.field.day.mon': 'M',
  'schedule.field.day.tue': 'T',
  'schedule.field.day.wed': 'W',
  'schedule.field.day.thu': 'R',
  'schedule.field.day.fri': 'F',
  'schedule.field.day.sat': 'S',
  'schedule.field.day.sun': 'S',
  'schedule.field.sub':
    'Tasks typed on site, day by day, with the subcontractor doing each one. A task may name a CPM activity for reference; nothing here changes the CPM schedule.',
  'schedule.field.empty': 'No sections yet. Add a section, then the tasks under it.',
  'schedule.field.cell': '{task}, {day}: {mark}',
  'schedule.field.day_editor': 'Change a day',
  'schedule.field.note': 'Note',
  'schedule.field.mark_x': 'X: happens this day',
  'schedule.field.mark_c': 'C: critical this day',
  'schedule.field.mark_none': 'Nothing',
  'schedule.field.no_subcontractor': 'None',
  'schedule.field.type_vendor': 'Type a vendor not in the directory',
  'schedule.field.vendor': 'Vendor',
  'schedule.field.cpm': 'CPM activity (reference only)',
  'schedule.field.no_cpm': 'None',
  'schedule.field.section': 'Section',
  'schedule.field.add_task': 'Add task',
  'schedule.field.edit_task': 'Change task',
  'schedule.field.save': 'Save',
  'schedule.field.remove_task': 'Remove task',
  'schedule.field.new_section': 'New section',
  'schedule.field.add_section': 'Add section',
  'schedule.field.site_note_day': 'Day',
  'schedule.field.save_site_note': 'Save whole-site note',
  'schedule.field.legend': 'Key',
  'schedule.field.legend_x': 'Happens that day',
  'schedule.field.legend_c': 'Critical that day',
  // A field change sent and never answered. Each names the thing whose
  // outcome is unknown, and each hint says why sending it again is safe, why
  // nothing else can be sent for it meanwhile, and that what is typed since
  // is kept for afterwards.
  'schedule.field.unsettled.day':
    "The answer to this day's change did not arrive, so whether it was saved is not known here.",
  'schedule.field.unsettled.day_hint':
    'Sending it again carries the same key and exactly what was sent, so a change that did land is handed back rather than made twice. X, C and Nothing wait until that is settled, because another change sent now could be read as this one. What you type in the note is kept for afterwards.',
  'schedule.field.unsettled.task':
    'The answer to this change to the task did not arrive, so whether it was saved is not known here.',
  'schedule.field.unsettled.task_hint':
    'Sending it again carries the same key and exactly what was sent, so a change that did land is handed back rather than made twice. Save and Remove task wait until that is settled. What you type in the boxes is kept for afterwards.',
  'schedule.field.unsettled.task_create':
    'The answer to adding the task did not arrive, so whether it was added is not known here.',
  'schedule.field.unsettled.task_create_hint':
    'Sending it again carries the same key and exactly what was sent, so a task that was added is handed back rather than added twice. The next task can be added once this one is settled; what you type in the boxes is kept.',
  'schedule.field.unsettled.section_create':
    'The answer to adding the section did not arrive, so whether it was added is not known here.',
  'schedule.field.unsettled.section_create_hint':
    'Sending it again carries the same key and exactly what was sent, so a section that was added is handed back rather than added twice. The next section can be added once this one is settled; what you type in the box is kept.',
  'schedule.field.unsettled.site_note':
    "The answer to this day's whole-site note did not arrive, so whether it was saved is not known here.",
  'schedule.field.unsettled.site_note_hint':
    "Sending it again carries the same key and exactly what was sent, so a note that did land is handed back rather than saved twice. Another note for that day waits until this one is settled; what you type in the box is kept.",
  'schedule.field.unsettled.retry': 'Send the same change again',
  'schedule.field.unsettled.check': 'Read the look ahead again',
  'schedule.lookahead.window':
    'Activities under way or planned from {from} to {to}, by their actual dates where the schedule records them and their planned dates where it does not, and any activity with a subtask in those days. Work with no dates, or whose dates end before {from}, is not listed here even if it is unfinished.',
  'schedule.lookahead.sub':
    'Every activity in the weeks chosen, with who it is given to and the subtasks planned under it. Not shown to customers or trades unless granted.',
  'schedule.lookahead.intro': 'Shows assigned activities and subtasks for the selected dates.',
  'schedule.lookahead.dates_help': 'Which dates are shown',
  'schedule.lookahead.contractor': 'My look ahead',
  'schedule.lookahead.contractor_sub':
    'The activities given to your company or to you, and your company’s subtasks under them.',
  'schedule.lookahead.company': 'Company',
  'schedule.lookahead.group': 'Group',
  'schedule.lookahead.person': 'Person',
  'schedule.lookahead.all': 'All',
  'schedule.lookahead.none': '—',
  'schedule.lookahead.mine': 'Mine',
  'schedule.lookahead.subtasks': 'Subtasks',
  'schedule.lookahead.subtask_count': '{count} subtasks',
  'schedule.lookahead.subtask_count_one': '1 subtask',
  'schedule.lookahead.subtask': 'Subtask',
  'schedule.lookahead.no_subtasks': 'No subtasks planned under this activity.',
  'schedule.lookahead.add_subtask': 'Add subtask',
  'schedule.lookahead.duration_days': 'Working days',
  'schedule.lookahead.subtask_dates': '{start} – {finish} ({days} working days)',
  'schedule.lookahead.superintendent_only': 'superintendent only',
  'schedule.lookahead.kept_to_superintendent': 'Kept to the superintendent’s view.',
  'schedule.lookahead.remove': 'Remove',
  'schedule.lookahead.edit': 'Edit',
  'schedule.lookahead.shared_with': 'Shared with',
  'schedule.lookahead.shared_line': 'shared with {company}',
  'schedule.lookahead.late': 'Ends after the activity',
  'schedule.lookahead.nothing_yours':
    'Nothing in these weeks is given to your company or to you.',
  'schedule.lookahead.company_of': 'Company for {code}',
  'schedule.lookahead.group_of': 'Group for {code}',
  'schedule.lookahead.person_of': 'Person for {code}',
  'schedule.lookahead.suggested_file': 'From the file',
  'schedule.lookahead.suggested_ai': 'Suggested by AI',
  'schedule.lookahead.confirm': 'Confirm',
  'schedule.lookahead.fill': 'Fill companies from the file',
  'schedule.lookahead.filled':
    'Matched by name: {by_name}. Suggested by AI: {by_ai}. Confirm each before the trade sees it.',
  'schedule.lookahead.unmatched': 'Not matched: {texts}.',
  'schedule.lookahead.leads': 'Company leads',
  'schedule.lookahead.lead_line': '{company}: {person}',
  'schedule.lookahead.hand_over': 'Hand the lead of {company} to',
  'schedule.lookahead.hand_over_to': 'Hand over to…',
  'schedule.lookahead.new_group': 'New group',
  'schedule.lookahead.add_group': 'Add group',
  'schedule.group.engineering': 'Engineering',
  'schedule.group.construction': 'Construction',
  'schedule.group.preconstruction': 'Preconstruction',
  'schedule.error.ai_off_for_project':
    'AI is switched off for this project, so only exact company names were matched.',
  'schedule.error.ai_unavailable':
    'The AI could not be reached, so only exact company names were matched.',
  'schedule.detail.actual': 'Actual',
  // The detail says where each pair of dates comes from (D1-07): the planned
  // pair is the file's, the early and late pairs are the critical-path dates.
  'schedule.detail.planned_start': 'Planned start (imported)',
  'schedule.detail.planned_finish': 'Planned finish (imported)',
  'schedule.detail.early': 'Early start – finish (critical path)',
  'schedule.detail.late': 'Late start – finish (critical path)',
  'schedule.detail.dates_explain':
    'Planned dates are the ones in the imported file. Early and late dates are the critical-path dates: the earliest and latest this activity can start and finish without moving the end of the job. They come from the file, or are worked out here from the links and durations when the file carried none.',
  'schedule.relation.FS': 'Finish-to-start (FS)',
  'schedule.relation.SS': 'Start-to-start (SS)',
  'schedule.relation.FF': 'Finish-to-finish (FF)',
  'schedule.relation.SF': 'Start-to-finish (SF)',
  'schedule.detail.trade': 'Trade',
  'schedule.detail.constraint': 'Constraint',
  'schedule.detail.predecessors': 'Predecessors',
  'schedule.detail.successors': 'Successors',
  'schedule.detail.link': 'Link',
  'schedule.detail.none': 'None.',
  'schedule.import.open': 'Import schedule',
  'schedule.import.formats':
    'Microsoft Project XML, Primavera P6 XER or XML, or a CSV export. The file is kept as an issue; nothing earlier is replaced.',
  'schedule.import.file': 'Schedule file',
  'schedule.import.kind': 'Import as',
  'schedule.import.kind.issued': 'The current schedule',
  'schedule.import.kind.draft': 'A draft beside it',
  'schedule.import.note': 'Note',
  'schedule.import.submit': 'Import',
  'schedule.import.sending': 'Importing…',
  'schedule.import.no_file': 'Choose a file to import.',
  'schedule.import.file_kept': 'To import: {name}',
  'schedule.import.unresolved':
    'The import of {name} has had no answer. Import sends the same request again, as it was sent, so it cannot import the file twice. To import something else instead, check the imports below first: the earlier one may be there.',
  'schedule.import.start_new': 'Start a new import instead',
  'schedule.import.unreadable':
    'The file could not be read as a schedule. Nothing was changed. Check it is an export from the scheduling program.',
  'schedule.imports.count': '{count} imports',
  'schedule.imports.count_one': '1 import',
  'schedule.imports.when': 'Imported',
  'schedule.imports.file': 'File',
  'schedule.imports.outcome': 'What changed',
  'schedule.imports.failed': 'Not imported',
  'schedule.imports.changed': '{added} added, {removed} removed, {moved} moved, {redated} re-dated',
  'fieldreport.title': 'Field report',
  'fieldreport.open_today': 'Open today',
  'fieldreport.read_only': 'You can read the field reports on this project but not write them.',
  'fieldreport.count': '{count} reports',
  'fieldreport.count_one': '1 report',
  'fieldreport.column.date': 'Date',
  'fieldreport.column.number': 'No.',
  'fieldreport.column.title': 'Title',
  'fieldreport.column.prepared_by': 'Prepared by',
  'fieldreport.column.walk': 'Walk stops',
  'fieldreport.column.issued': 'Issued copy',
  'fieldreport.issued.yes': 'Filed',
  'fieldreport.issued.no': 'None',
  'fieldreport.back': 'All field reports',
  'fieldreport.not_here': 'This report is not on this project.',
  'fieldreport.report_title': 'Field report for {date}, No. {number}',
  'fieldreport.export.pdf': 'Download as PDF',
  'fieldreport.export.docx': 'Download as Word',
  'fieldreport.cover.title': 'Cover',
  'fieldreport.cover.prepared_by': 'Prepared by',
  'fieldreport.cover.present_at_site': 'Present at site',
  'fieldreport.cover.weather': 'Weather',
  'fieldreport.cover.summary': 'Executive summary',
  'fieldreport.kpi.day_no': 'Day number',
  'fieldreport.kpi.days_since_incident': 'Days since the last recordable incident',
  'fieldreport.sections.title': 'Sections in this report',
  'fieldreport.sections.hint':
    'Which sections the report prints. A section switched off is left out of the PDF and the Word file, and its form is put away here; what was typed into it is kept.',
  'fieldreport.section.kpi': 'Project KPIs',
  'fieldreport.section.exec': '1. Executive Summary',
  'fieldreport.section.manpower': '2. Manpower',
  'fieldreport.section.charts': 'Manpower charts',
  'fieldreport.section.work': '3. Work Completed Today',
  'fieldreport.section.safety': '4. Safety Summary',
  'fieldreport.section.qaqc': '5. Quality Control (QA/QC)',
  'fieldreport.section.materials': '6. Materials & Deliveries',
  'fieldreport.section.lookahead': '7. Look-Ahead (Next 3-7 Days)',
  'fieldreport.section.schedule': 'Schedule (four-week window)',
  'fieldreport.section.dailylog': 'Daily Log (from the project\'s daily log)',
  'fieldreport.section.photos': '8. Photos Attached (observations)',
  'fieldreport.section.signatures': 'Signatures',
  'fieldreport.section.schedule_appendix': 'Appendix: activities on the report date',
  'fieldreport.walk.title': 'Site walk',
  'fieldreport.walk.none': 'No stops recorded on the walk.',
  'fieldreport.walk.photos': '{count} photographs',
  'fieldreport.walk.photos_one': '1 photograph',
  'fieldreport.photo.alt': 'Photograph {n} at stop {stop}: {location}',
  'fieldreport.photo.is_360': '360° photograph',
  'fieldreport.photo.loading': 'Loading the photograph…',
  'fieldreport.photo.unavailable': 'This photograph could not be loaded.',
  'fieldreport.photo.again': 'Load it again',
  'fieldreport.photo.add_title': 'Add a photograph to stop {n}',
  'fieldreport.photo.file': 'Photograph',
  'fieldreport.photo.mark_360': 'It is a 360° photograph',
  'fieldreport.photo.add_submit': 'Add the photograph',
  'fieldreport.photo.no_remove': 'A photograph added to a stop cannot be taken off it or replaced from this screen.',
  'fieldreport.unsettled.add_photo': 'We do not know whether the photograph you added was recorded.',
  'fieldreport.unsettled.add_photo_hint':
    'Send it again to be sure: the same file goes under the same key, so it is not added twice. Another photograph can be chosen once it is settled.',
  'fieldreport.unsettled.add_photo_retry': 'Send that photograph again',
  'fieldreport.photo.orphan_title': 'The photograph {file}, added to a stop that is no longer on the walk',
  'fieldreport.unsettled.add_photo_orphan':
    'We do not know whether this photograph was recorded before its stop was taken off the walk.',
  'fieldreport.unsettled.add_photo_orphan_hint':
    'Sending it again sends the same file, with the same 360 mark, under the same key, to the same stop. It is not put on another stop and the stop is not put back on the walk.',
  'fieldreport.photo.orphan_recorded':
    'The photograph {file} was recorded on its stop before the stop was taken off the walk. The report keeps the stop and its photographs, marked removed, and no longer prints them.',
  'fieldreport.photo.orphan_not_recorded':
    'The photograph {file} was not recorded: its stop was taken off the walk before it arrived. Nothing was added.',
  'fieldreport.photo.orphan_done': 'Done',
  'fieldreport.walk.stop_heading': 'Stop {n}: {location}',
  'fieldreport.walk.remove': 'Remove stop {n}',
  'fieldreport.walk.remove_confirm':
    'Remove stop {n} from the walk? The report keeps it, marked removed, and no longer prints it.',
  'fieldreport.walk.remove_yes': 'Remove stop {n}',
  'fieldreport.walk.remove_no': 'Keep it',
  'fieldreport.walk.add_title': 'Add a stop',
  'fieldreport.walk.add_submit': 'Add the stop',
  'fieldreport.field.stop.location': 'Location',
  'fieldreport.field.stop.sheet_ref': 'Sheet',
  'fieldreport.field.stop.note': 'What was seen',
  'fieldreport.unsettled.add_stop': 'We do not know whether the stop you added was recorded.',
  'fieldreport.unsettled.add_stop_hint':
    'Send it again to be sure: it goes exactly as it was sent the first time, under the same key, so the stop is not added twice. The fields stay shut until it is settled.',
  'fieldreport.unsettled.add_stop_retry': 'Send that stop again',
  'fieldreport.unsettled.remove_stop': 'We do not know whether the stop you removed was recorded as removed.',
  'fieldreport.unsettled.remove_stop_hint':
    'Send it again to be sure: it goes exactly as it was sent the first time, under the same key, so it is recorded once.',
  'fieldreport.unsettled.remove_stop_retry': 'Send that removal again',
  'fieldreport.issued.title': 'Issued copies',
  'fieldreport.issued.none': 'No issued copy has been filed.',
  'fieldreport.issued.current': 'the issued copy',
  'fieldreport.dailylog.title': 'Daily log for this date',
  'fieldreport.dailylog.none': 'There is no daily log for this date.',
  'fieldreport.dailylog.signed': 'The daily log for this date is signed; the report prints what it issued.',
  'fieldreport.dailylog.open': 'The daily log for this date is open; the report prints its entries as they stand.',
  'fieldreport.report_read_only': 'You can read this report but not write it.',
  'fieldreport.later_save':
    'Each part below is saved whole. If somebody else saves the same part after you, their save replaces yours: the report keeps no earlier version of it.',
  'fieldreport.save': 'Save {part}',
  'fieldreport.saving': 'Saving…',
  'fieldreport.unsettled.save': 'We do not know whether your last save of this part was recorded.',
  'fieldreport.unsettled.save_hint':
    'Send it again to be sure: it goes exactly as it was sent the first time, under the same key, so the same save is not recorded twice. The fields stay shut until it is settled.',
  'fieldreport.unsettled.save_retry': 'Send that save again',
  'fieldreport.unsettled.save_check': 'Read the report again',
  'fieldreport.kpi.hint':
    'Day number and days since the last incident are worked out from the project settings when left blank.',
  'fieldreport.exec.hint': 'Delays and recommendations print one to a line.',
  'fieldreport.status.on_schedule': 'On schedule',
  'fieldreport.status.behind': 'Behind',
  'fieldreport.status.ahead': 'Ahead',
  'fieldreport.field.cover.title': 'Title',
  'fieldreport.field.cover.report_number': 'Report number',
  'fieldreport.field.cover.prepared_by': 'Prepared by',
  'fieldreport.field.cover.present_at_site': 'Present at site',
  'fieldreport.field.cover.weather_conditions': 'Weather',
  'fieldreport.field.cover.weather_temp': 'Temperature',
  'fieldreport.field.cover.weather_wind': 'Wind',
  'fieldreport.field.cover.exec_summary': 'Summary',
  'fieldreport.field.kpi.day_no': 'Day #',
  'fieldreport.field.kpi.pct_overall': '% complete (overall)',
  'fieldreport.field.kpi.pct_critical': '% complete (critical path)',
  'fieldreport.field.kpi.variance': 'Variance vs baseline',
  'fieldreport.field.kpi.open_rfis': 'Open RFIs',
  'fieldreport.field.kpi.days_since_incident': 'Days since incident',
  'fieldreport.field.exec.status': 'Overall project status',
  'fieldreport.field.exec.accomplishments': 'Major accomplishment',
  'fieldreport.field.exec.delays': 'Delays',
  'fieldreport.field.exec.milestones': 'Milestone progress',
  'fieldreport.field.exec.recommendations': 'Recommendations',
  'fieldreport.field.safety.incidents': 'Incidents today',
  'fieldreport.field.safety.near_misses': 'Near misses',
  'fieldreport.field.safety.observations': 'Safety observations',
  'fieldreport.field.safety.corrective': 'Corrective actions taken',
  'fieldreport.field.safety.toolbox': 'Toolbox talks / training',
  'fieldreport.field.qaqc.inspections': 'Inspections performed',
  'fieldreport.field.qaqc.tests': 'Tests conducted and results',
  'fieldreport.field.qaqc.ncr': 'Non-conformance reports',
  'fieldreport.field.qaqc.punch': 'Punch list items closed',
  'fieldreport.field.signatures.reviewed_by': 'Reviewed by',
  'fieldreport.field.signatures.owner': 'Owner acknowledgment',
  'fieldreport.materials.hint': 'Issues print one to a line.',
  'fieldreport.lookahead.hint':
    'Status: I/P is in progress, COMP is complete, NS is not started, NP is no progress. Risks print one to a line.',
  'fieldreport.table.cell': '{column}, row {row}',
  'fieldreport.table.none': 'No rows.',
  'fieldreport.table.actions': 'Row actions',
  'fieldreport.table.remove': 'Remove row {row} of {table}',
  'fieldreport.table.add': 'Add a row to {table}',
  'fieldreport.field.manpower.shift': 'Shift',
  'fieldreport.field.manpower.rows': 'Workforce on site per trade',
  'fieldreport.field.manpower.rows.contractor': 'Contractor / trade',
  'fieldreport.field.manpower.rows.count': 'Count on site',
  'fieldreport.field.manpower.rows.hours': 'Hours worked',
  'fieldreport.field.manpower.rows.shift': 'Shift',
  'fieldreport.field.work.rows': 'Work completed today',
  'fieldreport.field.work.rows.contractor': 'Contractor / trade',
  'fieldreport.field.work.rows.floor': 'Floor',
  'fieldreport.field.work.rows.description': 'Description of work',
  'fieldreport.field.materials.equipment': 'Major equipment',
  'fieldreport.field.materials.equipment.name': 'Equipment name',
  'fieldreport.field.materials.equipment.planned': 'Planned delivery date',
  'fieldreport.field.materials.equipment.actual': 'Actual delivery date',
  'fieldreport.field.materials.other': 'Other materials',
  'fieldreport.field.materials.issues': 'Material issues / delays',
  'fieldreport.field.lookahead.rows': 'Activities',
  'fieldreport.field.lookahead.rows.activity': 'Activity',
  'fieldreport.field.lookahead.rows.target': 'Target date',
  'fieldreport.field.lookahead.rows.status': 'Status',
  'fieldreport.field.lookahead.rows.notes': 'Notes',
  'fieldreport.field.lookahead.risks': 'Potential risks',
  'dailylog.title': 'Daily log',
  'dailylog.open_today': 'Open today',
  'dailylog.count': '{count} days',
  'dailylog.count_one': '1 day',
  'dailylog.column.date': 'Date',
  'dailylog.column.state': 'State',
  'dailylog.read_only': 'You can read the daily log on this project but not write to it.',
  'dailylog.weather.title': 'Weather',
  'dailylog.weather.fetched': 'Weather source',
  'dailylog.weather.fetched_from': 'From {provider}, fetched {when}',
  'dailylog.weather.no_source': 'No weather source is set up for this site, so nothing is fetched.',
  'dailylog.weather.after_day_ends':
    "The day's weather is fetched once the day has ended at the site. It appears the next time the day is opened.",
  'dailylog.weather.not_available':
    'Not available. The weather source could not be reached; it is tried again when the day is next opened.',
  'dailylog.weather.unreadable': 'The weather for this day could not be read just now.',
  'dailylog.weather.source_incomplete':
    'A weather source is named for this site but is not fully set up, so nothing is fetched.',
  'dailylog.weather.observed': 'Seen on site',
  'dailylog.weather.observed_none': 'Nothing typed in for this day.',
  'dailylog.weather.observed_add': 'Add what you saw',
  'dailylog.weather.observed_edit': 'Change what you saw',
  'dailylog.weather.observed_save': 'Save',
  'dailylog.weather.observed_cancel': 'Cancel',
  'dailylog.weather.observed_apart':
    'What you type is kept as its own reading. It never changes what the weather source reported.',
  'dailylog.weather.field.conditions': 'Conditions',
  'dailylog.weather.field.high': 'High (°F)',
  'dailylog.weather.field.low': 'Low (°F)',
  'dailylog.weather.field.wind': 'Wind (mph)',
  'dailylog.weather.field.rain': 'Rain (inches)',
  'dailylog.weather.field.note': 'Note',
  'dailylog.weather.high_low': 'High {high}°F, low {low}°F',
  'dailylog.weather.wind': 'Wind {mph} mph',
  'dailylog.weather.rain': 'Rain {inches} in',
  'dailylog.weather.forecast': "Today's forecast",
  'dailylog.weather.forecast_note': 'A forecast, not a record. It is not saved to the day or its report.',
  'dailylog.forecast.label': 'A forecast for the site, not a record of the day.',
  'dailylog.forecast.high': 'High {temp}°F',
  'dailylog.forecast.low': 'Low {temp}°F',
  'dailylog.forecast.wind': 'Wind {wind}',
  'dailylog.forecast.from': 'From {provider}, fetched {when}',
  'dailylog.forecast.no_source': 'No weather source is set up for this site.',
  'dailylog.forecast.not_available': 'The forecast could not be reached just now.',
  'dailylog.forecast.source_incomplete': 'A weather source is named for this site but is not fully set up.',
  'dailylog.forecast.withheld': 'The forecast is not available to you on this job.',
  'dailylog.forecast.for_date': 'Issued for {date}',
  'dailylog.forecast.period': '{name}, {date}',
  'dailylog.back': 'All days',
  'dailylog.day.state.open': 'Open',
  'dailylog.day.state.signed': 'Signed',
  'dailylog.day.other_project': 'This day belongs to another project. Open that project to see it.',
  'dailylog.day.signed_note':
    'This day is signed. A correction is added as an amendment, or the day is reopened to correct it and sign it again.',
  'dailylog.day.revision': 'Revision {revision}',
  'dailylog.entries': 'Entries',
  'dailylog.entries.none': 'Nothing has been recorded for this day yet.',
  'dailylog.entry.column.time': 'Time',
  'dailylog.entry.column.kind': 'Section',
  'dailylog.entry.column.company': 'Company',
  'dailylog.entry.column.what': 'What was recorded',
  'dailylog.entry.column.state': 'State',
  'dailylog.entry.state.pending': 'Waiting for review',
  'dailylog.entry.state.accepted': 'Accepted',
  'dailylog.entry.state.rejected': 'Sent back',
  'dailylog.entry.amendment': 'Amendment',
  'dailylog.entry.crew': '{headcount} × {classification}, {hours} h',
  'dailylog.review.accept': 'Accept',
  'dailylog.review.return': 'Send back',
  'dailylog.review.return_form': 'Send this entry back',
  'dailylog.review.reason': 'Reason for sending it back',
  'dailylog.review.return_submit': 'Send back with this reason',
  'dailylog.review.reason_shown': 'Reason: {reason}',
  'dailylog.review.unsettled': 'We do not know whether your decision on this entry was recorded.',
  'dailylog.review.retry': 'Send that decision again',
  'dailylog.sign.title': 'Complete this day',
  'dailylog.sign.acknowledge': 'I have read the entries on this day and they are an accurate record of it.',
  'dailylog.sign.explain':
    'Signing freezes the day as issued. Anything added after it is an amendment. Sending the report is a separate step.',
  'dailylog.sign.submit': 'Sign the day',
  'dailylog.sign.waiting_one': 'One entry is still waiting for review. It will show as waiting on the signed day.',
  'dailylog.sign.waiting': '{count} entries are still waiting for review. They will show as waiting on the signed day.',
  'dailylog.sign.unsettled': 'We do not know whether your signature was recorded.',
  'dailylog.sign.retry': 'Send that signature again',
  'dailylog.day.signed_by': 'Signed by {person}, {when}',
  'dailylog.day.signed_at': 'Signed {when}',
  'dailylog.report.title': "The day's report",
  'dailylog.report.signed': 'This is the day as it was signed, for printing or the project file.',
  'dailylog.report.open': 'This day is not signed yet, so the report shows what is on it now and says so.',
  'dailylog.report.pdf': 'Download the report (PDF)',
  'dailylog.report.html': 'Download a printable page',
  'dailylog.amend.title': 'Correct the signed day',
  'dailylog.amend.explain':
    'The signed report stays as it was issued. A correction is added beside it as an amendment, with your name and the reason.',
  'dailylog.amend.reason': 'Why this correction is needed',
  'dailylog.amend.submit': 'Add the amendment',
  'dailylog.amend.reason_shown': 'Reason: {reason}',
  'dailylog.amend.unsettled': 'We do not know whether your amendment was recorded.',
  'dailylog.amend.retry': 'Send that amendment again',
  'dailylog.reopen.title': 'Reopen to correct',
  'dailylog.reopen.explain':
    'This day goes back to open so it can be corrected and signed again. What was signed stays on record as revision {revision}.',
  'dailylog.reopen.reason': 'Why the day is being reopened',
  'dailylog.reopen.submit': 'Reopen the day',
  'dailylog.reopen.unsettled': 'We do not know whether the day was reopened.',
  'dailylog.reopen.retry': 'Send that reopening again',
  'dailylog.revisions.title': 'Earlier revisions',
  'dailylog.revisions.title_signatures': 'Signatures of this revision',
  'dailylog.revisions.kept': "The signature the day kept. It is the day's record.",
  'dailylog.revisions.revision': 'Revision {revision}',
  'dailylog.revisions.signed': 'Signed by {person}, {when}',
  'dailylog.revisions.reopened': 'Reopened by {person}, {when}',
  'dailylog.revisions.pdf': 'Download revision {revision} (PDF)',
  'dailylog.revisions.regenerated':
    'Each report here is produced again from what was kept when it was signed. It is not the file sent at the time.',
  'dailylog.revisions.not_kept':
    'Signed on another box at the same time as the signature the day kept. It was issued, and stays on record.',
  'dailylog.send.title': "Send the day's report",
  'dailylog.send.explain':
    'A PDF of the report, produced again from what was kept at the signature, is attached to the email. The usual recipients are filled in; change them for this sending if you need to.',
  'dailylog.send.to': 'To (one address per line)',
  'dailylog.send.cc': 'CC (one address per line)',
  'dailylog.send.subject': 'Subject',
  'dailylog.send.message': 'Message',
  'dailylog.send.subject_default': 'Daily report, {date}',
  'dailylog.send.message_default': 'The daily report for {date} is attached.',
  'dailylog.send.preview': 'Check before sending',
  'dailylog.send.preview_title': 'What will be sent',
  'dailylog.send.attached': 'Attached',
  'dailylog.send.submit': 'Send',
  'dailylog.send.change': 'Change it',
  'dailylog.send.unsettled': 'We do not know whether your report was sent.',
  'dailylog.send.retry': 'Send that report again',
  'dailylog.usual.title': 'Usual recipients of the report',
  'dailylog.usual.explain':
    "Who the day's report is addressed to unless the sender changes it. Saving sends nothing. The \u201cday signed\u201d notice keeps its own lists, on the project's e-mail settings.",
  'dailylog.usual.save': 'Save usual recipients',
  'dailylog.usual.save_from_send': 'Save as usual recipients',
  'dailylog.usual.saved': 'Usual recipients saved. Nothing was sent.',
  'dailylog.usual.saved_other':
    'Saved as the usual recipients: To {to}; CC {cc}. What is typed now is different and is not saved. Nothing was sent.',
  'dailylog.lists.label': 'Use a distribution list',
  'dailylog.lists.choose': 'Choose a list',
  'dailylog.lists.to': 'Add to To',
  'dailylog.lists.cc': 'Add to CC',
  'dailylog.lists.explain':
    "Copies the list's addresses as they are now. Changing the list later does not change what is copied here.",
  'dailylog.sends.title': 'Sent',
  'dailylog.sends.none': 'This report has not been sent.',
  'dailylog.replies.title': 'Replies',
  'dailylog.replies.unconfirmed_title': 'Replies from senders we could not confirm',
  'dailylog.replies.from': '{from}, {when}',
  'dailylog.replies.check.not_a_recipient': 'This address was not one the report was sent to.',
  'dailylog.replies.check.not_authenticated':
    'This address was one the report was sent to, but its mail server did not confirm it sent this.',
  'dailylog.replies.files': 'Attached, not kept: {files}',
  'dailylog.sends.who': '{person}, {when}, to {to}',
  'dailylog.sends.state.queued': 'Waiting to go',
  'dailylog.sends.state.accepted': 'Accepted by the mail provider',
  'dailylog.sends.state.refused': 'Not sent',
  'dailylog.sends.state.unknown': 'Not known',
  'dailylog.sends.state.partial': 'Accepted for some recipients',
  'dailylog.sends.state.interrupted': 'Handed over, no answer',
  'dailylog.sends.copied': 'CC {cc}',
  'dailylog.sends.refused_some':
    'The mail relay did not take it for {refused}. It took it for the others; this site is not told whether it reached their inboxes.',
  'dailylog.sends.again_refused': 'Send again to them only',
  'dailylog.sends.accepted_note': 'The mail provider took it. This site is not told whether it reached each inbox.',
  'dailylog.sends.may_duplicate': 'It may already have arrived. A second copy is fine.',
  'dailylog.sends.again': 'Send again',
  'dailylog.sends.earlier_revision':
    'This mail carried revision {revision}. The day has been reopened since, so it is not the current report.',
  'dailylog.sends.other_signature':
    'This mail carried a signature of revision {revision} that the day did not keep, so it is not the current report.',
  'dailylog.add.title': 'Add to this day',
  'dailylog.add.kind': 'Section',
  'dailylog.add.company': 'Company',
  'dailylog.add.body': 'What happened',
  'dailylog.add.classification': 'Trade or classification',
  'dailylog.add.headcount': 'Headcount',
  'dailylog.add.hours': 'Hours each',
  'dailylog.add.submit': 'Add entry',
  'dailylog.unsettled.add': 'We do not know whether your last entry was recorded.',
  'dailylog.unsettled.add_hint':
    'Send it again to be sure: it goes exactly as it was sent the first time, and it cannot be recorded twice. Anything you have typed since stays in the form for the next entry.',
  'dailylog.unsettled.add_retry': 'Send that entry again',
  'dailylog.unsettled.add_check': 'Check the list of entries',
  'dailylog.kind.manpower': 'Manpower',
  'dailylog.kind.deliveries': 'Deliveries',
  'dailylog.kind.equipment': 'Equipment',
  'dailylog.kind.delays': 'Delays',
  'dailylog.kind.visitors': 'Visitors',
  'dailylog.kind.phone_calls': 'Phone calls',
  'dailylog.kind.safety': 'Safety',
  'dailylog.kind.quantities': 'Quantities',
  'dailylog.kind.notes': 'Notes',
  'dailylog.kind.plan_revisions': 'Plan revisions',
  'dailylog.kind.scheduled_work': 'Scheduled work',
  // Correspondence: the register, raising an item, and one item.
  'cor.title': 'Correspondence',
  'cor.explain': 'Letters, notices, transmittals, memos and instructions, sent and received, in one register. Each item is a matter with its replies and answers threaded under it.',
  'cor.count': '{count} items',
  'cor.count_one': '1 item',
  // A write on an item whose answer never arrived. One set per action, in the
  // action's own words; the same three facts as the submittal cards: sending
  // again is safe because the key goes with it, the boxes are shut because a
  // change would not reach what the instance may already hold, and a different
  // decision waits until this one is settled.
  'cor.unsettled.issue':
    'The answer to issuing this item did not arrive, so whether it was issued is not known here.',
  'cor.unsettled.issue_hint':
    'Issuing it again carries the same key, so an issue that did land is handed back rather than made twice.',
  'cor.unsettled.issue_retry': 'Issue it again',
  'cor.unsettled.response':
    'The answer to sending your response did not arrive, so whether it was recorded is not known here.',
  'cor.unsettled.response_hint':
    'Sending it again sends the same response under the same key, so one that did land is not recorded twice. The box is shut until that is settled, because a change typed now would not reach a response the instance may already hold.',
  'cor.unsettled.response_retry': 'Send the same response again',
  'cor.unsettled.close':
    'The answer to closing this item did not arrive, so whether it was closed is not known here.',
  'cor.unsettled.close_hint':
    'Closing it again carries the same key and the same note, so a close that did land is handed back rather than made twice.',
  'cor.unsettled.close_retry': 'Close it again',
  'cor.unsettled.attach':
    'The answer to attaching the file did not arrive, so whether it is on the item is not known here.',
  'cor.unsettled.attach_hint':
    'Sending it again sends the same file and caption under the same key, so an attachment that did land is not added twice. The boxes are shut until that is settled.',
  'cor.unsettled.attach_retry': 'Send the same file again',
  'cor.unsettled.reply':
    'The answer to creating the reply did not arrive, so whether the draft was made is not known here.',
  'cor.unsettled.reply_hint':
    'Creating it again sends the same reply under the same key, so a draft that was made is handed back rather than made twice. The boxes are shut until that is settled.',
  'cor.unsettled.reply_retry': 'Create the same reply again',
  'cor.unsettled.correction':
    'The answer to creating the correction did not arrive, so whether the draft was made is not known here.',
  'cor.unsettled.correction_hint':
    'Creating it again sends the same correction under the same key, so a draft that was made is handed back rather than made twice. The boxes are shut until that is settled.',
  'cor.unsettled.correction_retry': 'Create the same correction again',
  'cor.unsettled.edit':
    'The answer to saving the draft did not arrive, so whether your changes were saved is not known here.',
  'cor.unsettled.edit_hint':
    'Saving it again sends the same changes under the same key, so changes that did land are not made twice. You cannot change these until that is settled.',
  'cor.unsettled.edit_retry': 'Save the same changes again',
  'cor.unsettled.check': 'Check what the item says',
  'cor.error.session_ended':
    'This was sent in a session that has since ended, so it is not sent again from here.',

  // What the frame calls held work on this module: never the item's number or subject.
  'cor.held_label': 'a correspondence item',

  // Raising an item, when the save or the issue that follows it did not settle.
  'cor.create_unconfirmed':
    'The answer to saving this item did not arrive, so whether it was saved is not known here.',
  'cor.create_unconfirmed_hint':
    'Trying again sends exactly what was sent the first time, under the same key, so an item that did save is handed back rather than made twice. The boxes are locked until that is settled.',
  'cor.create_refused_hint': 'Nothing was saved. What you typed is still here; correct it and try again.',
  'cor.try_the_save_again': 'Try saving it again',
  'cor.open_the_register': 'Open the register',
  'cor.saved_not_issued':
    'The item was saved as a draft titled \u201c{subject}\u201d. The answer to issuing it did not arrive, so whether it was issued is not known here.',
  'cor.saved_not_issued_hint':
    'Issuing it again is safe: it carries the same key, so an issue that did land is handed back rather than made twice. Opening the draft shows where it actually stands. The boxes above are locked because that draft is already saved and editing them here would not change it.',
  'cor.saved_not_issued_refused':
    'The item was saved as a draft titled \u201c{subject}\u201d. Issuing it was declined, so it has not been issued.',
  'cor.saved_not_issued_refused_hint':
    'The draft is unchanged and you still meant to issue it. Put right what the message above asks for and issue it again, or open the draft to work on it. The boxes above are locked because that draft is already saved and editing them here would not change it.',
  'cor.saved_recovered':
    'The item was saved as a draft titled \u201c{subject}\u201d, when saving it was tried again. It has not been issued from here.',
  'cor.saved_recovered_issue_hint':
    'It was meant to be issued. Issuing it is one press; opening the draft shows where it stands. The boxes above are locked because that draft is already saved and editing them here would not change it.',
  'cor.saved_recovered_hint':
    'Opening the draft shows where it stands. The boxes above are locked because that draft is already saved and editing them here would not change it.',
  'cor.issue_the_saved_draft': 'Issue the saved draft',
  'cor.open_the_saved_draft': 'Open the saved draft',
  'cor.showing_so_far': 'Showing the first {count}. There are more.',
  'cor.showing_all': 'Showing all {count}.',
  'cor.show_more': 'Show more',
  'cor.new': 'Create an item',
  'cor.filter.type': 'Type',
  'cor.filter.status': 'Status',
  'cor.filter.direction': 'Direction',
  'cor.filter.mine': 'Mine only',
  'cor.column.number': 'Number',
  'cor.column.subject': 'Subject',
  'cor.column.type': 'Type',
  'cor.column.direction': 'Direction',
  'cor.column.status': 'Status',
  'cor.column.court': 'Ball in court',
  'cor.column.due': 'Response due',
  'cor.column.notice': 'Notice deadline',
  'cor.status.draft': 'Draft',
  'cor.status.sent': 'Sent',
  'cor.status.received': 'Received',
  'cor.status.response_due': 'Response due',
  'cor.status.responded': 'Responded',
  'cor.status.closed': 'Closed',
  'cor.direction.outbound': 'Outgoing',
  'cor.direction.inbound': 'Incoming',
  'cor.category.letter': 'Letter',
  'cor.category.notice': 'Notice',
  'cor.category.notice_to_proceed': 'Notice to proceed',
  'cor.category.transmittal': 'Transmittal',
  'cor.category.memo': 'Memo',
  'cor.category.request': 'Request',
  'cor.category.instruction': 'Instruction',
  'cor.types.none.title': 'No correspondence types are set up on this project yet.',
  'cor.types.none.admin': 'Every item is raised under a type, and the type decides whether it expects an answer and how long the answer has. Add the standard set to start: letter, notice, notice to proceed, transmittal, memo, request and instruction. Their response periods are marked as proposed until somebody accepts them.',
  'cor.types.none.other': 'Every item is raised under a type, and a project administrator sets the types up. Until then nothing can be raised here.',
  'cor.types.seed': 'Add the standard types',
  'cor.types.seeded': 'The standard types were added.',
  'cor.section.what': 'What it is',
  'cor.section.who': 'Who it is from and to',
  'cor.section.when': 'Timing',
  'cor.field.type': 'Type',
  'cor.field.direction': 'Direction',
  'cor.field.subject': 'Subject',
  'cor.hint.subject_required': 'Enter a subject.',
  'cor.field.body': 'Description',
  'cor.field.manager': 'Correspondence manager',
  'cor.field.from_person': 'From (person)',
  'cor.field.from_company': 'From (company)',
  'cor.field.to': 'To',
  'cor.field.to_company': 'To (company)',
  'cor.field.cc': 'Copied',
  'cor.field.required': 'The addressees must respond',
  'cor.field.private': 'Private: only the people on it can see it',
  'cor.field.notice_event': 'Date of the event the notice period runs from',
  'cor.hint.requires_response': 'This type expects a response within {days} days.',
  'cor.hint.requires_response_open': 'This type expects a response.',
  'cor.hint.no_response': 'This type does not expect a response.',
  'cor.hint.notice_period': 'Notice period: {days} days from the event.',
  'cor.hint.proposed': 'These periods are proposed and have not been accepted for this contract.',
  'cor.hint.issue_needs': 'To issue, name at least one addressee.',
  'cor.save_draft': 'Save as draft',
  'cor.issue': 'Issue',
  'cor.issued': '{number} was issued.',
  'cor.detail.body': 'Description',
  'cor.detail.body.empty': 'No description.',
  'cor.detail.issued_wording': 'As issued',
  'cor.detail.parties': 'Details',
  'cor.detail.role.from': 'From',
  'cor.detail.role.to': 'To',
  'cor.detail.role.cc': 'Copied',
  'cor.detail.must_respond': 'must respond',
  'cor.detail.answered': 'answered {when}',
  'cor.detail.manager': 'Manager',
  'cor.detail.type': 'Type',
  'cor.detail.direction': 'Direction',
  'cor.detail.issued_on': 'Issued',
  'cor.detail.response_due': 'Response due',
  'cor.detail.due_source.contract': 'from the contract',
  'cor.detail.due_source.type': 'from the type',
  'cor.detail.due_source.manual': 'set by hand',
  'cor.detail.due_source.default': 'package default, proposed',
  'cor.detail.notice_deadline': 'Notice deadline',
  'cor.detail.closed_at': 'Closed',
  'cor.detail.closed_by': 'Closed by',
  'cor.detail.close_note': 'Closing note',
  'cor.detail.private': 'Private',
  'cor.detail.court': 'Ball in court',
  'cor.detail.court.empty': 'Nobody holds it.',
  'cor.detail.responses': 'Responses',
  'cor.detail.responses.empty': 'No responses yet.',
  'cor.detail.respond.label': 'Your response',
  'cor.detail.respond': 'Respond',
  'cor.detail.responded': 'Your response was filed.',
  'cor.detail.sla.on_time': 'on time',
  'cor.detail.sla.late': 'late',
  'cor.detail.close.label': 'Closing note',
  'cor.detail.close': 'Close',
  'cor.detail.closed': 'The item was closed.',
  'cor.detail.actions': 'Next steps',
  'cor.detail.reply': 'Reply or forward',
  'cor.detail.reply.explain': 'A reply is a new item threaded under this one. Choosing a different type forwards it into that type, where this type allows it.',
  'cor.detail.reply.type': 'Type of the new item',
  'cor.detail.reply.same': 'Same type (reply)',
  'cor.detail.reply.body': 'What it says',
  'cor.detail.reply.carry': 'Carry the attachments and links onto the new item',
  'cor.detail.reply.create': 'Create the reply as a draft',
  'cor.detail.edit': 'Edit draft',
  'cor.detail.edit.explain': 'A draft can be changed until it is issued. After that it is corrected instead.',
  'cor.detail.edit.save': 'Save the draft',
  'cor.detail.edited': 'The draft was saved.',
  'cor.detail.last_edited': 'Last edited',
  'cor.detail.last_edited.by': '{person}, {at}',
  'cor.detail.correct': 'Correct this item',
  'cor.detail.correct.explain': 'An issued item is not edited. A correction is a new draft that supersedes this one once it is issued, and this one keeps what was sent.',
  'cor.detail.correct.reason': 'Why it is being corrected',
  'cor.detail.correct.body': 'Corrected description',
  'cor.detail.correct.create': 'Create the correction as a draft',
  'cor.detail.superseded_by': 'Superseded by a correction.',
  'cor.detail.supersedes': 'This corrects an earlier item.',
  'cor.detail.correction_reason': 'Reason: {reason}',
  'cor.detail.open_it': 'Open it',
  'cor.detail.parent': 'This is a reply to an earlier item.',
  'cor.detail.attachments': 'Attachments',
  'cor.detail.attachments.empty': 'No attachments.',
  'cor.detail.attachments.frozen': 'Attachments are part of what was issued. To add one now, correct the item.',
  'cor.detail.attach.file': 'File to attach',
  'cor.detail.attach.caption': 'Caption',
  'cor.detail.attach': 'Attach',
  'cor.detail.attached': 'The file was attached.',
  'cor.detail.download': 'Download',
  'cor.detail.not_on_this_node': 'Not on this computer yet',
  'cor.detail.links': 'Linked records',
  'cor.detail.links.empty': 'Not linked to anything.',
  'cor.detail.presence.absent': 'not on this computer',
  'cor.detail.presence.unknown': 'cannot tell if it is here',
  'cor.detail.thread': 'The whole matter',
  'cor.detail.thread.only': 'Nothing else is threaded with this item yet.',
  'cor.detail.this_item': 'this item',
  'cor.detail.disclaimer':
    'This record shows what was issued, by whom, to whom and when. It does not decide whether the item has contractual effect.',
  'cor.detail.workflow': 'A review workflow is running on this item.',
  // The markup module's own screen: the log across the job and the palette.
  'markuplog.title': 'Markup and review canvas',
  'markuplog.subtitle':
    'Every markup and comment on this job that you may see, and the review stamps reviewers pick from.',
  'markuplog.tab.markup': 'Markup',
  'markuplog.tab.comments': 'Comments',
  'markuplog.tab.stamps': 'Review stamps',
  'markuplog.show': 'Show',
  'markuplog.filter.all': 'All',
  'markuplog.filter.mine': 'Mine',
  'markuplog.filter.drafts': 'My drafts',
  'markuplog.filter.published': 'Published',
  'markuplog.threads.open': 'Open comments',
  'markuplog.threads.resolved': 'Resolved comments',
  'markuplog.threads.all': 'All comments',
  'markuplog.col.document': 'Document',
  'markuplog.col.layer': 'Layer',
  'markuplog.col.author': 'Drawn by',
  'markuplog.col.status': 'Status',
  'markuplog.col.marks': 'Marks',
  'markuplog.col.started': 'Started',
  'markuplog.col.comment': 'Comment',
  'markuplog.col.opened_by': 'Opened by',
  'markuplog.col.replies': 'Replies',
  'markuplog.kind.sheet': 'Drawing',
  'markuplog.kind.section': 'Specification section',
  'markuplog.kind.submittal': 'Submittal',
  'markuplog.page': 'page {page}',
  'markuplog.you': 'You',
  'markuplog.published': 'Published',
  'markuplog.draft': 'Draft, only you can see it',
  'markuplog.thread.open': 'Open',
  'markuplog.thread.resolved': 'Resolved',
  'markuplog.none': 'No markup is available for you to view on this project.',
  'markuplog.stamps.defaults':
    'This project uses the standard stamps. Nobody has changed them for this job.',
  'markuplog.stamps.retired': 'retired',
  'markuplog.stamps.change': 'Change the stamps',
  'markuplog.stamps.who_may_change':
    'A project administrator on this tool, or someone allowed to set the review stamp palette, can change these.',
  'markuplog.stamps.retire_explained':
    'A stamp taken off the palette is retired, not deleted: reviews that already carry it keep showing it.',
  'markuplog.stamps.col.order': 'Order',
  'markuplog.stamps.col.label': 'Wording',
  'markuplog.stamps.col.in_use': 'In use',
  'markuplog.stamps.on_palette': 'On the palette',
  'markuplog.stamps.up': 'Move {label} up',
  'markuplog.stamps.down': 'Move {label} down',
  'markuplog.stamps.label_of': 'Wording of {code}',
  'markuplog.stamps.new': 'New stamp',
  'markuplog.stamps.add': 'Add',
  'markuplog.stamps.blank': 'Every stamp needs wording before the palette can be saved.',
  'markuplog.stamps.save': 'Save the stamps',
  'markuplog.stamps.unsettled':
    'This change to the stamps was sent, but no answer came back, so it may or may not have been saved.',
  'markuplog.stamps.unsettled_note':
    'Sending the same change again cannot apply it twice or undo a change somebody else has made since. It is kept while you move around the app, and lost if the page is reloaded.',
  'markuplog.stamps.send_again': 'Send the same change again',
  'markuplog.stamps.start_over': 'Let it go and start again',
  'markuplog.stamps.saving': 'Saving\u2026',
  // Named sets, issued more than once, and each one read issuance by issuance.
  'drawings.series.title': 'Drawing sets',
  'drawings.series.subtitle':
    'Each upload to a set is a new issue, like Bid, Permit or IFC. The latest one is current.',
  'drawings.series.open': 'Sets & issues',
  'drawings.series.name': 'Set',
  'drawings.series.name_example': 'e.g. Construction Documents',
  'drawings.series.create': 'Create set',
  'drawings.series.none': 'No sets yet.',
  'drawings.series.issuances': 'Issues',
  'drawings.series.current': 'Current issue',
  'drawings.series.open_current': 'View current',
  'drawings.series.none_issued': 'None yet',
  'drawings.series.issuances_sub':
    'Newest first.',
  'drawings.series.file_label': 'Add an upload to this set',
  'drawings.series.file_pick': 'Select upload',
  'drawings.series.file': 'Add to set',
  'drawings.series.unfile': 'Remove from set',
  'drawings.series.no_issuances': 'Nothing added to this set yet.',
  'drawings.series.issuance': 'Issue',
  'drawings.series.sheets_issued': 'Sheets issued',
  'drawings.series.is_current': 'Current',
  'drawings.series.older': 'Previous',
  'drawings.series.in_review': 'In review',
  'drawings.series.issuance_of': 'Issue {sequence} of {count}',
  'drawings.series.steps': 'Other issues',
  'drawings.series.go_older': '← {name}',
  'drawings.series.go_newer': '{name} →',
  'drawings.series.older_notice':
    'You\'re viewing a previous issue. Sheets revised since then are marked.',
  'drawings.series.open_current_named': 'View current issue ({name})',
  'drawings.series.current_notice': 'Current issue.',
  'drawings.series.sheet': 'Sheet',
  'drawings.series.sheet_title': 'Title',
  'drawings.series.came_from': 'Issue',
  'drawings.series.issued_here': 'This issue',
  'drawings.series.carried_from': 'From {name}',
  'drawings.series.in_force': 'Current',
  'drawings.series.sheet_current': 'Current',
  'drawings.series.sheet_superseded': 'Superseded by Rev {revision}',
  'drawings.series.refused':
    'You don\'t have access to this.',
  'drawings.series.uploads_unread':
    'Couldn\'t load uploads.',
  'drawings.series.steps_unread':
    'Couldn\'t load other issues.',
  'drawings.series.name_unread': 'Couldn\'t load set name.',
  'drawings.series.create_unsettled':
    'Couldn\'t confirm the set was created.',
  'drawings.series.file_unsettled':
    'Couldn\'t confirm the upload was added.',
  'drawings.series.unsettled_hint':
    'Retrying is safe. It won\'t create a duplicate.',
  'drawings.series.send_again': 'Retry',
  'drawings.series.refused_hint': 'Nothing was changed. Try again.',
  // System status: the button on the bar, the drawing and the provisioning tab.
  'system.button': 'System status',
  'system.title': 'System status',
  'system.explain':
    'How this site\'s system is put together: the cloud VM, its database and the computers on site, with what the application can see of each right now.',
  'system.tab.status': 'Status',
  'system.tab.provisioning': 'Provisioning',
  'system.assumed':
    'Nobody has provisioned this system yet, so it is drawn from the machine that answered this page and the machines it is configured to know of.',
  'system.measured_at': 'Measured {at}.',
  'system.serving_you': 'Your sign-in is being served by {name}.',
  'system.serving_unknown': 'The machine serving your sign-in is not on the drawing.',
  'system.diagram_label': 'Diagram of the cloud VM, its database, the computers on site and this browser',
  'system.legend':
    'Green: heard from lately. Red and dashed: not heard from lately. Grey: nothing heard either way. Blue: the machine serving you.',
  'system.live_window': 'A computer on site counts as live if it has called in within the last {minutes} minutes.',
  'system.you_are_here': '(serving you)',
  'system.unset_vm': 'Cloud VM (not set up)',
  'system.unset_local': 'Local computer (not set up)',
  'system.kind.vm': 'Cloud VM',
  'system.kind.local': 'Local computer',
  'system.kind.unprovisioned': 'Server (not provisioned)',
  'system.app.title': 'Application',
  'system.basis.answering': 'Live: answered this page',
  'system.basis.replicating': 'Live: reached on the last sync',
  'system.basis.unreachable': 'Not reached on the last sync',
  'system.basis.checked_in': 'Live: called in lately',
  'system.basis.silent': 'Not heard from lately',
  'system.basis.unknown': 'Nothing heard either way',
  'system.node.title': 'This machine',
  'system.node.origin': 'Name on the network',
  'system.node.where': 'Where it is',
  'system.node.version': 'Version',
  'system.node.started': 'Started',
  'system.node.uptime': 'Up for',
  'system.node.sign_in': 'Sign-in method',
  'system.db.title': 'Database',
  'system.db.reachable': 'Reachable',
  'system.db.dialect': 'Engine',
  'system.db.round_trip': 'Round trip',
  'system.db.pool': 'Connection pool',
  'system.db.size': 'Pool size',
  'system.db.in_use': 'In use',
  'system.db.idle': 'Idle',
  'system.db.overflow': 'Overflow in use',
  'system.db.max_overflow': 'Overflow allowed',
  'system.db.timeout': 'Wait before giving up',
  'system.db.pool_line': '{used} of {size} connections in use',
  'system.db.measured_by': 'Database figures measured by {name}',
  'system.db.unattached': 'The database figures were measured by a machine that is not on the drawing.',
  'system.viewer.title': 'Your computer',
  'system.viewer.person': 'Signed in as',
  'system.viewer.address': 'Address the application saw',
  'system.viewer.round_trip': 'Round trip from this browser',
  'system.viewer.browser': 'Browser',
  'system.computer.name': 'Name',
  'system.computer.kind': 'Kind',
  'system.computer.origin': 'Network name',
  'system.computer.note': 'Where and what',
  'system.computer.position': 'Order',
  'system.computer.state': 'State',
  'system.computer.last_heard': 'Last heard from',
  'system.yes': 'Yes',
  'system.no': 'No',
  'system.ms': '{n} ms',
  'system.seconds': '{n} s',
  'system.duration': '{days} d {hours} h {minutes} min',
  'system.provision.explain':
    'Say which machines this system is made of. This changes the drawing only: linking a computer on site is done on the Site computers screen, and whether a machine is live is always read from what the application has heard.',
  'system.provision.empty': 'No machines provisioned yet. The status tab shows the assumed one VM and one local computer.',
  'system.provision.show_retired': 'Show retired machines',
  'system.provision.add_title': 'Add a machine',
  'system.provision.add': 'Add',
  'system.provision.edit': 'Edit',
  'system.provision.retire': 'Retire',
  'system.provision.added': '{name} was added to the drawing.',
  'system.provision.saved': '{name} was saved.',
  'system.provision.retired': '{name} was retired from the drawing.',
  'system.provision.retired_on': 'Retired {at}',
  'system.unsettled.create':
    'The answer to adding this machine did not arrive, so whether it was added is not known here.',
  'system.unsettled.create_hint':
    'Sending again carries the same key, so a machine that was added is handed back rather than added twice. The boxes are locked until that is settled.',
  'system.unsettled.create_retry': 'Add it again',
  'system.unsettled.edit':
    'The answer to saving this machine did not arrive, so whether it was saved is not known here.',
  'system.unsettled.edit_hint':
    'Sending again sends exactly what was sent before, with no key: the machine ends as you sent it, and a save that already arrived finds nothing left to change. The boxes are locked until that is settled.',
  'system.unsettled.edit_retry': 'Save it again',
  'system.unsettled.retire':
    'The answer to retiring this machine did not arrive, so whether it was retired is not known here.',
  'system.unsettled.retire_hint':
    'Sending again asks for the same retirement. The server keeps one retirement for each machine, so a machine that was retired stays retired once, from the time it was first retired.',
  'system.unsettled.retire_retry': 'Retire it again',
  'system.unsettled.check': 'Check the list',
  'drawings.open_reviews': 'Review sessions',
  'drawings.reviews.title': 'Review sessions',
  'drawings.reviews.subtitle':
    'A drawing set marked up together for a set number of days, with a record of everything done in it.',
  'drawings.reviews.open': 'Open a review session',
  'drawings.reviews.set': 'Set to review',
  'drawings.reviews.pick_set': 'Choose a set',
  'drawings.reviews.name': 'Session name',
  'drawings.reviews.days': 'Runs for (days)',
  'drawings.reviews.tab.running': 'Running',
  'drawings.reviews.tab.review': 'Review',
  'drawings.reviews.none.running': 'No review session is running on this project.',
  'drawings.reviews.none.review': 'No review session has been closed on this project yet.',
  'drawings.reviews.status': 'Status',
  'drawings.reviews.status.active': 'Running',
  'drawings.reviews.status.ended': 'Time ran out',
  'drawings.reviews.status.closed': 'Closed',
  'drawings.reviews.ends': 'Ends',
  'drawings.reviews.closed_on': 'Closed',
  'drawings.reviews.comments': 'Comments',
  'drawings.reviews.host': 'Host',
  'drawings.session.of_set': 'Reviewing {set}.',
  'drawings.session.ends_on': 'Ends {date}.',
  'drawings.session.closed_on': 'Closed {date}.',
  'drawings.session.hosted_by': 'Hosted by {name}.',
  'drawings.session.export': 'Export comments',
  'drawings.session.refused': 'You don\'t have access to this review session.',
  'drawings.session.not_here': 'This review session is not here.',
  'drawings.session.extend_by': 'Extend by (days)',
  'drawings.session.extend': 'Extend',
  'drawings.session.close': 'Close and archive',
  'drawings.session.ended':
    'This session’s time has run out, so it takes no more comments. The host can extend it or close it.',
  'drawings.session.archived':
    'This session is closed and archived. Its marks and its record stay exactly as they were.',
  'drawings.session.sheet': 'Sheet',
  'drawings.session.page_n': 'Page {page}',
  'drawings.session.tools': 'Review tools',
  'drawings.session.comment': 'Comment',
  'drawings.session.save_comment': 'Save',
  'drawings.session.discard': 'Discard',
  'drawings.session.no_comments': 'No comments on this sheet yet.',
  'drawings.session.on_sheet': 'Comments on this sheet',
  'drawings.session.no_words': '(no words)',
  'drawings.session.edited': 'edited',
  'drawings.session.edit': 'Edit',
  'drawings.session.remove': 'Remove',
  'drawings.session.record': 'Record',
  'drawings.session.show_mark': 'Show on {sheet}',
  'drawings.session.record.opened': '{name} opened the session.',
  'drawings.session.record.joined': '{name} joined.',
  'drawings.session.record.placed': '{name} placed a comment.',
  'drawings.session.record.edited': '{name} edited a comment.',
  'drawings.session.record.removed': '{name} removed a comment.',
  'drawings.session.record.extended': '{name} extended the session.',
  'drawings.session.record.closed': '{name} closed the session.',
  'drawings.session.record.checked': '{name} checked the comments against a newer set.',
  'drawings.session.check.title': 'Were the comments picked up?',
  'drawings.session.check.no_newer':
    'No newer set has been loaded on this project. Load the reissue, then check it here.',
  'drawings.session.check.against': 'Newer set',
  'drawings.session.check.run': 'Check',
  'drawings.session.check.running': 'Checking…',
  'drawings.session.check.draft':
    'Checked against {set} on {date}. These are AI drafts: open the sheet and look before you rely on one.',
  'drawings.session.check.verdict': 'Answer',
  'drawings.session.check.note': 'What it saw',
  'drawings.session.check.changed_since':
    'Changed since it was judged. It now reads: {text}',
  'drawings.session.check.removed_since': 'Removed since it was judged.',
  'drawings.session.verdict.picked_up': 'Picked up',
  'drawings.session.verdict.not_picked_up': 'Not picked up',
  'drawings.session.verdict.unclear': 'Unclear',
  'drawings.session.verdict.sheet_missing': 'Sheet not in the newer set',
  // The public welcome page, before anybody signs in.
  'welcome.title': 'SiteFocus: CM — construction management for the people who build',
  'welcome.nav.product': 'Product',
  'welcome.nav.teams': 'Teams',
  'welcome.nav.signup': 'Get started',
  'welcome.nav.signin': 'Sign in',
  'welcome.hero.eyebrow': 'Construction management, built from the field up',
  'welcome.hero.title': 'Built for the trailer, the office, and the floor.',
  'welcome.hero.lede':
    'Manage RFIs, submittals, drawings, schedules, daily logs, and punch lists in one platform. Keep project and field teams aligned on priorities, responsibilities, and the information needed to move work forward.',
  'welcome.hero.primary': 'Get started',
  'welcome.hero.secondary': 'Sign in',
  'welcome.strip.one.value': 'Centralized Project Information',
  'welcome.strip.one.label': 'Keep project documents, communications, and field records organized in one system.',
  'welcome.strip.two.value': 'Trailer and Cloud',
  'welcome.strip.two.label':
    'SiteFocus runs locally and mirrors to the cloud. Your productivity shouldn’t be limited by your internet connection.',
  'welcome.strip.three.value': 'Connected Project Teams',
  'welcome.strip.three.label':
    'Coordinate across roles and trades with clear responsibilities and visibility into outstanding work.',
  'welcome.screens.title': 'See it working',
  'welcome.screens.lede':
    'These are real screens from SiteFocus, filled with sample project data.',
  'welcome.screens.overview.tab': 'Project overview',
  'welcome.screens.overview.caption':
    'What is in your court today, and everything that moved on the job, as it happens.',
  'welcome.screens.overview.alt':
    'The SiteFocus project overview, listing items waiting on the viewer and a live feed of project activity.',
  'welcome.screens.rfi.tab': 'RFIs',
  'welcome.screens.rfi.caption':
    'Every question to the design team, who holds it and when the answer is due.',
  'welcome.screens.rfi.alt':
    'The SiteFocus RFI register, with numbered RFIs, their state, ball in court and due dates.',
  'welcome.screens.deficiency.tab': 'Punch list',
  'welcome.screens.deficiency.caption':
    'Deficiencies and punch items by severity, location and due date, from walk-down to close-out.',
  'welcome.screens.deficiency.alt':
    'The SiteFocus deficiency and punch list, with items marked critical, major and minor.',
  'welcome.features.eyebrow': 'Everything on one job',
  'welcome.features.title': 'The tools a project actually runs on.',
  'welcome.features.rfi.title': 'RFIs and submittals',
  'welcome.features.rfi.body':
    'Raise, route and answer with the ball in court always visible, and nothing lost in an inbox.',
  'welcome.features.field.title': 'Daily logs and field reports',
  'welcome.features.field.body':
    'Crews, weather, work done and observations, written on a tablet before the trucks leave.',
  'welcome.features.punch.title': 'Deficiencies and punch list',
  'welcome.features.punch.body':
    'Find it, pin it, assign it and close it, with the photos and the history kept beside it.',
  'welcome.features.drawings.title': 'Drawings and specifications',
  'welcome.features.drawings.body':
    'The current set, the sheet that changed, and the markup that goes with it.',
  'welcome.features.schedule.title': 'Schedule and look-ahead',
  'welcome.features.schedule.body':
    'Import the schedule, plan the next weeks with the trades, and see what is due.',
  'welcome.features.people.title': 'Directory and permissions',
  'welcome.features.people.body':
    'Every company and person on the job, each seeing what their role allows and no more.',
  'welcome.crew.eyebrow': 'Teams',
  'welcome.crew.title': 'Connect your team. Control your project.',
  'welcome.crew.lede':
    'Support the people coordinating the project, directing the work, and verifying its quality and safety.',
  'welcome.crew.super.title': 'Superintendents',
  'welcome.crew.super.body':
    'Coordinate field activities, manage daily priorities, and track issues affecting site progress.',
  'welcome.crew.engineer.title': 'Project Engineers',
  'welcome.crew.engineer.body':
    'Manage RFIs, submittals, and drawings. Track outstanding reviews and responses, and maintain organized project records.',
  'welcome.crew.foreman.title': 'Foremen',
  'welcome.crew.foreman.body':
    'Document daily progress, communicate field conditions, and keep crews aligned on the work ahead.',
  'welcome.crew.quality.title': 'Quality & Safety',
  'welcome.crew.quality.body':
    'Manage inspections, document findings, and track deficiencies and corrective actions through closeout.',
  'welcome.field.eyebrow': 'Where the work happens',
  'welcome.field.title': 'Made for the places a laptop does not go.',
  'welcome.field.lede':
    'Switchgear rooms, ceiling spaces and the deck over the excavation. Lockouts, tests and observations go into SiteFocus where they happen, not at the end of the week.',
  'welcome.photos.walkway.alt':
    'Two people in high-visibility vests on a raised walkway, looking over an excavation with a crawler crane beyond.',
  'welcome.photos.switchgear.alt':
    'A technician in a hard hat and high-visibility vest applying a lockout tag at an electrical switchgear lineup.',
  'welcome.photos.panel.alt':
    'A technician in a hard hat and gloves working on the wiring inside an open control panel.',
  'welcome.signup.eyebrow': 'Get started',
  'welcome.signup.title': 'Bring SiteFocus to your next job.',
  'welcome.signup.lede':
    'Tell us a little about yourself, or about a colleague who should see it, and we will be in touch.',
  'welcome.signup.kind.legend': 'This is for',
  'welcome.signup.kind.signup': 'Me',
  'welcome.signup.kind.referral': 'A colleague I am referring',
  'welcome.signup.name': 'Your name',
  'welcome.signup.email': 'Your email address',
  'welcome.signup.company': 'Company',
  'welcome.signup.role': 'Your role',
  'welcome.signup.phone': 'Phone (optional)',
  'welcome.signup.referral_name': 'Colleague’s name',
  'welcome.signup.referral_email': 'Colleague’s email address',
  'welcome.signup.referral_company': 'Colleague’s company',
  'welcome.signup.message': 'Anything we should know? (optional)',
  'welcome.signup.website': 'Leave this empty',
  'welcome.signup.submit': 'Send',
  'welcome.signup.sending': 'Sending…',
  'welcome.signup.sent.title': 'Thank you. Our mail service accepted your request.',
  'welcome.signup.sent.body': 'Someone from our team will reply to the address you gave.',
  'welcome.signup.unknown':
    'We handed this to our mail service and did not hear back, so we do not know whether it was accepted. Everything you typed is still here. Sending it again may send it twice.',
  'welcome.signup.off':
    'This form is not passing requests on at the moment, so nothing was sent. Everything you typed is still here.',
  'welcome.signup.referral_sent.title': 'Thank you for the referral.',
  'welcome.signup.not_passed_on':
    'We could not pass this on just now. Nothing you typed has been lost from this page; please try again later.',
  'welcome.signup.too_many': 'That is a lot of requests from here in a short time. Please try again in a few minutes.',
  'welcome.signup.invalid': 'Please check the name and email address fields.',
  'welcome.signup.unreachable':
    'We did not get an answer from the site, so we do not know whether your request went through. Everything you typed is still here. Sending it again may send it twice.',
  'welcome.proceed.title': 'Already on a SiteFocus job?',
  'welcome.proceed.body': 'Sign in with the email address your project administrator set up for you.',
  'welcome.proceed.email': 'Email address',
  'welcome.proceed.submit': 'Continue to sign in',
  'welcome.footer.line': 'SiteFocus: CM, a construction management platform from VADD.',
  'welcome.footer.copyright': '© 2026 VADD Construction Services. All rights reserved.',
  'welcome.footer.nav': 'Site links',
  'welcome.footer.contact': 'Contact us',

  // Notification e-mail: the project administrator's settings, and each person's own.
  'mail.area.label': 'Notification e-mail',
  'mail.refused':
    "You do not have access to this project's notification e-mail any more, so it is not shown. If that has changed, try again.",
  'mail.admin.title': 'Notification e-mail',
  'mail.admin.subtitle':
    "What this project mails, who receives its general notices, and how its mail reads. Only this project's administrator changes these.",
  'mail.admin.nothing_mailed': 'Nothing on this site mails a notice yet, so there is nothing to set.',
  'mail.tool.submittals': 'Submittals',
  'mail.tool.rfi': 'RFIs',
  'mail.tool.dailylog': 'Daily log',
  'mail.notice.hand_over': 'Work handed to someone',
  'mail.notice.day_signed': "A day's log signed",
  'mail.col.tool': 'Tool',
  'mail.col.notice': 'Notice',
  'mail.col.mailed': 'Mailed',
  'mail.col.locked': 'Locked',
  'mail.col.set_by': 'Set by',
  'mail.col.because': 'Why',
  'mail.col.actions': 'Change',
  'mail.defaults.title': 'Default settings',
  'mail.defaults.explain':
    'Which notices each tool mails on this project. Each person can change their own, unless the tool is locked here. A person who turned notification e-mail off is never mailed, locked or not.',
  'mail.defaults.mailed_for': 'Mailed: {what}',
  'mail.defaults.locked_for': 'Locked: {what}',
  'mail.defaults.by_project': 'This project',
  'mail.defaults.by_platform': 'Nobody yet; mailed',
  'mail.lists.title': 'Distribution lists',
  'mail.lists.explain':
    "Who receives each general notice: the people on the lists chosen here, once each, if they can open the tool on this project. With no list, nobody receives it. A hand-over always goes to whoever holds the work, so it has no list.",
  'mail.lists.none_general': 'No tool here sends a general notice.',
  'mail.lists.nobody': 'No list. Nobody receives this one.',
  'mail.lists.remove': 'Take {list} off',
  'mail.lists.add_label': 'Add a list',
  'mail.lists.choose': 'Choose a list',
  'mail.lists.add': 'Add',
  'mail.templates.title': 'E-mail templates',
  'mail.templates.explain':
    "How a notice's mail reads on this project. A template changes the words only, never who receives the mail. Leave a part empty to keep the usual wording.",
  'mail.templates.language': 'Language',
  'mail.templates.names': 'Names a template may use:',
  'mail.templates.platform': 'Back to the usual wording',
  'mail.templates.missing_title': 'Notices with no template',
  'mail.templates.missing_none': 'Every notice has a template in both languages.',
  'mail.templates.missing_line': '{notice} ({language})',
  'mail.templates.missing_usual': 'Uses the usual wording.',
  'mail.templates.assign_explain': 'Make a template, then choose which notices use it.',
  'mail.templates.missing_none_given': "Every notice uses one of this project's templates.",
  'mail.templates.given_title': 'Notices with a template',
  'mail.templates.give_label': 'Template for {notice}',
  'mail.templates.usual_option': 'Usual wording',
  'mail.templates.list_title': "This project's templates",
  'mail.templates.new': 'New template',
  'mail.templates.name': 'Template name',
  'mail.templates.used_by': 'Used by: {notices}',
  'mail.templates.remove': 'Remove template',
  'mail.templates.preview_as': 'Preview as',
  'mail.templates.preview_title': 'Preview',
  'mail.templates.preview_explain':
    'How this template reads, with sample values in place of the names. Nothing is sent.',
  'mail.draft.title': 'Draft from an example e-mail, with AI',
  'mail.draft.explain':
    "Upload a saved e-mail (.eml, .txt or .html) or paste one. Only its subject and text are sent to this server's AI service, with e-mail addresses and phone numbers masked first. The example is not saved. The draft fills the form in the language chosen above; nothing is saved until you press Save.",
  'mail.draft.file': 'Example e-mail file',
  'mail.draft.example': 'Example e-mail',
  'mail.draft.ask': 'Draft with AI',
  'mail.draft.drafting': 'Drafting…',
  'mail.draft.done':
    'The draft is in the form. {masked} e-mail addresses and phone numbers were masked before it was sent. Check it, then Save.',
  'mail.draft.held':
    'The draft came back after the form had changed, so it was not put in. It is for {template}, in {language}. Apply it to put it there in place of what is typed; nothing is saved until you press Save.',
  'mail.draft.apply': 'Apply the draft',
  'mail.draft.discard': 'Discard the draft',
  'mail.templates.kept':
    'Unsent changes from {template} are kept here, apart from the new template, because the new template has different ones typed in the same places. Nothing is saved until you press Save.',
  'mail.templates.kept_use': 'Put them in the new template',
  'mail.templates.kept_discard': 'Discard them',
  'mail.templates.preview_frame': 'The e-mail as it would read',
  'mail.templates.preview_text': 'Plain text',
  'mail.language.en': 'English',
  'mail.language.es': 'Spanish',
  'mail.part.subject': 'Subject',
  'mail.part.greeting': 'Greeting',
  'mail.part.intro': 'Opening line',
  'mail.part.footer': 'Footer',
  'mail.mine.open': 'My e-mail',
  'mail.mine.title': 'My notification e-mail on this project',
  'mail.mine.no_email': 'Send me no notification e-mail at all',
  'mail.mine.no_email_help':
    'This stops every notification e-mail to you, on every project, even where a tool is locked. Your notifications in SiteFocus stay as they are.',
  'mail.mine.nothing_mailed': 'Nothing on this site mails a notice yet.',
  'mail.mine.back_to_default': "Back to the project's default",
  'mail.because.opt_out': 'You turned notification e-mail off',
  'mail.because.locked': 'Locked by the project administrator',
  'mail.because.choice': 'Your own choice',
  'mail.because.default': "The project's default",
  'mail.because.platform': 'Nothing set; mailed',
} as const

export type StringKey = keyof typeof en
